import React, { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  BellRing,
  BookOpen,
  Bot,
  Calendar,
  Check,
  CheckCircle,
  CheckSquare,
  Clock,
  Cloud,
  Code,
  Database,
  DollarSign,
  Download,
  Edit,
  Eye,
  Factory,
  FileCheck,
  FileText,
  FolderOpen,
  Globe,
  Handshake,
  HardHat,
  Hash,
  Heart,
  Info,
  Leaf,
  LineChart,
  MapPin,
  MessageCircle,
  MessageSquare,
  Package,
  PieChart,
  Play,
  Plug,
  Plus,
  RefreshCw,
  Send,
  Server,
  Settings,
  Shield,
  Target,
  TestTube,
  Trash2,
  TrendingDown,
  TrendingUp,
  Truck,
  Upload,
  UserPlus,
  Users,
  Workflow,
  Zap
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Main Dashboard Component
const Dashboard = () => {
  const [metrics, setMetrics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [sapDashboard, setSapDashboard] = useState(null);
  const [esgMetrics, setEsgMetrics] = useState([]);
  const [emissions, setEmissions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [metricsResponse, analyticsResponse, sapResponse] = await Promise.all([
        axios.get(`${API}/dashboard/metrics`),
        axios.get(`${API}/dashboard/analytics`),
        axios.get(`${API}/sap/dashboard`)
      ]);
      setMetrics(metricsResponse.data);
      setAnalytics(analyticsResponse.data);
      setSapDashboard(sapResponse.data);
      fetchESGData();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchESGData = async () => {
    try {
      const [metricsRes, emissionsRes] = await Promise.all([
        axios.get(`${API}/esg/metrics`),
        axios.get(`${API}/esg/emissions`)
      ]);
      setEsgMetrics(metricsRes.data);
      setEmissions(emissionsRes.data);
    } catch (error) {
      console.error('Error fetching ESG data:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Executive Dashboard</h1>
        <p className="text-gray-600">Real-time operations overview and AI insights</p>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard">Executive Overview</TabsTrigger>
          <TabsTrigger value="sap-integration">SAP Integration Health</TabsTrigger>
          <TabsTrigger value="ai-insights">AI Strategic Insights</TabsTrigger>
          <TabsTrigger value="cross-module">Cross-Module Analytics</TabsTrigger>
          <TabsTrigger value="sap-sustainability">SAP Sustainability</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Assets</CardTitle>
            <Factory className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.total_assets || 0}</div>
            <p className="text-xs text-muted-foreground">Active monitoring</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Efficiency</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.operational_efficiency?.toFixed(1) || 0}%</div>
            <p className="text-xs text-muted-foreground">Operational efficiency</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Compliance</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.compliance_score?.toFixed(1) || 0}%</div>
            <p className="text-xs text-muted-foreground">Regulatory compliance</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uptime</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics?.uptime_percentage?.toFixed(1) || 0}%</div>
            <p className="text-xs text-muted-foreground">System uptime</p>
          </CardContent>
        </Card>
      </div>

      {/* Analytics Overview */}
      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Asset Health Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Excellent</span>
                  <Badge variant="outline" className="bg-green-50">{analytics.asset_health_distribution.excellent}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Good</span>
                  <Badge variant="outline" className="bg-blue-50">{analytics.asset_health_distribution.good}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Fair</span>
                  <Badge variant="outline" className="bg-yellow-50">{analytics.asset_health_distribution.fair}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Poor</span>
                  <Badge variant="outline" className="bg-red-50">{analytics.asset_health_distribution.poor}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Alert Severity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Critical</span>
                  <Badge variant="destructive">{analytics.alert_severity_breakdown.critical}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">High</span>
                  <Badge variant="outline" className="bg-orange-50">{analytics.alert_severity_breakdown.high}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Medium</span>
                  <Badge variant="outline" className="bg-yellow-50">{analytics.alert_severity_breakdown.medium}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Low</span>
                  <Badge variant="outline">{analytics.alert_severity_breakdown.low}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Compliance Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm">Compliant</span>
                  <Badge variant="outline" className="bg-green-50">{analytics.compliance_status_breakdown.compliant}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Warning</span>
                  <Badge variant="outline" className="bg-yellow-50">{analytics.compliance_status_breakdown.warning}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Violation</span>
                  <Badge variant="destructive">{analytics.compliance_status_breakdown.violation}</Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">Pending</span>
                  <Badge variant="outline">{analytics.compliance_status_breakdown.pending}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Key Insights */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Predicted Savings</CardTitle>
            <CardDescription>AI-driven cost optimization opportunities</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              ${metrics?.predicted_savings?.toLocaleString() || '0'}
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Potential annual savings through predictive maintenance and optimization
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Environmental Score</CardTitle>
            <CardDescription>ESG sustainability metrics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {metrics?.environmental_score?.toFixed(1) || '0'}/100
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Environmental sustainability and compliance rating
            </p>
          </CardContent>
        </Card>
      </div>
        </TabsContent>

        <TabsContent value="sap-integration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>SAP Integration Health Dashboard</CardTitle>
              <CardDescription>Real-time status of all SAP module integrations</CardDescription>
            </CardHeader>
            <CardContent>
              {sapDashboard && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-3xl font-bold text-green-600">{sapDashboard.summary?.active_modules || 0}</div>
                      <div className="text-sm text-gray-600">Active Modules</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-600">{sapDashboard.summary?.total_syncs_today || 0}</div>
                      <div className="text-sm text-gray-600">Syncs Today</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-3xl font-bold text-purple-600">{sapDashboard.summary?.success_rate?.toFixed(1) || 0}%</div>
                      <div className="text-sm text-gray-600">Success Rate</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-3xl font-bold text-yellow-600">{sapDashboard.summary?.avg_sync_time_ms?.toFixed(0) || 0}ms</div>
                      <div className="text-sm text-gray-600">Avg Sync Time</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(sapDashboard.modules_by_category || {}).map(([category, count]) => (
                      <div key={category} className="p-4 border rounded-lg">
                        <div className="text-lg font-bold text-gray-900">{count}</div>
                        <div className="text-sm text-gray-600 uppercase">{category}</div>
                        <Badge className="mt-2 bg-green-100 text-green-800">Active</Badge>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Module Integration Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { name: 'Predictive Maintenance', sap: 'SAP PM/EAM', status: 'active', syncs: 127 },
                  { name: 'Compliance Management', sap: 'SAP GRC', status: 'active', syncs: 89 },
                  { name: 'CTRM Trading', sap: 'SAP CTRM', status: 'active', syncs: 245 },
                  { name: 'Joint Ventures', sap: 'SAP JVA', status: 'active', syncs: 56 },
                  { name: 'ESG Intelligence', sap: 'SAP Sustainability', status: 'active', syncs: 134 }
                ].map((module) => (
                  <div key={module.name} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <div>
                        <div className="font-medium">{module.name}</div>
                        <div className="text-sm text-gray-600">{module.sap}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-600">{module.syncs}</div>
                      <div className="text-xs text-gray-600">Total Syncs</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-insights" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bot className="h-5 w-5 text-purple-600" />
                AI-Powered Strategic Insights
              </CardTitle>
              <CardDescription>Cross-module analytics and predictive recommendations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  {
                    title: 'Cost Optimization Opportunity',
                    insight: 'Analysis shows 15% cost reduction potential by consolidating maintenance schedules across Platform Alpha and Beta based on SAP PM data.',
                    impact: 'High',
                    savings: '$2.4M annually',
                    category: 'cost'
                  },
                  {
                    title: 'Compliance Risk Alert',
                    insight: 'SAP GRC data indicates 3 regulatory requirements approaching deadline. Automated remediation workflow recommended.',
                    impact: 'Critical',
                    savings: 'Risk mitigation',
                    category: 'risk'
                  },
                  {
                    title: 'Trading Position Optimization',
                    insight: 'CTRM analysis suggests repositioning 25% of crude oil futures based on market trends and inventory levels from SAP.',
                    impact: 'Medium',
                    savings: '$1.1M potential gain',
                    category: 'revenue'
                  },
                  {
                    title: 'ESG Performance Enhancement',
                    insight: 'Carbon accounting data shows opportunity to offset 12% emissions through process optimization in Refinery Unit 2.',
                    impact: 'Medium',
                    savings: '450 MT CO2 reduction',
                    category: 'sustainability'
                  }
                ].map((insight, idx) => (
                  <Alert key={idx} className="border-l-4 border-l-purple-500">
                    <Bot className="h-4 w-4 text-purple-600" />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-semibold">{insight.title}</div>
                        <Badge className={
                          insight.impact === 'Critical' ? 'bg-red-100 text-red-800' :
                          insight.impact === 'High' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }>
                          {insight.impact} Impact
                        </Badge>
                      </div>
                      <div className="text-sm text-gray-700 mb-2">{insight.insight}</div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-600 font-medium">{insight.savings}</span>
                        <Badge variant="outline">{insight.category}</Badge>
                      </div>
                    </div>
                  </Alert>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cross-module" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Cross-Module Analytics</CardTitle>
              <CardDescription>Unified insights across all integrated systems</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Maintenance → Compliance Correlation</h3>
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600 mb-2">87%</div>
                    <div className="text-sm text-gray-700">Critical maintenance issues resolved before compliance deadlines</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    SAP PM work orders automatically triggering compliance checks in SAP GRC
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Trading → ESG Impact</h3>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600 mb-2">12.5%</div>
                    <div className="text-sm text-gray-700">Carbon footprint reduction from optimized trading strategies</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    CTRM positions analysis integrated with sustainability metrics
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">JVA → Financial Performance</h3>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600 mb-2">$15.2M</div>
                    <div className="text-sm text-gray-700">Partner revenue distributed this quarter via SAP</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    Automated billing and distribution through SAP FI/CO integration
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Predictive Maintenance ROI</h3>
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600 mb-2">$8.4M</div>
                    <div className="text-sm text-gray-700">Cost savings from AI-predicted maintenance</div>
                  </div>
                  <div className="text-sm text-gray-600">
                    Work orders from PetraMind AI integrated with SAP PM reducing downtime
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sap-sustainability" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3"><Leaf className="h-6 w-6 text-green-600" /><span>SAP Sustainability Integration</span></div>
                <Badge className="bg-green-100 text-green-800">Active</Badge>
              </CardTitle>
              <CardDescription>Carbon accounting and ESG reporting with SAP</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-green-50 rounded-lg"><div className="text-2xl font-bold text-green-600">{esgMetrics.length}</div><div className="text-sm text-gray-600">ESG Metrics Synced</div></div>
                <div className="text-center p-4 bg-blue-50 rounded-lg"><div className="text-2xl font-bold text-blue-600">{emissions.length}</div><div className="text-sm text-gray-600">Emission Sources</div></div>
                <div className="text-center p-4 bg-purple-50 rounded-lg"><div className="text-2xl font-bold text-purple-600">2</div><div className="text-sm text-gray-600">Carbon Reports</div></div>
              </div>
              <Button className="w-full" onClick={async () => { try { await axios.post(`${API}/esg/sap/sync`); fetchESGData(); } catch(e) {} }}><RefreshCw className="h-4 w-4 mr-2" />Sync ESG Data with SAP</Button>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Carbon Accounting in SAP CO</CardTitle><CardDescription>Emissions data synchronized to SAP Cost Centers</CardDescription></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[1,2].map((i) => (
                  <div key={i} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between mb-2"><div className="font-medium">{i===1 ? 'Refinery Unit 1' : 'Offshore Platform Alpha'}</div><div className="font-bold text-green-600">{(Math.random()*5000).toFixed(0)} MT CO2</div></div>
                    <div className="text-sm text-gray-600">SAP Cost Center: CC-100{i} • Period: 2024-Q4</div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Enhanced AI Assistant Component (Combines Chat + Analytics)
const AIAssistant = () => {
  const [messages, setMessages] = useState([
    { 
      type: 'assistant', 
      content: 'Hello! I\'m PetraMind AI, your intelligent assistant for Oil & Gas operations. I can help with general questions, data analysis, cost optimization, and operational insights. How can I help you today?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState('');

  const sampleQuestions = [
    "What assets need immediate attention?",
    "Show me cost optimization opportunities",
    "Which assets have the highest risk scores?", 
    "Analyze our operational efficiency trends",
    "What are the top safety concerns?",
    "Find drilling operations with delays",
    "Show me environmental compliance status",
    "Predict equipment failures for next quarter"
  ];

  const sendMessage = async () => {
    if (!currentMessage.trim() || loading) return;

    const userMessage = currentMessage.trim();
    setCurrentMessage('');
    setMessages(prev => [...prev, { 
      type: 'user', 
      content: userMessage,
      timestamp: new Date().toISOString()
    }]);
    setLoading(true);

    try {
      // Use the enhanced natural language endpoint for comprehensive responses
      const response = await axios.post(`${API}/ai/natural-language/query`, {
        query: userMessage,
        context: context.trim() || "general_operations",
        include_charts: false,
        include_recommendations: true
      });

      setMessages(prev => [...prev, { 
        type: 'assistant', 
        content: response.data.answer,
        data_insights: response.data.data_insights || [],
        recommendations: response.data.recommendations || [],
        confidence: response.data.confidence || 0,
        timestamp: new Date().toISOString()
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      // Fallback to basic chat if natural language fails
      try {
        const fallbackResponse = await axios.post(`${API}/chat/query`, {
          question: userMessage
        });
        setMessages(prev => [...prev, { 
          type: 'assistant', 
          content: fallbackResponse.data.answer,
          insights: fallbackResponse.data.insights || [],
          recommendations: fallbackResponse.data.recommendations || [],
          timestamp: new Date().toISOString()
        }]);
      } catch (fallbackError) {
        setMessages(prev => [...prev, { 
          type: 'assistant', 
          content: 'I apologize, but I\'m having trouble processing your request right now. Please try again or rephrase your question.',
          timestamp: new Date().toISOString()
        }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  // Format AI response text to remove markdown and apply proper formatting
  const formatAIResponse = (text) => {
    if (!text) return '';
    
    // Convert the text to properly formatted JSX elements
    const parts = [];
    const lines = text.split('\n');
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i];
      
      // Skip empty lines (but add spacing)
      if (line.trim() === '') {
        parts.push(<br key={`br-${i}`} />);
        continue;
      }
      
      // Handle headers (### ## #)
      if (line.startsWith('### ')) {
        parts.push(
          <h4 key={i} className="font-semibold text-lg text-gray-800 mt-4 mb-2">
            {line.replace('### ', '')}
          </h4>
        );
        continue;
      }
      if (line.startsWith('## ')) {
        parts.push(
          <h3 key={i} className="font-bold text-xl text-gray-800 mt-4 mb-2">
            {line.replace('## ', '')}
          </h3>
        );
        continue;
      }
      if (line.startsWith('# ')) {
        parts.push(
          <h2 key={i} className="font-bold text-2xl text-gray-800 mt-4 mb-3">
            {line.replace('# ', '')}
          </h2>
        );
        continue;
      }
      
      // Handle bullet points
      if (line.match(/^[\s]*[-\*\+]\s/)) {
        const content = line.replace(/^[\s]*[-\*\+]\s/, '');
        parts.push(
          <div key={i} className="flex items-start gap-2 my-1">
            <span className="text-blue-500 font-bold">•</span>
            <span>{formatInlineMarkdown(content)}</span>
          </div>
        );
        continue;
      }
      
      // Handle numbered lists
      if (line.match(/^[\s]*\d+\.\s/)) {
        const content = line.replace(/^[\s]*\d+\.\s/, '');
        const number = line.match(/^[\s]*(\d+)\./)[1];
        parts.push(
          <div key={i} className="flex items-start gap-2 my-1">
            <span className="text-blue-500 font-semibold min-w-[20px]">{number}.</span>
            <span>{formatInlineMarkdown(content)}</span>
          </div>
        );
        continue;
      }
      
      // Regular paragraphs
      if (line.trim()) {
        parts.push(
          <p key={i} className="mb-2 leading-relaxed">
            {formatInlineMarkdown(line)}
          </p>
        );
      }
    }
    
    return parts;
  };

  // Format inline markdown (bold, italic, etc.)
  const formatInlineMarkdown = (text) => {
    if (!text) return '';
    
    // Split by markdown patterns and create JSX elements
    const parts = [];
    let remainingText = text;
    let key = 0;
    
    while (remainingText.length > 0) {
      // Find bold text **text**
      const boldMatch = remainingText.match(/\*\*(.*?)\*\*/);
      if (boldMatch) {
        const beforeBold = remainingText.substring(0, boldMatch.index);
        if (beforeBold) {
          parts.push(<span key={key++}>{beforeBold}</span>);
        }
        parts.push(
          <strong key={key++} className="font-semibold text-gray-900">
            {boldMatch[1]}
          </strong>
        );
        remainingText = remainingText.substring(boldMatch.index + boldMatch[0].length);
        continue;
      }
      
      // Find italic text *text*
      const italicMatch = remainingText.match(/\*(.*?)\*/);
      if (italicMatch && !remainingText.includes('**')) {
        const beforeItalic = remainingText.substring(0, italicMatch.index);
        if (beforeItalic) {
          parts.push(<span key={key++}>{beforeItalic}</span>);
        }
        parts.push(
          <em key={key++} className="italic text-gray-800">
            {italicMatch[1]}
          </em>
        );
        remainingText = remainingText.substring(italicMatch.index + italicMatch[0].length);
        continue;
      }
      
      // Find code text `text`
      const codeMatch = remainingText.match(/`(.*?)`/);
      if (codeMatch) {
        const beforeCode = remainingText.substring(0, codeMatch.index);
        if (beforeCode) {
          parts.push(<span key={key++}>{beforeCode}</span>);
        }
        parts.push(
          <code key={key++} className="bg-gray-100 px-2 py-1 rounded font-mono text-sm">
            {codeMatch[1]}
          </code>
        );
        remainingText = remainingText.substring(codeMatch.index + codeMatch[0].length);
        continue;
      }
      
      // No more markdown found, add remaining text
      parts.push(<span key={key++}>{remainingText}</span>);
      break;
    }
    
    return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Assistant</h1>
        <p className="text-gray-600">Your intelligent assistant for operations, analytics, and insights</p>
      </div>

      {/* Context Setting */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversation Context</CardTitle>
        </CardHeader>
        <CardContent>
          <Input
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder="Optional: Set context (e.g., 'maintenance planning', 'cost analysis', 'safety review')"
            className="mb-3"
          />
          <p className="text-sm text-gray-500">
            Setting context helps me provide more relevant and accurate responses.
          </p>
        </CardContent>
      </Card>

      {/* Sample Questions */}
      <Card>
        <CardHeader>
          <CardTitle>Try asking me...</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {sampleQuestions.map((question, index) => (
              <Button
                key={index}
                variant="outline"
                onClick={() => setCurrentMessage(question)}
                className="text-left justify-start h-auto py-3 whitespace-normal text-sm"
              >
                <MessageSquare className="w-4 h-4 mr-2 flex-shrink-0" />
                {question}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Enhanced Chat Messages */}
      <Card className="h-[500px] flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-600" />
            AI Conversation & Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex flex-col">
          <div className="flex-1 overflow-y-auto space-y-4 mb-4">
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-4 rounded-lg shadow-sm ${
                  message.type === 'user' 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-50 text-gray-900 border'
                }`}>
                  <div className="leading-relaxed">{formatAIResponse(message.content)}</div>
                  
                  {/* Data Insights Section */}
                  {message.data_insights && message.data_insights.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="font-semibold mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-blue-600" />
                        Key Metrics & Insights:
                      </h5>
                      <div className="grid grid-cols-2 gap-3">
                        {message.data_insights.map((insight, i) => (
                          <div key={i} className="bg-white p-3 rounded border text-center">
                            <div className="text-lg font-bold text-blue-600">{insight.value}</div>
                            <div className="text-sm text-gray-600">{insight.metric}</div>
                            {insight.unit && <div className="text-xs text-gray-500">{insight.unit}</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Legacy Insights Support */}
                  {message.insights && message.insights.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="font-semibold mb-2 flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        Key Insights:
                      </h5>
                      <ul className="text-sm space-y-2">
                        {message.insights.map((insight, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CheckCircle className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                            {insight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Recommendations Section */}
                  {message.recommendations && message.recommendations.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <h5 className="font-semibold mb-2 flex items-center gap-2">
                        <Target className="w-4 h-4 text-purple-600" />
                        Recommendations:
                      </h5>
                      <ul className="text-sm space-y-2">
                        {message.recommendations.map((rec, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Target className="w-3 h-3 text-purple-500 mt-0.5 flex-shrink-0" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Confidence & Timestamp */}
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                    <span>{formatTimestamp(message.timestamp)}</span>
                    {message.confidence && (
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3" />
                        {(message.confidence * 100).toFixed(1)}% confidence
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-50 border text-gray-900 p-4 rounded-lg shadow-sm">
                  <div className="flex items-center gap-2">
                    <RefreshCw className="w-4 h-4 animate-spin text-blue-600" />
                    <span>PetraMind AI is analyzing your request...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Enhanced Input Area */}
          <div className="space-y-3 border-t pt-3">
            <div className="flex gap-2">
              <Textarea
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything: general questions, data analysis, cost optimization, operational insights..."
                rows={2}
                className="flex-1 resize-none"
              />
              <Button 
                onClick={sendMessage}
                disabled={loading || !currentMessage.trim()}
                className="px-6 self-end"
              >
                {loading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Bot className="w-4 h-4" />
                )}
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              💡 I can handle both simple questions and complex data analysis. Try asking about cost optimization, asset health, compliance status, or operational efficiency!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Predictive Maintenance Component
const PredictiveMaintenance = () => {
  const [assets, setAssets] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prediction, setPrediction] = useState(null);
  const [predicting, setPredicting] = useState(false);

  useEffect(() => {
    fetchMaintenanceData();
  }, []);

  const fetchMaintenanceData = async () => {
    try {
      const [assetsResponse, alertsResponse] = await Promise.all([
        axios.get(`${API}/assets`),
        axios.get(`${API}/maintenance/alerts`)
      ]);
      setAssets(assetsResponse.data);
      setAlerts(alertsResponse.data);
    } catch (error) {
      console.error('Error fetching maintenance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const predictMaintenance = async (assetId) => {
    setPredicting(true);
    try {
      const response = await axios.post(`${API}/maintenance/predict/${assetId}`);
      setPrediction(response.data);
    } catch (error) {
      console.error('Error generating prediction:', error);
    } finally {
      setPredicting(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading maintenance data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Predictive Maintenance</h1>
        <p className="text-gray-600">AI-powered asset monitoring and maintenance predictions</p>
      </div>

      <Tabs defaultValue="assets" className="space-y-6">
        <TabsList>
          <TabsTrigger value="assets" data-testid="assets-tab">Assets ({assets.length})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="alerts-tab">Active Alerts ({alerts.filter(a => !a.resolved).length})</TabsTrigger>
          <TabsTrigger value="predictions" data-testid="predictions-tab">AI Predictions</TabsTrigger>
          <TabsTrigger value="sap-integration">SAP PM/EAM Integration</TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {assets.map((asset) => (
              <Card key={asset.id} data-testid={`asset-card-${asset.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{asset.name}</span>
                    <Badge variant={asset.status === 'operational' ? 'default' : 'secondary'}>
                      {asset.status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {asset.asset_type} • {asset.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Health Score</span>
                      <span className={`font-semibold ${getHealthColor(asset.health_score)}`}>
                        {asset.health_score}/100
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Criticality</span>
                      <Badge variant="outline">{asset.criticality}</Badge>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Last Inspection</span>
                      <span className="text-sm text-gray-600">
                        {new Date(asset.last_inspection).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <Button 
                      onClick={() => predictMaintenance(asset.id)} 
                      disabled={predicting}
                      className="w-full mt-4"
                      data-testid={`predict-maintenance-${asset.id}`}
                    >
                      {predicting ? 'Analyzing...' : 'Generate AI Prediction'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <div className="grid gap-4">
            {alerts.filter(alert => !alert.resolved).map((alert) => (
              <Alert key={alert.id} data-testid={`alert-${alert.id}`}>
                <AlertTriangle className="h-4 w-4" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{alert.title}</h4>
                    <div className="flex gap-2">
                      <Badge className={getSeverityColor(alert.severity)}>
                        {alert.severity}
                      </Badge>
                      <Badge variant="outline">{alert.alert_type}</Badge>
                    </div>
                  </div>
                  <AlertDescription className="mb-3">
                    {alert.description}
                  </AlertDescription>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h5 className="font-medium text-sm mb-1">AI Recommendation:</h5>
                    <p className="text-sm text-gray-700">{alert.recommendation}</p>
                  </div>
                </div>
              </Alert>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="predictions" className="space-y-4">
          {prediction ? (
            <Card data-testid="prediction-result">
              <CardHeader>
                <CardTitle>AI Maintenance Prediction</CardTitle>
                <CardDescription>
                  Generated prediction for asset analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {prediction.prediction_confidence?.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Confidence</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        ${prediction.estimated_cost_savings?.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Estimated Savings</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">
                        {prediction.failure_probability?.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Failure Risk</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold mb-2">Optimal Maintenance Window</h4>
                      <p className="text-gray-700">{prediction.optimal_maintenance_window}</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2">AI Recommendation</h4>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-gray-700">{prediction.recommended_action}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Predictions Generated</h3>
                <p className="text-gray-600 mb-4">
                  Select an asset and click "Generate AI Prediction" to get predictive maintenance insights
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sap-integration" className="space-y-6">
          <SAPMaintenanceIntegration assets={assets} alerts={alerts} onRefresh={fetchMaintenanceData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// SAP PM/EAM Integration Component for Predictive Maintenance
const SAPMaintenanceIntegration = ({ assets, alerts, onRefresh }) => {
  const [sapConfig, setSapConfig] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [workOrders, setWorkOrders] = useState([]);
  const [sapAssets, setSapAssets] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSAPIntegrationData();
  }, []);

  const fetchSAPIntegrationData = async () => {
    try {
      // Fetch SAP EAM module configuration
      const modulesRes = await axios.get(`${API}/sap/modules`);
      const eamModule = modulesRes.data.find(m => m.module_category === 'eam');
      setSapConfig(eamModule);

      if (eamModule) {
        // Fetch work orders and sync history
        const [workOrdersRes, historyRes] = await Promise.all([
          axios.get(`${API}/maintenance/sap/work-orders`),
          axios.get(`${API}/sap/modules/${eamModule.id}/sync-logs?limit=10`)
        ]);
        setWorkOrders(workOrdersRes.data);
        setSyncHistory(historyRes.data);
      }
    } catch (error) {
      console.error('Error fetching SAP integration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncAssetsWithSAP = async () => {
    if (!sapConfig) return;
    
    setSyncStatus('syncing');
    try {
      await axios.post(`${API}/maintenance/sap/sync-assets`);
      await fetchSAPIntegrationData();
      onRefresh();
      setSyncStatus('success');
    } catch (error) {
      console.error('Error syncing assets:', error);
      setSyncStatus('error');
    }
    
    setTimeout(() => setSyncStatus('idle'), 3000);
  };

  const createWorkOrderInSAP = async (alert) => {
    if (!sapConfig) return;
    
    try {
      await axios.post(`${API}/maintenance/sap/create-work-order`, {
        alert_id: alert.id,
        priority: alert.severity === 'critical' ? 'High' : 'Medium',
        description: alert.description
      });
      await fetchSAPIntegrationData();
    } catch (error) {
      console.error('Error creating work order:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading SAP integration...</div>
      </div>
    );
  }

  if (!sapConfig) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold mb-2">SAP EAM Not Configured</h3>
          <p className="text-gray-600 mb-4">
            Configure SAP Enterprise Asset Management module to enable integration
          </p>
          <Button onClick={() => window.location.href = '#sap-integration'}>
            Configure SAP EAM
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* SAP Integration Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="h-6 w-6 text-blue-600" />
              <span>SAP PM/EAM Integration Status</span>
            </div>
            <Badge className={sapConfig.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {sapConfig.is_enabled ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Real-time synchronization with SAP Plant Maintenance and Enterprise Asset Management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{assets.length}</div>
              <div className="text-sm text-gray-600">Assets Synced</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{workOrders.length}</div>
              <div className="text-sm text-gray-600">Work Orders</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {sapConfig.last_sync ? new Date(sapConfig.last_sync).toLocaleDateString() : 'Never'}
              </div>
              <div className="text-sm text-gray-600">Last Sync</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{sapConfig.sync_frequency}</div>
              <div className="text-sm text-gray-600">Sync Mode</div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={syncAssetsWithSAP}
              disabled={syncStatus === 'syncing'}
              className="flex-1"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
              {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Assets with SAP'}
            </Button>
            <Button variant="outline" className="flex-1">
              <Settings className="h-4 w-4 mr-2" />
              Configure Integration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Work Orders Section */}
      <Card>
        <CardHeader>
          <CardTitle>SAP Work Orders</CardTitle>
          <CardDescription>
            Automated work order creation and management in SAP PM
          </CardDescription>
        </CardHeader>
        <CardContent>
          {workOrders.length > 0 ? (
            <div className="space-y-3">
              {workOrders.map((wo) => (
                <div key={wo.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{wo.title}</div>
                    <div className="text-sm text-gray-600">
                      Order: {wo.sap_order_number} • {wo.asset_name}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      wo.status === 'completed' ? 'bg-green-100 text-green-800' :
                      wo.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>
                      {wo.status}
                    </Badge>
                    <Badge variant="outline">{wo.priority}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No work orders found. Work orders will be automatically created for critical alerts.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Auto Work Order Creation for Critical Alerts */}
      <Card>
        <CardHeader>
          <CardTitle>Automated Work Order Creation</CardTitle>
          <CardDescription>
            Critical alerts that can generate SAP work orders automatically
          </CardDescription>
        </CardHeader>
        <CardContent>
          {alerts.filter(a => !a.resolved && (a.severity === 'critical' || a.severity === 'high')).length > 0 ? (
            <div className="space-y-3">
              {alerts.filter(a => !a.resolved && (a.severity === 'critical' || a.severity === 'high')).map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="font-medium">{alert.title}</span>
                      <Badge className={alert.severity === 'critical' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}>
                        {alert.severity}
                      </Badge>
                    </div>
                    <div className="text-sm text-gray-600">{alert.description}</div>
                  </div>
                  <Button 
                    size="sm"
                    onClick={() => createWorkOrderInSAP(alert)}
                  >
                    Create Work Order in SAP
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No critical alerts requiring work orders at this time.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync History */}
      <Card>
        <CardHeader>
          <CardTitle>Synchronization History</CardTitle>
          <CardDescription>Recent sync operations with SAP PM/EAM</CardDescription>
        </CardHeader>
        <CardContent>
          {syncHistory.length > 0 ? (
            <div className="space-y-2">
              {syncHistory.map((log) => (
                <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="h-4 w-4 text-blue-600" />
                    <div>
                      <div className="text-sm font-medium">{log.records_synced} assets synced</div>
                      <div className="text-xs text-gray-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <Badge className={log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {log.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No sync history available.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field Mappings Reference */}
      <Card>
        <CardHeader>
          <CardTitle>SAP Field Mappings</CardTitle>
          <CardDescription>Asset data mapping between PetraMind and SAP PM/EAM</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold">PetraMind Field</th>
                  <th className="text-left p-3 font-semibold">SAP Field</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-medium">asset_id</td>
                  <td className="p-3 font-mono text-sm">EQUNR</td>
                  <td className="p-3 text-sm text-gray-600">Equipment Number</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">asset_name</td>
                  <td className="p-3 font-mono text-sm">EQKTX</td>
                  <td className="p-3 text-sm text-gray-600">Equipment Description</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">location</td>
                  <td className="p-3 font-mono text-sm">SWERK</td>
                  <td className="p-3 text-sm text-gray-600">Plant/Location</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">status</td>
                  <td className="p-3 font-mono text-sm">ISTAT</td>
                  <td className="p-3 text-sm text-gray-600">Equipment Status</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">maintenance_order</td>
                  <td className="p-3 font-mono text-sm">AUFNR</td>
                  <td className="p-3 text-sm text-gray-600">Maintenance Order Number</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Compliance Management Component
const ComplianceManagement = () => {
  const [compliance, setCompliance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    fetchComplianceData();
  }, []);

  const fetchComplianceData = async () => {
    try {
      const response = await axios.get(`${API}/compliance`);
      setCompliance(response.data);
    } catch (error) {
      console.error('Error fetching compliance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async (regulationId) => {
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/compliance/generate-report/${regulationId}`);
      setReport(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setGeneratingReport(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'compliant': return 'bg-green-100 text-green-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'violation': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading compliance data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Regulatory Compliance</h1>
        <p className="text-gray-600">AI-powered compliance tracking and audit management</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="compliance-overview-tab">Overview ({compliance.length})</TabsTrigger>
          <TabsTrigger value="reports" data-testid="compliance-reports-tab">AI Reports</TabsTrigger>
          <TabsTrigger value="sap-grc">SAP GRC Integration</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4">
            {compliance.map((item) => (
              <Card key={item.id} data-testid={`compliance-item-${item.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{item.regulation_name}</span>
                    <div className="flex gap-2">
                      <Badge className={getStatusColor(item.compliance_status)}>
                        {item.compliance_status}
                      </Badge>
                      <Badge variant="outline" className={getRiskColor(item.risk_level)}>
                        {item.risk_level} risk
                      </Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {item.description}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Last Audit:</span>
                        <div className="text-gray-600">
                          {new Date(item.last_audit_date).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Next Audit:</span>
                        <div className="text-gray-600">
                          {new Date(item.next_audit_date).toLocaleDateString()}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium">Department:</span>
                        <div className="text-gray-600">
                          {item.responsible_department}
                        </div>
                      </div>
                    </div>
                    
                    <Button 
                      onClick={() => generateReport(item.id)} 
                      disabled={generatingReport}
                      className="w-full"
                      data-testid={`generate-report-${item.id}`}
                    >
                      {generatingReport ? 'Generating AI Report...' : 'Generate AI Audit Report'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          {report ? (
            <Card data-testid="compliance-report">
              <CardHeader>
                <CardTitle>{report.regulation_name} - AI Audit Report</CardTitle>
                <CardDescription>
                  Generated on {new Date(report.generated_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {report.compliance_score?.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Compliance Score</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">
                        {report.risk_assessment}
                      </div>
                      <div className="text-sm text-gray-600">Risk Assessment</div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">AI Audit Summary</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{report.audit_summary}</p>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-3">Action Items</h4>
                    <div className="space-y-2">
                      {report.action_items?.map((item, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Reports Generated</h3>
                <p className="text-gray-600 mb-4">
                  Select a compliance item and click "Generate AI Audit Report" to create detailed analysis
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sap-grc" className="space-y-6">
          <SAPGRCIntegration compliance={compliance} onRefresh={fetchComplianceData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// SAP GRC Integration Component for Compliance Management
const SAPGRCIntegration = ({ compliance, onRefresh }) => {
  const [grcConfig, setGrcConfig] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [risks, setRisks] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [auditReports, setAuditReports] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGRCIntegrationData();
  }, []);

  const fetchGRCIntegrationData = async () => {
    try {
      // Fetch SAP GRC module - look for s4hana module with GRC capabilities
      const modulesRes = await axios.get(`${API}/sap/modules`);
      // For GRC, we can use the S4HANA FI or CO module as GRC is integrated
      const grcModule = modulesRes.data.find(m => m.module_name.includes('CO - Controlling'));
      setGrcConfig(grcModule);

      if (grcModule) {
        const [risksRes, policiesRes, reportsRes, historyRes] = await Promise.all([
          axios.get(`${API}/compliance/sap/risks`),
          axios.get(`${API}/compliance/sap/policies`),
          axios.get(`${API}/compliance/sap/audit-reports`),
          axios.get(`${API}/sap/modules/${grcModule.id}/sync-logs?limit=10`)
        ]);
        setRisks(risksRes.data);
        setPolicies(policiesRes.data);
        setAuditReports(reportsRes.data);
        setSyncHistory(historyRes.data);
      }
    } catch (error) {
      console.error('Error fetching GRC integration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncComplianceWithSAP = async () => {
    if (!grcConfig) return;
    
    setSyncStatus('syncing');
    try {
      await axios.post(`${API}/compliance/sap/sync`);
      await fetchGRCIntegrationData();
      onRefresh();
      setSyncStatus('success');
    } catch (error) {
      console.error('Error syncing compliance:', error);
      setSyncStatus('error');
    }
    
    setTimeout(() => setSyncStatus('idle'), 3000);
  };

  const submitReportToSAP = async (complianceId) => {
    if (!grcConfig) return;
    
    try {
      await axios.post(`${API}/compliance/sap/submit-report`, {
        compliance_id: complianceId
      });
      await fetchGRCIntegrationData();
    } catch (error) {
      console.error('Error submitting report:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading SAP GRC integration...</div>
      </div>
    );
  }

  if (!grcConfig) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold mb-2">SAP GRC Not Configured</h3>
          <p className="text-gray-600 mb-4">
            Configure SAP Governance, Risk & Compliance module to enable integration
          </p>
          <Button onClick={() => window.location.href = '#sap-integration'}>
            Configure SAP GRC
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* SAP GRC Integration Status Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-6 w-6 text-purple-600" />
              <span>SAP GRC Integration Status</span>
            </div>
            <Badge className={grcConfig.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {grcConfig.is_enabled ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Real-time synchronization with SAP Governance, Risk & Compliance
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{compliance.length}</div>
              <div className="text-sm text-gray-600">Regulations Synced</div>
            </div>
            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">{risks.length}</div>
              <div className="text-sm text-gray-600">Risk Items</div>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{policies.length}</div>
              <div className="text-sm text-gray-600">Active Policies</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{auditReports.length}</div>
              <div className="text-sm text-gray-600">Audit Reports</div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={syncComplianceWithSAP}
              disabled={syncStatus === 'syncing'}
              className="flex-1"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
              {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Compliance with SAP'}
            </Button>
            <Button variant="outline" className="flex-1">
              <Settings className="h-4 w-4 mr-2" />
              Configure Integration
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Risk Management Section */}
      <Card>
        <CardHeader>
          <CardTitle>Risk Management in SAP GRC</CardTitle>
          <CardDescription>
            Real-time risk assessment and monitoring synchronized with SAP
          </CardDescription>
        </CardHeader>
        <CardContent>
          {risks.length > 0 ? (
            <div className="space-y-3">
              {risks.map((risk) => (
                <div key={risk.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <span className="font-medium">{risk.risk_name}</span>
                    </div>
                    <div className="text-sm text-gray-600">{risk.description}</div>
                    <div className="text-xs text-gray-500 mt-1">SAP ID: {risk.sap_risk_id}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={
                      risk.severity === 'critical' ? 'bg-red-100 text-red-800' :
                      risk.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>
                      {risk.severity}
                    </Badge>
                    <Badge variant="outline">{risk.status}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No active risks found in SAP GRC.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Policy Management Section */}
      <Card>
        <CardHeader>
          <CardTitle>Policy Management</CardTitle>
          <CardDescription>
            Corporate policies synchronized with SAP GRC
          </CardDescription>
        </CardHeader>
        <CardContent>
          {policies.length > 0 ? (
            <div className="space-y-3">
              {policies.map((policy) => (
                <div key={policy.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">{policy.policy_name}</div>
                    <Badge className={policy.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                      {policy.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600 mb-2">{policy.description}</div>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>SAP Policy ID: {policy.sap_policy_id}</span>
                    <span>Version: {policy.version}</span>
                    <span>Effective: {new Date(policy.effective_date).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No policies configured in SAP GRC.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Automated Reporting Section */}
      <Card>
        <CardHeader>
          <CardTitle>Automated Compliance Reporting</CardTitle>
          <CardDescription>
            Submit compliance reports directly to SAP GRC
          </CardDescription>
        </CardHeader>
        <CardContent>
          {compliance.length > 0 ? (
            <div className="space-y-3">
              {compliance.map((item) => (
                <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium mb-1">{item.regulation_name}</div>
                    <div className="text-sm text-gray-600">{item.responsible_department}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={
                      item.compliance_status === 'compliant' ? 'bg-green-100 text-green-800' :
                      item.compliance_status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {item.compliance_status}
                    </Badge>
                    <Button 
                      size="sm"
                      onClick={() => submitReportToSAP(item.id)}
                    >
                      Submit to SAP
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No compliance items to report.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit Reports Section */}
      <Card>
        <CardHeader>
          <CardTitle>SAP GRC Audit Reports</CardTitle>
          <CardDescription>Reports synchronized from SAP GRC system</CardDescription>
        </CardHeader>
        <CardContent>
          {auditReports.length > 0 ? (
            <div className="space-y-3">
              {auditReports.map((report) => (
                <div key={report.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">{report.report_name}</div>
                    <Badge className={report.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {report.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">SAP Report ID:</span>
                      <div className="font-medium">{report.sap_report_id}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Audit Date:</span>
                      <div className="font-medium">{new Date(report.audit_date).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <span className="text-gray-600">Findings:</span>
                      <div className="font-medium">{report.findings_count} issues</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No audit reports available from SAP GRC.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sync History */}
      <Card>
        <CardHeader>
          <CardTitle>Synchronization History</CardTitle>
          <CardDescription>Recent sync operations with SAP GRC</CardDescription>
        </CardHeader>
        <CardContent>
          {syncHistory.length > 0 ? (
            <div className="space-y-2">
              {syncHistory.map((log) => (
                <div key={log.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <RefreshCw className="h-4 w-4 text-purple-600" />
                    <div>
                      <div className="text-sm font-medium">{log.records_synced} records synced</div>
                      <div className="text-xs text-gray-600">
                        {new Date(log.timestamp).toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <Badge className={log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                    {log.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No sync history available.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Field Mappings Reference */}
      <Card>
        <CardHeader>
          <CardTitle>SAP GRC Field Mappings</CardTitle>
          <CardDescription>Compliance data mapping between PetraMind and SAP GRC</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-semibold">PetraMind Field</th>
                  <th className="text-left p-3 font-semibold">SAP GRC Field</th>
                  <th className="text-left p-3 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-3 font-medium">regulation_id</td>
                  <td className="p-3 font-mono text-sm">RULE_ID</td>
                  <td className="p-3 text-sm text-gray-600">Regulation/Rule Identifier</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">compliance_status</td>
                  <td className="p-3 font-mono text-sm">STATUS</td>
                  <td className="p-3 text-sm text-gray-600">Compliance Status Code</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">risk_level</td>
                  <td className="p-3 font-mono text-sm">RISK_RATING</td>
                  <td className="p-3 text-sm text-gray-600">Risk Assessment Level</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">audit_date</td>
                  <td className="p-3 font-mono text-sm">AUDIT_DT</td>
                  <td className="p-3 text-sm text-gray-600">Audit Date</td>
                </tr>
                <tr className="border-b">
                  <td className="p-3 font-medium">responsible_dept</td>
                  <td className="p-3 font-mono text-sm">DEPT_CODE</td>
                  <td className="p-3 text-sm text-gray-600">Department Code</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== SCALE MODULES COMPONENTS ====================

// CTRM (Commodity Trading & Risk Management) Component
const CTRM = () => {
  const [trades, setTrades] = useState([]);
  const [riskDashboard, setRiskDashboard] = useState(null);
  const [marketAnalysis, setMarketAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchCTRMData();
  }, []);

  const fetchCTRMData = async () => {
    try {
      const [tradesResponse, riskResponse] = await Promise.all([
        axios.get(`${API}/ctrm/trades`),
        axios.get(`${API}/ctrm/risk-dashboard`)
      ]);
      setTrades(tradesResponse.data);
      setRiskDashboard(riskResponse.data);
    } catch (error) {
      console.error('Error fetching CTRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMarketAnalysis = async (commodity) => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/ctrm/market-analysis/${commodity}`);
      setMarketAnalysis(response.data);
    } catch (error) {
      console.error('Error generating market analysis:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'executed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskColor = (riskScore) => {
    if (riskScore < 25) return 'text-green-600';
    if (riskScore < 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading CTRM data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Commodity Trading & Risk Management</h1>
        <p className="text-gray-600">AI-powered trading analytics and risk optimization</p>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard" data-testid="ctrm-dashboard-tab">Risk Dashboard</TabsTrigger>
          <TabsTrigger value="trades" data-testid="ctrm-trades-tab">Active Trades ({trades.length})</TabsTrigger>
          <TabsTrigger value="analysis" data-testid="ctrm-analysis-tab">Market Analysis</TabsTrigger>
          <TabsTrigger value="sap-ctrm">SAP CTRM Integration</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-6">
          {riskDashboard && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Trades</CardTitle>
                    <LineChart className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{riskDashboard.total_active_trades}</div>
                    <p className="text-xs text-muted-foreground">Open positions</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Market Exposure</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">${(riskDashboard.total_market_exposure / 1000000).toFixed(1)}M</div>
                    <p className="text-xs text-muted-foreground">Total exposure</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Average Risk</CardTitle>
                    <Target className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${getRiskColor(riskDashboard.average_risk_score)}`}>
                      {riskDashboard.average_risk_score.toFixed(1)}/100
                    </div>
                    <p className="text-xs text-muted-foreground">Risk score</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total P&L</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className={`text-2xl font-bold ${riskDashboard.total_profit_loss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ${(riskDashboard.total_profit_loss / 1000).toFixed(0)}K
                    </div>
                    <p className="text-xs text-muted-foreground">Realized P&L</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Risk Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Low Risk (&lt;25)</span>
                        <Badge variant="outline" className="bg-green-50">{riskDashboard.risk_distribution.low_risk}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Medium Risk (25-50)</span>
                        <Badge variant="outline" className="bg-yellow-50">{riskDashboard.risk_distribution.medium_risk}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">High Risk (&gt;50)</span>
                        <Badge variant="outline" className="bg-red-50">{riskDashboard.risk_distribution.high_risk}</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Commodity Exposure</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(riskDashboard.commodity_breakdown).map(([commodity, data]) => (
                        <div key={commodity} className="flex justify-between items-center">
                          <span className="text-sm capitalize">{commodity.replace('_', ' ')}</span>
                          <div className="text-right">
                            <div className="text-sm font-semibold">{data.trades} trades</div>
                            <div className="text-xs text-gray-500">${(data.exposure / 1000000).toFixed(1)}M</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="trades" className="space-y-4">
          <div className="grid gap-4">
            {trades.map((trade) => (
              <Card key={trade.id} data-testid={`trade-${trade.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span className="capitalize">{trade.commodity.replace('_', ' ')} - {trade.trade_type.toUpperCase()}</span>
                    <div className="flex gap-2">
                      <Badge className={getStatusColor(trade.status)}>{trade.status}</Badge>
                      <Badge variant="outline" className={getRiskColor(trade.risk_score)}>
                        Risk: {trade.risk_score}/100
                      </Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {trade.counterparty} • {trade.volume.toLocaleString()} units @ ${trade.price_per_unit}/unit
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Trade Value:</span>
                      <div className="text-gray-600">${(trade.volume * trade.price_per_unit).toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="font-medium">Trade Date:</span>
                      <div className="text-gray-600">{new Date(trade.trade_date).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <span className="font-medium">Delivery:</span>
                      <div className="text-gray-600">{new Date(trade.delivery_date).toLocaleDateString()}</div>
                    </div>
                    <div>
                      <span className="font-medium">P&L:</span>
                      <div className={trade.profit_loss ? (trade.profit_loss >= 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-600'}>
                        {trade.profit_loss ? `$${trade.profit_loss.toLocaleString()}` : 'Pending'}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analysis" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Button 
              onClick={() => generateMarketAnalysis('crude_oil')} 
              disabled={analyzing}
              data-testid="analyze-crude-oil"
            >
              {analyzing ? 'Analyzing...' : 'Analyze Crude Oil'}
            </Button>
            <Button 
              onClick={() => generateMarketAnalysis('natural_gas')} 
              disabled={analyzing}
              data-testid="analyze-natural-gas"
            >
              {analyzing ? 'Analyzing...' : 'Analyze Natural Gas'}
            </Button>
            <Button 
              onClick={() => generateMarketAnalysis('refined_products')} 
              disabled={analyzing}
              data-testid="analyze-refined-products"
            >
              {analyzing ? 'Analyzing...' : 'Analyze Refined Products'}
            </Button>
          </div>

          {marketAnalysis ? (
            <Card data-testid="market-analysis-result">
              <CardHeader>
                <CardTitle className="capitalize">
                  {marketAnalysis.commodity.replace('_', ' ')} Market Analysis
                </CardTitle>
                <CardDescription>
                  AI-powered trading insights and recommendations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        ${marketAnalysis.current_price.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">Current Price</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        ${marketAnalysis.predicted_price.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">Predicted Price</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {marketAnalysis.confidence.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Confidence</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className={`text-2xl font-bold ${
                        marketAnalysis.trend === 'bullish' ? 'text-green-600' :
                        marketAnalysis.trend === 'bearish' ? 'text-red-600' : 'text-gray-600'
                      }`}>
                        {marketAnalysis.trend.toUpperCase()}
                      </div>
                      <div className="text-sm text-gray-600">Market Trend</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Key Market Factors</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {marketAnalysis.key_factors.map((factor, index) => (
                        <Badge key={index} variant="outline" className="justify-center p-2">
                          {factor}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">AI Trading Recommendation</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{marketAnalysis.recommendation}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <PieChart className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Analysis Generated</h3>
                <p className="text-gray-600 mb-4">
                  Select a commodity to generate AI-powered market analysis and trading recommendations
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sap-ctrm" className="space-y-6">
          <SAPCTRMIntegration trades={trades} riskDashboard={riskDashboard} onRefresh={fetchCTRMData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// SAP CTRM Integration Component
const SAPCTRMIntegration = ({ trades, riskDashboard, onRefresh }) => {
  const [sapConfig, setSapConfig] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [positions, setPositions] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [pnlReports, setPnlReports] = useState([]);
  const [syncHistory, setSyncHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSAPCTRMData();
  }, []);

  const fetchSAPCTRMData = async () => {
    try {
      const modulesRes = await axios.get(`${API}/sap/modules`);
      const ctrmModule = modulesRes.data.find(m => m.module_name.includes('SD - Sales'));
      setSapConfig(ctrmModule);

      if (ctrmModule) {
        const [positionsRes, settlementsRes, pnlRes, historyRes] = await Promise.all([
          axios.get(`${API}/ctrm/sap/positions`),
          axios.get(`${API}/ctrm/sap/settlements`),
          axios.get(`${API}/ctrm/sap/pnl-reports`),
          axios.get(`${API}/sap/modules/${ctrmModule.id}/sync-logs?limit=10`)
        ]);
        setPositions(positionsRes.data);
        setSettlements(settlementsRes.data);
        setPnlReports(pnlRes.data);
        setSyncHistory(historyRes.data);
      }
    } catch (error) {
      console.error('Error fetching SAP CTRM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncWithSAP = async () => {
    if (!sapConfig) return;
    setSyncStatus('syncing');
    try {
      await axios.post(`${API}/ctrm/sap/sync`);
      await fetchSAPCTRMData();
      onRefresh();
      setSyncStatus('success');
    } catch (error) {
      console.error('Error syncing:', error);
      setSyncStatus('error');
    }
    setTimeout(() => setSyncStatus('idle'), 3000);
  };

  const reconcilePosition = async (tradeId) => {
    try {
      await axios.post(`${API}/ctrm/sap/reconcile`, { trade_id: tradeId });
      await fetchSAPCTRMData();
    } catch (error) {
      console.error('Error reconciling:', error);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-lg">Loading SAP CTRM integration...</div></div>;

  if (!sapConfig) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold mb-2">SAP CTRM Not Configured</h3>
          <p className="text-gray-600 mb-4">Configure SAP CTRM module to enable trade integration</p>
          <Button onClick={() => window.location.href = '#sap-integration'}>Configure SAP CTRM</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DollarSign className="h-6 w-6 text-green-600" />
              <span>SAP CTRM Integration Status</span>
            </div>
            <Badge className={sapConfig.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {sapConfig.is_enabled ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
          <CardDescription>Real-time commodity trading synchronization with SAP</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{trades.length}</div>
              <div className="text-sm text-gray-600">Trades Synced</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{positions.length}</div>
              <div className="text-sm text-gray-600">Positions</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{settlements.length}</div>
              <div className="text-sm text-gray-600">Settlements</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">{pnlReports.length}</div>
              <div className="text-sm text-gray-600">P&L Reports</div>
            </div>
          </div>
          <div className="flex gap-3">
            <Button onClick={syncWithSAP} disabled={syncStatus === 'syncing'} className="flex-1">
              <RefreshCw className={`h-4 w-4 mr-2 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
              {syncStatus === 'syncing' ? 'Syncing...' : 'Sync Trades with SAP'}
            </Button>
            <Button variant="outline" className="flex-1">
              <Settings className="h-4 w-4 mr-2" />Configure Integration
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Position Reconciliation</CardTitle>
          <CardDescription>Reconcile trading positions with SAP CTRM</CardDescription>
        </CardHeader>
        <CardContent>
          {positions.length > 0 ? (
            <div className="space-y-3">
              {positions.map((pos) => (
                <div key={pos.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="font-medium">{pos.commodity} Position</div>
                    <div className="text-sm text-gray-600">SAP Position ID: {pos.sap_position_id} • Quantity: {pos.quantity} {pos.unit}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={pos.status === 'reconciled' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {pos.status}
                    </Badge>
                    <Button size="sm" onClick={() => reconcilePosition(pos.trade_id)}>Reconcile</Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No positions to reconcile</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Settlement Integration with SAP FI</CardTitle>
          <CardDescription>Trade settlements synchronized to SAP Financial Accounting</CardDescription>
        </CardHeader>
        <CardContent>
          {settlements.length > 0 ? (
            <div className="space-y-3">
              {settlements.map((settlement) => (
                <div key={settlement.id} className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">Settlement #{settlement.settlement_number}</div>
                    <Badge className={settlement.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}>
                      {settlement.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div><span className="text-gray-600">SAP Doc:</span> <span className="font-medium">{settlement.sap_document_number}</span></div>
                    <div><span className="text-gray-600">Amount:</span> <span className="font-medium">${settlement.amount.toLocaleString()}</span></div>
                    <div><span className="text-gray-600">Date:</span> <span className="font-medium">{new Date(settlement.settlement_date).toLocaleDateString()}</span></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No settlements available</div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>P&L Reporting to SAP</CardTitle>
          <CardDescription>Profit & Loss reports synchronized with SAP Controlling</CardDescription>
        </CardHeader>
        <CardContent>
          {pnlReports.length > 0 ? (
            <div className="space-y-3">
              {pnlReports.map((report) => (
                <div key={report.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium">{report.report_name}</div>
                    <Badge className={report.pnl >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      ${report.pnl.toLocaleString()}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600">SAP Report ID: {report.sap_report_id} • Period: {report.period}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No P&L reports available</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// JVA (Joint Venture Accounting) Component  
const JVA = () => {
  const [ventures, setVentures] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [selectedVenture, setSelectedVenture] = useState(null);
  const [reconciliationReport, setReconciliationReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);

  useEffect(() => {
    fetchJVAData();
  }, []);

  const fetchJVAData = async () => {
    try {
      const venturesResponse = await axios.get(`${API}/jva/ventures`);
      setVentures(venturesResponse.data);
      if (venturesResponse.data.length > 0) {
        setSelectedVenture(venturesResponse.data[0]);
        const txnResponse = await axios.get(`${API}/jva/transactions/${venturesResponse.data[0].id}`);
        setTransactions(txnResponse.data);
      }
    } catch (error) {
      console.error('Error fetching JVA data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectVenture = async (venture) => {
    setSelectedVenture(venture);
    try {
      const txnResponse = await axios.get(`${API}/jva/transactions/${venture.id}`);
      setTransactions(txnResponse.data);
    } catch (error) {
      console.error('Error fetching venture transactions:', error);
    }
  };

  const reconcileVenture = async (ventureId) => {
    setReconciling(true);
    try {
      const response = await axios.post(`${API}/jva/reconcile/${ventureId}`);
      setReconciliationReport(response.data);
    } catch (error) {
      console.error('Error reconciling venture:', error);
    } finally {
      setReconciling(false);
    }
  };

  const getReconciliationColor = (status) => {
    switch (status) {
      case 'reconciled': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'disputed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'revenue': return 'text-green-600';
      case 'expense': return 'text-red-600';  
      case 'capital': return 'text-blue-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading JVA data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Joint Venture Accounting</h1>
        <p className="text-gray-600">AI-powered partner billing and reconciliation management</p>
      </div>

      <Tabs defaultValue="ventures" className="space-y-6">
        <TabsList>
          <TabsTrigger value="ventures" data-testid="jva-ventures-tab">Ventures ({ventures.length})</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="jva-transactions-tab">Transactions</TabsTrigger>
          <TabsTrigger value="reconciliation" data-testid="jva-reconciliation-tab">AI Reconciliation</TabsTrigger>
          <TabsTrigger value="sap-jva">SAP JVA Integration</TabsTrigger>
        </TabsList>

        <TabsContent value="ventures" className="space-y-4">
          <div className="grid gap-4">
            {ventures.map((venture) => (
              <Card 
                key={venture.id} 
                className={`cursor-pointer transition-colors ${selectedVenture?.id === venture.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => selectVenture(venture)}
                data-testid={`venture-${venture.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{venture.venture_name}</span>
                    <Badge variant={venture.status === 'active' ? 'default' : 'secondary'}>
                      {venture.status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {venture.project_type} • {venture.partners.length} partners
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-medium text-sm mb-2">Partner Ownership:</h4>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {Object.entries(venture.ownership_percentages).map(([partner, percentage]) => (
                          <div key={partner} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                            <span className="text-sm">{partner}</span>
                            <span className="text-sm font-semibold">{percentage}%</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex justify-between items-center pt-3 border-t">
                      <span className="text-sm text-gray-600">
                        Created: {new Date(venture.created_at).toLocaleDateString()}
                      </span>
                      <Button 
                        onClick={(e) => {
                          e.stopPropagation();
                          reconcileVenture(venture.id);
                        }}
                        disabled={reconciling}
                        size="sm"
                        data-testid={`reconcile-venture-${venture.id}`}
                      >
                        {reconciling ? 'Reconciling...' : 'AI Reconcile'}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="transactions" className="space-y-4">
          {selectedVenture ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{selectedVenture.venture_name} - Transactions</CardTitle>
                  <CardDescription>
                    Partner allocations and reconciliation status
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <div className="grid gap-4">
                {transactions.map((txn) => (
                  <Card key={txn.id} data-testid={`transaction-${txn.id}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className={getTransactionTypeColor(txn.transaction_type)}>
                          {txn.transaction_type.toUpperCase()}: ${txn.amount.toLocaleString()}
                        </span>
                        <Badge className={getReconciliationColor(txn.reconciliation_status)}>
                          {txn.reconciliation_status}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        {txn.description} • {new Date(txn.date).toLocaleDateString()}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div>
                        <h4 className="font-medium text-sm mb-2">Partner Allocations:</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                          {Object.entries(txn.partner_allocations).map(([partner, amount]) => (
                            <div key={partner} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                              <span className="text-sm">{partner}</span>
                              <span className="text-sm font-semibold">${amount.toLocaleString()}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Handshake className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Venture Selected</h3>
                <p className="text-gray-600">Select a joint venture to view its transactions and allocations</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="reconciliation" className="space-y-4">
          {reconciliationReport ? (
            <Card data-testid="reconciliation-report">
              <CardHeader>
                <CardTitle>{reconciliationReport.venture_name} - AI Reconciliation Report</CardTitle>
                <CardDescription>
                  Generated on {new Date(reconciliationReport.generated_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        ${reconciliationReport.financial_summary.total_revenue.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Total Revenue</div>
                    </div>
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-600">
                        ${reconciliationReport.financial_summary.total_expenses.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Total Expenses</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className={`text-2xl font-bold ${
                        reconciliationReport.financial_summary.net_position >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        ${reconciliationReport.financial_summary.net_position.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Net Position</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Reconciliation Status</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Reconciled</span>
                            <Badge className="bg-green-100 text-green-800">
                              {reconciliationReport.reconciliation_status.reconciled}
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Pending</span>
                            <Badge className="bg-yellow-100 text-yellow-800">
                              {reconciliationReport.reconciliation_status.pending}
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm">Disputed</span>
                            <Badge className="bg-red-100 text-red-800">
                              {reconciliationReport.reconciliation_status.disputed}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle>AI Recommendations</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <p className="text-gray-700 whitespace-pre-wrap text-sm">
                            {reconciliationReport.reconciliation_summary}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Reconciliation Generated</h3>
                <p className="text-gray-600 mb-4">
                  Select a venture and click "AI Reconcile" to generate comprehensive reconciliation analysis
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="sap-jva" className="space-y-6">
          <SAPJVAIntegration ventures={ventures} transactions={transactions} onRefresh={fetchJVAData} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// SAP JVA Integration Component
const SAPJVAIntegration = ({ ventures, transactions, onRefresh }) => {
  const [sapConfig, setSapConfig] = useState(null);
  const [syncStatus, setSyncStatus] = useState('idle');
  const [billings, setBillings] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSAPJVAData();
  }, []);

  const fetchSAPJVAData = async () => {
    try {
      const modulesRes = await axios.get(`${API}/sap/modules`);
      const jvaModule = modulesRes.data.find(m => m.module_name.includes('FI - Financial'));
      setSapConfig(jvaModule);

      if (jvaModule) {
        const [billingsRes, distributionsRes] = await Promise.all([
          axios.get(`${API}/jva/sap/billings`),
          axios.get(`${API}/jva/sap/distributions`)
        ]);
        setBillings(billingsRes.data);
        setDistributions(distributionsRes.data);
      }
    } catch (error) {
      console.error('Error fetching SAP JVA data:', error);
    } finally {
      setLoading(false);
    }
  };

  const syncWithSAP = async () => {
    if (!sapConfig) return;
    setSyncStatus('syncing');
    try {
      await axios.post(`${API}/jva/sap/sync`);
      await fetchSAPJVAData();
      onRefresh();
      setSyncStatus('success');
    } catch (error) {
      setSyncStatus('error');
    }
    setTimeout(() => setSyncStatus('idle'), 3000);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-pulse text-lg">Loading SAP JVA integration...</div></div>;

  if (!sapConfig) {
    return (
      <Card>
        <CardContent className="text-center py-12">
          <Handshake className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold mb-2">SAP JVA Not Configured</h3>
          <p className="text-gray-600 mb-4">Configure SAP JVA module for partner billing</p>
          <Button onClick={() => window.location.href = '#sap-integration'}>Configure SAP JVA</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Handshake className="h-6 w-6 text-blue-600" />
              <span>SAP JVA Integration Status</span>
            </div>
            <Badge className={sapConfig.is_enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
              {sapConfig.is_enabled ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
          <CardDescription>Partner billing and revenue distribution with SAP</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{ventures.length}</div>
              <div className="text-sm text-gray-600">JV Partners Synced</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{billings.length}</div>
              <div className="text-sm text-gray-600">Partner Billings</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{distributions.length}</div>
              <div className="text-sm text-gray-600">Revenue Distributions</div>
            </div>
          </div>
          <Button onClick={syncWithSAP} disabled={syncStatus === 'syncing'} className="w-full">
            <RefreshCw className={`h-4 w-4 mr-2 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`} />
            {syncStatus === 'syncing' ? 'Syncing...' : 'Sync JVA Data with SAP'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Automated Partner Billing in SAP</CardTitle>
          <CardDescription>Partner invoices synchronized to SAP FI</CardDescription>
        </CardHeader>
        <CardContent>
          {billings.length > 0 ? (
            <div className="space-y-3">
              {billings.map((billing) => (
                <div key={billing.id} className="p-4 border rounded-lg">
                  <div className="flex justify-between mb-2">
                    <div className="font-medium">{billing.partner_name}</div>
                    <Badge className={billing.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                      {billing.status}
                    </Badge>
                  </div>
                  <div className="text-sm text-gray-600">SAP Invoice: {billing.sap_invoice_number} • Amount: ${billing.amount.toLocaleString()}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-center py-8 text-gray-500">No billings available</div>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Revenue Distribution to SAP CO</CardTitle>
          <CardDescription>Automated revenue allocation and cost tracking</CardDescription>
        </CardHeader>
        <CardContent>
          {distributions.length > 0 ? (
            <div className="space-y-3">
              {distributions.map((dist) => (
                <div key={dist.id} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <div className="font-medium">{dist.venture_name} - {dist.partner_name}</div>
                    <div className="font-bold text-green-600">${dist.distribution_amount.toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-gray-600">Share: {dist.ownership_percentage}% • Period: {dist.period}</div>
                </div>
              ))}
            </div>
          ) : <div className="text-center py-8 text-gray-500">No distributions available</div>}
        </CardContent>
      </Card>
    </div>
  );
};

// ESG Intelligence Component
const ESGIntelligence = () => {
  const [esgMetrics, setEsgMetrics] = useState([]);
  const [emissions, setEmissions] = useState([]);
  const [sustainabilityReport, setSustainabilityReport] = useState(null);
  const [carbonForecast, setCarbonForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [forecasting, setForecasting] = useState(false);

  useEffect(() => {
    fetchESGData();
  }, []);

  const fetchESGData = async () => {
    try {
      const [metricsResponse, emissionsResponse] = await Promise.all([
        axios.get(`${API}/esg/metrics`),
        axios.get(`${API}/esg/emissions`)
      ]);
      setEsgMetrics(metricsResponse.data);
      setEmissions(emissionsResponse.data);
    } catch (error) {
      console.error('Error fetching ESG data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSustainabilityReport = async () => {
    setGeneratingReport(true);
    try {
      const response = await axios.post(`${API}/esg/sustainability-report`);
      setSustainabilityReport(response.data);
    } catch (error) {
      console.error('Error generating sustainability report:', error);
    } finally {
      setGeneratingReport(false);
    }
  };

  const generateCarbonForecast = async () => {
    setForecasting(true);
    try {
      const response = await axios.post(`${API}/esg/carbon-forecast`);
      setCarbonForecast(response.data);
    } catch (error) {
      console.error('Error generating carbon forecast:', error);
    } finally {
      setForecasting(false);
    }
  };

  const getESGScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getEmissionSourceColor = (source) => {
    switch (source) {
      case 'flaring': return 'bg-red-100 text-red-800';
      case 'venting': return 'bg-orange-100 text-orange-800';
      case 'combustion': return 'bg-yellow-100 text-yellow-800';
      case 'fugitive': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading ESG data...</div>
      </div>
    );
  }

  const latestMetrics = esgMetrics[0] || {};

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">ESG Intelligence</h1>
        <p className="text-gray-600">Environmental, Social & Governance sustainability tracking</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="esg-overview-tab">ESG Overview</TabsTrigger>
          <TabsTrigger value="emissions" data-testid="esg-emissions-tab">Emissions Tracking</TabsTrigger>
          <TabsTrigger value="reports" data-testid="esg-reports-tab">AI Reports</TabsTrigger>
          <TabsTrigger value="forecast" data-testid="esg-forecast-tab">Carbon Forecast</TabsTrigger>
          <TabsTrigger value="sap-sustainability">SAP Sustainability Integration</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Overall ESG</CardTitle>
                <Leaf className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getESGScoreColor(latestMetrics.overall_esg_score || 0)}`}>
                  {latestMetrics.overall_esg_score?.toFixed(1) || '0'}/100
                </div>
                <p className="text-xs text-muted-foreground">ESG Score</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Environmental</CardTitle>
                <Leaf className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getESGScoreColor(latestMetrics.environmental_score || 0)}`}>
                  {latestMetrics.environmental_score?.toFixed(1) || '0'}/100
                </div>
                <p className="text-xs text-muted-foreground">Environmental</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Social</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getESGScoreColor(latestMetrics.social_score || 0)}`}>
                  {latestMetrics.social_score?.toFixed(1) || '0'}/100
                </div>
                <p className="text-xs text-muted-foreground">Social</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Governance</CardTitle>
                <Shield className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${getESGScoreColor(latestMetrics.governance_score || 0)}`}>
                  {latestMetrics.governance_score?.toFixed(1) || '0'}/100
                </div>
                <p className="text-xs text-muted-foreground">Governance</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Carbon Footprint</CardTitle>
                <CardDescription>Emissions and environmental impact</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-3xl font-bold text-gray-700">
                      {latestMetrics.carbon_emissions?.toLocaleString() || '0'}
                    </div>
                    <div className="text-sm text-gray-600">Tons CO2 Equivalent</div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Energy Consumption:</span>
                      <div className="text-gray-600">{latestMetrics.energy_consumption?.toLocaleString() || '0'} MWh</div>
                    </div>
                    <div>
                      <span className="font-medium">Safety Incidents:</span>
                      <div className="text-gray-600">{latestMetrics.safety_incidents || '0'} incidents</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Social Impact</CardTitle>
                <CardDescription>Community and workforce initiatives</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600">
                      ${latestMetrics.community_investment?.toLocaleString() || '0'}
                    </div>
                    <div className="text-sm text-gray-600">Community Investment</div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm">Workforce Safety Programs</span>
                      <Badge variant="outline">Active</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Local Community Projects</span>
                      <Badge variant="outline">12 Projects</Badge>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <Button 
              onClick={generateSustainabilityReport} 
              disabled={generatingReport}
              className="w-full"
              data-testid="generate-sustainability-report"
            >
              {generatingReport ? 'Generating...' : 'Generate AI Sustainability Report'}
            </Button>
            <Button 
              onClick={generateCarbonForecast} 
              disabled={forecasting}
              className="w-full"
              data-testid="generate-carbon-forecast"
            >
              {forecasting ? 'Forecasting...' : 'Generate Carbon Forecast'}
            </Button>
            <Button variant="outline" className="w-full">
              Export ESG Data
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="emissions" className="space-y-4">
          <div className="grid gap-4">
            {emissions.map((emission) => (
              <Card key={emission.id} data-testid={`emission-${emission.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{emission.facility}</span>
                    <div className="flex gap-2">
                      <Badge className={getEmissionSourceColor(emission.source)}>
                        {emission.source}
                      </Badge>
                      <Badge variant="outline">{emission.emission_type}</Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    Emission tracking and reduction targets
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <div className="text-xl font-bold text-red-600">
                        {emission.amount.toLocaleString()}
                      </div>
                      <div className="text-xs text-gray-600">Current Emissions (tons)</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-xl font-bold text-green-600">
                        {emission.reduction_target?.toLocaleString() || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-600">Reduction Target (tons)</div>
                    </div>
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className="text-xl font-bold text-blue-600">
                        {emission.reduction_target 
                          ? ((emission.amount - emission.reduction_target) / emission.amount * 100).toFixed(1)
                          : '0'}%
                      </div>
                      <div className="text-xs text-gray-600">Reduction Potential</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold text-gray-600">
                        {new Date(emission.date).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-600">Last Updated</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reports" className="space-y-4">
          {sustainabilityReport ? (
            <Card data-testid="sustainability-report">
              <CardHeader>
                <CardTitle>AI Sustainability Report - {sustainabilityReport.report_period}</CardTitle>
                <CardDescription>
                  Generated on {new Date(sustainabilityReport.generated_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {sustainabilityReport.performance_metrics.current_esg_score.toFixed(1)}
                      </div>
                      <div className="text-sm text-gray-600">Current ESG Score</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className={`text-2xl font-bold ${sustainabilityReport.performance_metrics.esg_score_change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {sustainabilityReport.performance_metrics.esg_score_change >= 0 ? '+' : ''}{sustainabilityReport.performance_metrics.esg_score_change.toFixed(1)}
                      </div>
                      <div className="text-sm text-gray-600">Score Change</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {sustainabilityReport.emissions_breakdown.reduction_potential_percent.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Reduction Potential</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Executive Summary & Analysis</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{sustainabilityReport.executive_summary}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Key Recommendations</h4>
                    <div className="space-y-2">
                      {sustainabilityReport.recommendations.map((rec, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 bg-white border rounded-lg">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Sustainability Report Generated</h3>
                <p className="text-gray-600 mb-4">
                  Click "Generate AI Sustainability Report" to create comprehensive ESG analysis
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="forecast" className="space-y-4">
          {carbonForecast ? (
            <Card data-testid="carbon-forecast">
              <CardHeader>
                <CardTitle>AI Carbon Emissions Forecast</CardTitle>
                <CardDescription>
                  {carbonForecast.forecast_period} • {carbonForecast.methodology}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-700">
                        {(carbonForecast.annual_summary.total_predicted_emissions / 1000).toFixed(1)}K
                      </div>
                      <div className="text-sm text-gray-600">Total Predicted (tons)</div>
                    </div>
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {carbonForecast.annual_summary.average_monthly_emissions.toFixed(0)}
                      </div>
                      <div className="text-sm text-gray-600">Monthly Average (tons)</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {carbonForecast.annual_summary.projected_reduction_percent.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Reduction vs Baseline</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {carbonForecast.annual_summary.confidence_range}
                      </div>
                      <div className="text-sm text-gray-600">Confidence Range</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">AI Analysis & Methodology</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{carbonForecast.executive_summary}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Monthly Predictions</h4>
                    <div className="grid gap-2">
                      {carbonForecast.monthly_predictions.slice(0, 6).map((prediction, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                          <span className="text-sm font-medium">{prediction.month}</span>
                          <div className="flex items-center gap-4">
                            <span className="text-sm">{prediction.predicted_emissions.toFixed(1)} tons</span>
                            <Badge variant="outline">{prediction.confidence_level.toFixed(0)}% confidence</Badge>
                            <span className={`text-xs ${prediction.reduction_vs_baseline >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {prediction.reduction_vs_baseline >= 0 ? '+' : ''}{prediction.reduction_vs_baseline.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <TrendingDown className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Carbon Forecast Generated</h3>
                <p className="text-gray-600 mb-4">
                  Click "Generate Carbon Forecast" to create AI-powered emissions predictions
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== INTEGRATIONS LAYER COMPONENT ====================

const IntegrationsLayer = () => {
  const [integrations, setIntegrations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [dataFlows, setDataFlows] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchIntegrationsData();
  }, []);

  const fetchIntegrationsData = async () => {
    try {
      const [integrationsResponse, analyticsResponse] = await Promise.all([
        axios.get(`${API}/integrations`),
        axios.get(`${API}/integrations/analytics`)
      ]);
      setIntegrations(integrationsResponse.data);
      setAnalytics(analyticsResponse.data);
    } catch (error) {
      console.error('Error fetching integrations data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectIntegration = async (integration) => {
    setSelectedIntegration(integration);
    try {
      const flowsResponse = await axios.get(`${API}/integrations/${integration.id}/data-flows`);
      setDataFlows(flowsResponse.data);
    } catch (error) {
      console.error('Error fetching data flows:', error);
    }
  };

  const testIntegration = async (integrationId, testType = 'connectivity') => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/integrations/test`, {
        integration_id: integrationId,
        test_type: testType,
        mock_data_size: 100
      });
      setTestResults(response.data);
    } catch (error) {
      console.error('Error testing integration:', error);
    } finally {
      setTesting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'testing': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'error': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type, category) => {
    if (type === 'sap') {
      switch (category) {
        case 'sap_s4hana': return <Factory className="h-5 w-5 text-blue-600" />;
        case 'sap_btp': return <Globe className="h-5 w-5 text-purple-600" />;
        case 'sap_datasphere': return <Database className="h-5 w-5 text-green-600" />;
        case 'sap_successfactors': return <Users className="h-5 w-5 text-orange-600" />;
        case 'sap_ibp': return <LineChart className="h-5 w-5 text-red-600" />;
        default: return <Plug className="h-5 w-5 text-blue-600" />;
      }
    } else if (type === 'third_party') {
      return <Globe className="h-5 w-5 text-purple-600" />;
    } else if (type === 'database') {
      return <Database className="h-5 w-5 text-gray-600" />;
    }
    return <Plug className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading integrations...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Integrations Layer</h1>
        <p className="text-gray-600">SAP modules and third-party system integrations</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="integrations-overview-tab">
            Overview ({integrations.length})
          </TabsTrigger>
          <TabsTrigger value="sap" data-testid="integrations-sap-tab">
            SAP Modules
          </TabsTrigger>
          <TabsTrigger value="data-flows" data-testid="integrations-flows-tab">
            Data Flows
          </TabsTrigger>
          <TabsTrigger value="testing" data-testid="integrations-testing-tab">
            Testing
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {analytics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Integrations</CardTitle>
                    <Plug className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.total_integrations}</div>
                    <p className="text-xs text-muted-foreground">
                      {analytics.active_integrations} active
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Data Processed</CardTitle>
                    <Database className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {((analytics.data_flow_summary.total_inbound_records + analytics.data_flow_summary.total_outbound_records) / 1000).toFixed(1)}K
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {analytics.data_flow_summary.total_data_size_mb.toFixed(1)}MB total
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                    <CheckCircle className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      {analytics.data_flow_summary.success_rate.toFixed(1)}%
                    </div>
                    <p className="text-xs text-muted-foreground">Data flow reliability</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Uptime</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {analytics.performance_metrics.uptime_percentage.toFixed(1)}%
                    </div>
                    <p className="text-xs text-muted-foreground">System availability</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Integration Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">SAP Modules</span>
                        <Badge variant="outline" className="bg-blue-50">
                          {analytics.integration_types.sap}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Third Party</span>
                        <Badge variant="outline" className="bg-purple-50">
                          {analytics.integration_types.third_party}
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm">Databases</span>
                        <Badge variant="outline" className="bg-gray-50">
                          {analytics.integration_types.database}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>SAP Module Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.sap_module_status).map(([module, status]) => (
                        <div key={module} className="flex justify-between items-center">
                          <span className="text-sm capitalize">{module.replace('_', ' ')}</span>
                          <Badge className={getStatusColor(status)}>{status}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Performance Metrics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-xl font-bold text-blue-600">
                          {analytics.performance_metrics.avg_processing_time_ms.toFixed(0)}ms
                        </div>
                        <div className="text-xs text-gray-600">Avg Processing Time</div>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="text-xl font-bold text-red-600">
                          {analytics.performance_metrics.total_errors}
                        </div>
                        <div className="text-xs text-gray-600">Total Errors</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}

          <div className="grid gap-4">
            {integrations.map((integration) => (
              <Card 
                key={integration.id} 
                className={`cursor-pointer transition-colors ${selectedIntegration?.id === integration.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => selectIntegration(integration)}
                data-testid={`integration-${integration.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getTypeIcon(integration.integration_type, integration.category)}
                      <span>{integration.name}</span>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={getStatusColor(integration.status)}>
                        {integration.status}
                      </Badge>
                      <Badge variant="outline">
                        {integration.data_flow_direction}
                      </Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {integration.category.replace('_', ' ').toUpperCase()} • 
                    {integration.authentication_type} • 
                    Last sync: {integration.last_sync ? new Date(integration.last_sync).toLocaleString() : 'Never'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Records In:</span>
                      <div className="text-gray-600">{integration.records_processed_in.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="font-medium">Records Out:</span>
                      <div className="text-gray-600">{integration.records_processed_out.toLocaleString()}</div>
                    </div>
                    <div>
                      <span className="font-medium">Errors:</span>
                      <div className={integration.error_count > 0 ? 'text-red-600' : 'text-green-600'}>
                        {integration.error_count}
                      </div>
                    </div>
                    <div>
                      <Button 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          testIntegration(integration.id);
                        }}
                        disabled={testing}
                        data-testid={`test-integration-${integration.id}`}
                      >
                        <TestTube className="h-3 w-3 mr-1" />
                        Test
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sap" className="space-y-4">
          <div className="grid gap-4">
            {integrations.filter(i => i.integration_type === 'sap').map((integration) => (
              <Card key={integration.id} data-testid={`sap-integration-${integration.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    {getTypeIcon(integration.integration_type, integration.category)}
                    {integration.name}
                    <Badge className={getStatusColor(integration.status)}>
                      {integration.status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {integration.endpoint_url}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-blue-50 rounded-lg">
                        <div className="text-lg font-bold text-blue-600">
                          {integration.records_processed_in.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Inbound Records</div>
                      </div>
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-lg font-bold text-green-600">
                          {integration.records_processed_out.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-600">Outbound Records</div>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="text-lg font-bold text-red-600">
                          {integration.error_count}
                        </div>
                        <div className="text-xs text-gray-600">Errors</div>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        onClick={() => testIntegration(integration.id, 'connectivity')}
                        disabled={testing}
                      >
                        <Play className="h-3 w-3 mr-1" />
                        Test Connectivity
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => testIntegration(integration.id, 'data_flow')}
                        disabled={testing}
                      >
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Test Data Flow
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="data-flows" className="space-y-4">
          {selectedIntegration ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>{selectedIntegration.name} - Data Flow History</CardTitle>
                  <CardDescription>
                    Inbound and outbound data processing metrics
                  </CardDescription>
                </CardHeader>
              </Card>
              
              <div className="grid gap-4">
                {dataFlows.map((flow) => (
                  <Card key={flow.id} data-testid={`data-flow-${flow.id}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between text-base">
                        <span className="capitalize">{flow.direction} - {flow.data_type}</span>
                        <div className="flex gap-2">
                          <Badge className={flow.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                            {flow.status}
                          </Badge>
                          <Badge variant="outline">
                            {flow.records_count} records
                          </Badge>
                        </div>
                      </CardTitle>
                      <CardDescription>
                        {new Date(flow.timestamp).toLocaleString()} • 
                        {flow.data_size_mb}MB • 
                        Processed in {flow.processing_time_ms}ms
                      </CardDescription>
                    </CardHeader>
                  </Card>
                ))}
              </div>
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Integration Selected</h3>
                <p className="text-gray-600">Select an integration to view its data flow history</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="testing" className="space-y-4">
          {testResults ? (
            <Card data-testid="integration-test-results">
              <CardHeader>
                <CardTitle>{testResults.integration_name} - Test Results</CardTitle>
                <CardDescription>
                  {testResults.test_type} test performed on {new Date(testResults.tested_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className={`text-2xl font-bold ${testResults.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {testResults.status.toUpperCase()}
                      </div>
                      <div className="text-sm text-gray-600">Test Status</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {testResults.connectivity_ms}ms
                      </div>
                      <div className="text-sm text-gray-600">Connectivity</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {testResults.throughput_records_per_sec}/sec
                      </div>
                      <div className="text-sm text-gray-600">Throughput</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">
                        {testResults.mock_data_processed}
                      </div>
                      <div className="text-sm text-gray-600">Records Tested</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">AI Analysis</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{testResults.ai_analysis}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Recommendations</h4>
                    <div className="space-y-2">
                      {testResults.recommendations.map((rec, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 bg-white border rounded-lg">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <TestTube className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Tests Run</h3>
                <p className="text-gray-600 mb-4">
                  Select an integration and click "Test" to run connectivity and data flow tests
                </p>
                <Button disabled={testing}>
                  {testing ? 'Testing...' : 'Run Integration Tests'}
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== COMPREHENSIVE SAP INTEGRATION COMPONENT ====================

const SAPIntegrationManager = () => {
  const [sapModules, setSapModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchSAPData();
  }, []);

  const fetchSAPData = async () => {
    try {
      const [modulesRes, dashboardRes] = await Promise.all([
        axios.get(`${API}/sap/modules`),
        axios.get(`${API}/sap/dashboard`)
      ]);
      setSapModules(modulesRes.data);
      setDashboard(dashboardRes.data);
    } catch (error) {
      console.error('Error fetching SAP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectModule = async (module) => {
    setSelectedModule(module);
    try {
      const [mappingsRes, logsRes, metricsRes, testsRes] = await Promise.all([
        axios.get(`${API}/sap/modules/${module.id}/mappings`),
        axios.get(`${API}/sap/modules/${module.id}/sync-logs?limit=20`),
        axios.get(`${API}/sap/modules/${module.id}/metrics`),
        axios.get(`${API}/sap/modules/${module.id}/test-history`)
      ]);
      setMappings(mappingsRes.data);
      setSyncLogs(logsRes.data);
      setMetrics(metricsRes.data);
      setTestResults(testsRes.data);
    } catch (error) {
      console.error('Error fetching module details:', error);
    }
  };

  const testModule = async (moduleId, testType) => {
    setTesting(true);
    try {
      await axios.post(`${API}/sap/modules/${moduleId}/test`, {
        module_config_id: moduleId,
        test_type: testType,
        timeout_seconds: 30,
        mock_mode: true
      });
      
      // Refresh test history
      const testsRes = await axios.get(`${API}/sap/modules/${moduleId}/test-history`);
      setTestResults(testsRes.data);
    } catch (error) {
      console.error('Error testing module:', error);
    } finally {
      setTesting(false);
    }
  };

  const triggerSync = async (moduleId) => {
    setSyncing(true);
    try {
      await axios.post(`${API}/sap/modules/${moduleId}/sync`, {
        direction: 'bidirectional'
      });
      
      // Refresh sync logs and metrics
      const [logsRes, metricsRes] = await Promise.all([
        axios.get(`${API}/sap/modules/${moduleId}/sync-logs?limit=20`),
        axios.get(`${API}/sap/modules/${moduleId}/metrics`)
      ]);
      setSyncLogs(logsRes.data);
      setMetrics(metricsRes.data);
    } catch (error) {
      console.error('Error triggering sync:', error);
    } finally {
      setSyncing(false);
    }
  };

  const getModuleCategoryIcon = (category) => {
    const icons = {
      's4hana': <Factory className="h-5 w-5 text-blue-600" />,
      'ibp': <LineChart className="h-5 w-5 text-purple-600" />,
      'btp': <Globe className="h-5 w-5 text-green-600" />,
      'datasphere': <Database className="h-5 w-5 text-cyan-600" />,
      'successfactors': <Users className="h-5 w-5 text-orange-600" />,
      'ariba': <Package className="h-5 w-5 text-red-600" />,
      'eam': <Settings className="h-5 w-5 text-gray-600" />,
      'otc': <DollarSign className="h-5 w-5 text-yellow-600" />
    };
    return icons[category] || <Plug className="h-5 w-5" />;
  };

  const getSyncStatusColor = (status) => {
    const colors = {
      'idle': 'bg-gray-100 text-gray-800',
      'syncing': 'bg-blue-100 text-blue-800',
      'success': 'bg-green-100 text-green-800',
      'failed': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading SAP integration manager...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">SAP Integration Manager</h1>
        <p className="text-gray-600">Comprehensive SAP module configuration, testing, and monitoring</p>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-6">
        <TabsList>
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="modules">Modules ({sapModules.length})</TabsTrigger>
          <TabsTrigger value="testing">Testing</TabsTrigger>
          <TabsTrigger value="mappings">Field Mappings</TabsTrigger>
          <TabsTrigger value="sync">Sync Operations</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {dashboard && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Modules</CardTitle>
                    <Plug className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboard.summary.total_modules}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard.summary.active_modules} active
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Syncs Today</CardTitle>
                    <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{dashboard.summary.total_syncs_today}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboard.summary.success_rate.toFixed(1)}% success rate
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Records Processed</CardTitle>
                    <Database className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(dashboard.summary.total_records_processed / 1000).toFixed(1)}K
                    </div>
                    <p className="text-xs text-muted-foreground">Total records synced</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Avg Sync Time</CardTitle>
                    <Clock className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {dashboard.summary.avg_sync_time_ms.toFixed(0)}ms
                    </div>
                    <p className="text-xs text-muted-foreground">Average processing time</p>
                  </CardContent>
                </Card>
              </div>

              {/* Modules by Category */}
              <Card>
                <CardHeader>
                  <CardTitle>SAP Modules by Category</CardTitle>
                  <CardDescription>Distribution across SAP products</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(dashboard.modules_by_category).map(([category, count]) => (
                      <div key={category} className="text-center p-4 bg-gray-50 rounded-lg">
                        <div className="flex justify-center mb-2">
                          {getModuleCategoryIcon(category)}
                        </div>
                        <div className="text-2xl font-bold text-gray-900">{count}</div>
                        <div className="text-xs text-gray-600 uppercase">{category}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Syncs */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent Sync Operations</CardTitle>
                  <CardDescription>Latest 10 sync activities</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {dashboard.recent_syncs.slice(0, 10).map((sync, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <RefreshCw className="h-4 w-4 text-blue-600" />
                          <div>
                            <div className="font-medium text-sm">
                              {sync.records_synced} records • {sync.sync_direction}
                            </div>
                            <div className="text-xs text-gray-600">
                              {new Date(sync.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <Badge className={sync.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                          {sync.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Modules Tab */}
        <TabsContent value="modules" className="space-y-4">
          <div className="grid gap-4">
            {sapModules.map((module) => (
              <Card 
                key={module.id}
                className={`cursor-pointer transition-colors ${selectedModule?.id === module.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => selectModule(module)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getModuleCategoryIcon(module.module_category)}
                      <div>
                        <div>{module.module_name}</div>
                        <div className="text-sm font-normal text-gray-600">
                          {module.module_category.toUpperCase()}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={getSyncStatusColor(module.sync_status)}>
                        {module.sync_status}
                      </Badge>
                      <Badge variant={module.is_enabled ? 'default' : 'outline'}>
                        {module.is_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {module.endpoint} • {module.sync_frequency} sync • 
                    Last sync: {module.last_sync ? new Date(module.last_sync).toLocaleString() : 'Never'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button 
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        triggerSync(module.id);
                      }}
                      disabled={syncing}
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Sync Now
                    </Button>
                    <Button 
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        testModule(module.id, 'connectivity');
                      }}
                      disabled={testing}
                    >
                      <TestTube className="h-3 w-3 mr-1" />
                      Test
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Testing Tab */}
        <TabsContent value="testing" className="space-y-4">
          {selectedModule ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Test {selectedModule.module_name}</CardTitle>
                  <CardDescription>
                    Run comprehensive tests on this SAP module
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {['connectivity', 'authentication', 'data_flow', 'performance', 'error_handling'].map((testType) => (
                      <Button
                        key={testType}
                        onClick={() => testModule(selectedModule.id, testType)}
                        disabled={testing}
                        className="w-full"
                      >
                        <TestTube className="h-3 w-3 mr-1" />
                        {testType.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </Button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Test Results */}
              {testResults.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Test History</CardTitle>
                    <CardDescription>Recent test results for this module</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {testResults.slice(0, 10).map((test) => (
                        <div key={test.id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge className={test.status === 'passed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {test.status}
                              </Badge>
                              <span className="font-medium">{test.test_type}</span>
                            </div>
                            <div className="text-sm text-gray-600">
                              {test.response_time_ms}ms
                            </div>
                          </div>
                          <div className="text-xs text-gray-600">
                            {new Date(test.timestamp).toLocaleString()}
                          </div>
                          {test.details && (
                            <div className="mt-2 text-sm">
                              {Object.entries(test.details).map(([key, value]) => (
                                <div key={key} className="flex justify-between py-1">
                                  <span className="text-gray-600">{key}:</span>
                                  <span className="font-medium">{String(value)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Module Metrics */}
              {metrics && (
                <Card>
                  <CardHeader>
                    <CardTitle>Performance Metrics</CardTitle>
                    <CardDescription>Overall module performance statistics</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                          {metrics.total_syncs}
                        </div>
                        <div className="text-sm text-gray-600">Total Syncs</div>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {metrics.uptime_percentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600">Uptime</div>
                      </div>
                      <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                          {metrics.avg_sync_time_ms.toFixed(0)}ms
                        </div>
                        <div className="text-sm text-gray-600">Avg Sync Time</div>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-600">
                          {metrics.error_rate.toFixed(1)}%
                        </div>
                        <div className="text-sm text-gray-600">Error Rate</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <TestTube className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Module Selected</h3>
                <p className="text-gray-600">Select a SAP module to run tests</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Field Mappings Tab */}
        <TabsContent value="mappings" className="space-y-4">
          {selectedModule ? (
            <Card>
              <CardHeader>
                <CardTitle>Field Mappings - {selectedModule.module_name}</CardTitle>
                <CardDescription>
                  Data field mapping between PetraMind and SAP
                </CardDescription>
              </CardHeader>
              <CardContent>
                {mappings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-semibold">PetraMind Field</th>
                          <th className="text-left p-3 font-semibold">SAP Field</th>
                          <th className="text-left p-3 font-semibold">Data Type</th>
                          <th className="text-left p-3 font-semibold">Required</th>
                          <th className="text-left p-3 font-semibold">Transformation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mappings.map((mapping) => (
                          <tr key={mapping.id} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{mapping.petra_field}</td>
                            <td className="p-3 font-mono text-sm">{mapping.sap_field}</td>
                            <td className="p-3">
                              <Badge variant="outline">{mapping.data_type}</Badge>
                            </td>
                            <td className="p-3">
                              {mapping.is_required ? (
                                <CheckCircle className="h-4 w-4 text-green-600" />
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </td>
                            <td className="p-3 text-sm text-gray-600">
                              {mapping.transformation || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No field mappings configured for this module
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Database className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Module Selected</h3>
                <p className="text-gray-600">Select a SAP module to view field mappings</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Sync Operations Tab */}
        <TabsContent value="sync" className="space-y-4">
          {selectedModule ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Sync Management - {selectedModule.module_name}</CardTitle>
                  <CardDescription>
                    Manual sync trigger and sync history
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={() => triggerSync(selectedModule.id)}
                    disabled={syncing}
                    size="lg"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {syncing ? 'Syncing...' : 'Trigger Manual Sync'}
                  </Button>
                </CardContent>
              </Card>

              {/* Sync Logs */}
              {syncLogs.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Sync History</CardTitle>
                    <CardDescription>Recent sync operations for this module</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {syncLogs.map((log) => (
                        <div key={log.id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <Badge className={log.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                {log.status}
                              </Badge>
                              <span className="font-medium capitalize">{log.sync_direction}</span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {log.sync_duration_ms}ms
                            </span>
                          </div>
                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <span className="text-gray-600">Synced:</span>
                              <span className="font-medium ml-2">{log.records_synced}</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Failed:</span>
                              <span className="font-medium ml-2">{log.records_failed}</span>
                            </div>
                            <div className="text-gray-600">
                              {new Date(log.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <RefreshCw className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Module Selected</h3>
                <p className="text-gray-600">Select a SAP module to manage sync operations</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== AI STUDIO COMPONENT ====================

const AIStudio = () => {
  const [aiModels, setAiModels] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [modelPerformance, setModelPerformance] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [promptTest, setPromptTest] = useState({ prompt: '', models: ['gpt-4o-mini'] });
  const [promptResults, setPromptResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchAIStudioData();
  }, []);

  const fetchAIStudioData = async () => {
    try {
      const [modelsResponse, analyticsResponse] = await Promise.all([
        axios.get(`${API}/ai-studio/models`),
        axios.get(`${API}/ai-studio/analytics`)
      ]);
      setAiModels(modelsResponse.data);
      setAnalytics(analyticsResponse.data);
    } catch (error) {
      console.error('Error fetching AI Studio data:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectModel = async (model) => {
    setSelectedModel(model);
    try {
      const performanceResponse = await axios.get(`${API}/ai-studio/performance/${model.id}`);
      setModelPerformance(performanceResponse.data);
    } catch (error) {
      console.error('Error fetching model performance:', error);
    }
  };

  const testModel = async (modelId, testPrompt) => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/ai-studio/models/${modelId}/test`, {
        model_id: modelId,
        test_prompt: testPrompt
      });
      setTestResults(response.data);
    } catch (error) {
      console.error('Error testing model:', error);
    } finally {
      setTesting(false);
    }
  };

  const testPromptEngineering = async () => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/ai-studio/prompt-engineering`, promptTest);
      setPromptResults(response.data);
    } catch (error) {
      console.error('Error testing prompt engineering:', error);
    } finally {
      setTesting(false);
    }
  };

  const getProviderColor = (provider) => {
    switch (provider) {
      case 'openai': return 'bg-green-100 text-green-800';
      case 'anthropic': return 'bg-orange-100 text-orange-800';
      case 'gemini': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUseCaseIcon = (useCase) => {
    switch (useCase) {
      case 'maintenance_prediction': return <Settings className="h-4 w-4" />;
      case 'compliance_analysis': return <Shield className="h-4 w-4" />;
      case 'market_analysis': return <LineChart className="h-4 w-4" />;
      case 'esg_reporting': return <Leaf className="h-4 w-4" />;
      case 'executive_analytics': return <BarChart3 className="h-4 w-4" />;
      default: return <Bot className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading AI Studio...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Studio</h1>
        <p className="text-gray-600">Centralized AI model management and configuration</p>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview" data-testid="ai-studio-overview-tab">
            Overview ({aiModels.length})
          </TabsTrigger>
          <TabsTrigger value="models" data-testid="ai-studio-models-tab">
            Model Management
          </TabsTrigger>
          <TabsTrigger value="performance" data-testid="ai-studio-performance-tab">
            Performance
          </TabsTrigger>
          <TabsTrigger value="prompt-engineering" data-testid="ai-studio-prompts-tab">
            Prompt Engineering
          </TabsTrigger>
          <TabsTrigger value="aws-bedrock">
            AWS Bedrock
          </TabsTrigger>
          <TabsTrigger value="sap-joule">
            SAP Joule
          </TabsTrigger>
          <TabsTrigger value="btp-deployment">
            BTP Deployment
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {analytics && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">AI Models</CardTitle>
                    <Bot className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analytics.total_models}</div>
                    <p className="text-xs text-muted-foreground">
                      {analytics.active_models} active
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">
                      {(analytics.performance_summary.total_requests / 1000).toFixed(1)}K
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {analytics.performance_summary.overall_success_rate.toFixed(1)}% success rate
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Performance Score</CardTitle>
                    <Target className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">
                      {analytics.performance_summary.avg_performance_score.toFixed(1)}/100
                    </div>
                    <p className="text-xs text-muted-foreground">Average across all models</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Monthly Cost</CardTitle>
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">
                      ${analytics.performance_summary.total_cost_30d.toFixed(0)}
                    </div>
                    <p className="text-xs text-muted-foreground">Last 30 days</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Provider Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.provider_distribution).map(([provider, count]) => (
                        <div key={provider} className="flex justify-between items-center">
                          <span className="text-sm capitalize">{provider}</span>
                          <Badge className={getProviderColor(provider)}>{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Use Case Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(analytics.use_case_distribution).map(([useCase, count]) => (
                        <div key={useCase} className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            {getUseCaseIcon(useCase)}
                            <span className="text-sm capitalize">{useCase.replace('_', ' ')}</span>
                          </div>
                          <Badge variant="outline">{count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Models</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {analytics.top_performing_models.slice(0, 5).map((model, index) => (
                        <div key={index} className="flex justify-between items-center">
                          <div>
                            <div className="text-sm font-medium">{model.name}</div>
                            <div className="text-xs text-gray-500">{model.use_case}</div>
                          </div>
                          <Badge variant="outline" className="bg-green-50">
                            {model.performance_score.toFixed(0)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="models" className="space-y-4">
          <div className="grid gap-4">
            {aiModels.map((model) => (
              <Card 
                key={model.id} 
                className={`cursor-pointer transition-colors ${selectedModel?.id === model.id ? 'ring-2 ring-blue-500' : ''}`}
                onClick={() => selectModel(model)}
                data-testid={`ai-model-${model.id}`}
              >
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getUseCaseIcon(model.use_case)}
                      <span>{model.name}</span>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={getProviderColor(model.provider)}>
                        {model.provider}
                      </Badge>
                      <Badge variant="outline">{model.model_name}</Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {model.use_case.replace('_', ' ')} • 
                    Performance: {model.performance_score.toFixed(1)}/100 • 
                    {model.total_requests.toLocaleString()} total requests
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Temperature:</span>
                        <div className="text-gray-600">{model.temperature}</div>
                      </div>
                      <div>
                        <span className="font-medium">Max Tokens:</span>
                        <div className="text-gray-600">{model.max_tokens}</div>
                      </div>
                      <div>
                        <span className="font-medium">Success Rate:</span>
                        <div className="text-green-600">
                          {((model.successful_requests / model.total_requests) * 100).toFixed(1)}%
                        </div>
                      </div>
                      <div>
                        <Button 
                          size="sm" 
                          onClick={(e) => {
                            e.stopPropagation();
                            testModel(model.id, 'Test prompt for AI model evaluation');
                          }}
                          disabled={testing}
                          data-testid={`test-model-${model.id}`}
                        >
                          <Play className="h-3 w-3 mr-1" />
                          Test
                        </Button>
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-sm mb-2">Prompt Template:</h4>
                      <div className="bg-gray-50 p-3 rounded text-xs font-mono">
                        {model.prompt_template}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          {selectedModel && modelPerformance ? (
            <Card data-testid="model-performance">
              <CardHeader>
                <CardTitle>{selectedModel.name} - Performance Analytics</CardTitle>
                <CardDescription>
                  {modelPerformance.period_days} days of performance data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-2xl font-bold text-blue-600">
                        {modelPerformance.avg_success_rate.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Success Rate</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-600">
                        {modelPerformance.avg_response_time_ms}ms
                      </div>
                      <div className="text-sm text-gray-600">Avg Response Time</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-600">
                        {modelPerformance.total_requests.toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Total Requests</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-600">
                        ${modelPerformance.total_cost_usd.toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-600">Total Cost</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Daily Performance Trends</h4>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {modelPerformance.daily_metrics.slice(0, 10).map((metric, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-white border rounded">
                          <span className="text-sm">{new Date(metric.date).toLocaleDateString()}</span>
                          <div className="flex items-center gap-4 text-sm">
                            <span>{metric.request_count} requests</span>
                            <span className="text-green-600">{metric.success_rate.toFixed(1)}%</span>
                            <span className="text-blue-600">{metric.avg_response_time_ms}ms</span>
                            <span className="text-yellow-600">${metric.cost_usd.toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <BarChart3 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Model Selected</h3>
                <p className="text-gray-600">Select an AI model to view detailed performance analytics</p>
              </CardContent>
            </Card>
          )}

          {testResults && (
            <Card data-testid="model-test-results">
              <CardHeader>
                <CardTitle>{testResults.model_name} - Test Results</CardTitle>
                <CardDescription>
                  Model test performed on {new Date(testResults.tested_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-blue-50 rounded-lg">
                      <div className={`text-xl font-bold ${testResults.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                        {testResults.status.toUpperCase()}
                      </div>
                      <div className="text-xs text-gray-600">Test Status</div>
                    </div>
                    {testResults.response_time_ms && (
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-xl font-bold text-green-600">
                          {testResults.response_time_ms}ms
                        </div>
                        <div className="text-xs text-gray-600">Response Time</div>
                      </div>
                    )}
                    <div className="text-center p-3 bg-purple-50 rounded-lg">
                      <div className="text-xl font-bold text-purple-600">
                        {testResults.model_config?.model_name}
                      </div>
                      <div className="text-xs text-gray-600">Model Used</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2">Test Prompt:</h4>
                    <div className="bg-gray-50 p-3 rounded text-sm">
                      {testResults.test_prompt}
                    </div>
                  </div>

                  {testResults.ai_response && (
                    <div>
                      <h4 className="font-semibold mb-2">AI Response:</h4>
                      <div className="bg-blue-50 p-3 rounded text-sm">
                        {testResults.ai_response}
                      </div>
                    </div>
                  )}

                  {testResults.error && (
                    <div>
                      <h4 className="font-semibold mb-2 text-red-600">Error:</h4>
                      <div className="bg-red-50 p-3 rounded text-sm text-red-700">
                        {testResults.error}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="prompt-engineering" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Prompt Engineering Lab</CardTitle>
              <CardDescription>
                Test and optimize prompts across different AI models
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Test Prompt:</label>
                  <Textarea
                    value={promptTest.prompt}
                    onChange={(e) => setPromptTest({...promptTest, prompt: e.target.value})}
                    placeholder="Enter your prompt to test across different models..."
                    className="min-h-[100px]"
                    data-testid="prompt-input"
                  />
                </div>

                <div className="flex gap-4">
                  <Button 
                    onClick={testPromptEngineering}
                    disabled={testing || !promptTest.prompt.trim()}
                    data-testid="test-prompt-button"
                  >
                    {testing ? 'Testing...' : 'Test Prompt Across Models'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {promptResults && (
            <Card data-testid="prompt-engineering-results">
              <CardHeader>
                <CardTitle>Prompt Engineering Results</CardTitle>
                <CardDescription>
                  Tested on {new Date(promptResults.tested_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2">Tested Prompt:</h4>
                    <div className="bg-gray-50 p-3 rounded text-sm">
                      {promptResults.test_prompt}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Model Responses:</h4>
                    <div className="space-y-4">
                      {promptResults.results.map((result, index) => (
                        <Card key={index}>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base flex items-center justify-between">
                              <span>{result.model} ({result.provider})</span>
                              <div className="flex gap-2">
                                <Badge className={result.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                  {result.status}
                                </Badge>
                                {result.quality_score > 0 && (
                                  <Badge variant="outline">
                                    Quality: {result.quality_score.toFixed(0)}%
                                  </Badge>
                                )}
                              </div>
                            </CardTitle>
                            {result.response_time_ms && (
                              <CardDescription>
                                Response time: {result.response_time_ms}ms
                              </CardDescription>
                            )}
                          </CardHeader>
                          <CardContent>
                            {result.response ? (
                              <div className="bg-blue-50 p-3 rounded text-sm">
                                {result.response}
                              </div>
                            ) : (
                              <div className="bg-red-50 p-3 rounded text-sm text-red-700">
                                Error: {result.error}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">AI Analysis & Recommendations:</h4>
                    <div className="bg-gray-50 p-4 rounded">
                      <p className="text-gray-700 whitespace-pre-wrap">{promptResults.ai_analysis}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!promptResults && (
            <Card>
              <CardContent className="text-center py-12">
                <Code className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Prompt Tests Run</h3>
                <p className="text-gray-600 mb-4">
                  Enter a prompt above and test it across different AI models to compare responses
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="aws-bedrock" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Cloud className="h-6 w-6 text-orange-600" />
                  <span>AWS Bedrock Integration</span>
                </div>
                <Badge className="bg-orange-100 text-orange-800">Ready</Badge>
              </CardTitle>
              <CardDescription>Claude & Titan models for text generation and analysis</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Claude 3 Sonnet</CardTitle>
                      <CardDescription>Advanced reasoning and analysis</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Status:</span>
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Model ID:</span>
                          <span className="font-mono text-xs">anthropic.claude-3-sonnet-v1</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Use Cases:</span>
                          <span className="text-right">Complex Analysis, Seismic Data</span>
                        </div>
                        <Button className="w-full mt-4" size="sm">
                          <Settings className="h-4 w-4 mr-2" />
                          Configure Model
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Amazon Titan</CardTitle>
                      <CardDescription>Cost-effective text generation</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Status:</span>
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Model ID:</span>
                          <span className="font-mono text-xs">amazon.titan-text-lite-v1</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Use Cases:</span>
                          <span className="text-right">Summaries, Reports</span>
                        </div>
                        <Button className="w-full mt-4" size="sm">
                          <Settings className="h-4 w-4 mr-2" />
                          Configure Model
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>System Prompts for Oil & Gas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <div className="font-medium text-sm mb-1">Drilling Analysis</div>
                        <div className="text-xs text-gray-600">ROP, WOB, RPM analysis</div>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg">
                        <div className="font-medium text-sm mb-1">Seismic Interpretation</div>
                        <div className="text-xs text-gray-600">Fault systems, traps</div>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <div className="font-medium text-sm mb-1">Maintenance Prediction</div>
                        <div className="text-xs text-gray-600">Equipment health analysis</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>API Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">AWS Region</label>
                        <input type="text" className="w-full mt-1 p-2 border rounded" placeholder="us-east-1" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">IAM Role ARN</label>
                        <input type="text" className="w-full mt-1 p-2 border rounded" placeholder="arn:aws:iam::..." />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Max Retries</label>
                          <input type="number" className="w-full mt-1 p-2 border rounded" defaultValue="3" />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Timeout (seconds)</label>
                          <input type="number" className="w-full mt-1 p-2 border rounded" defaultValue="60" />
                        </div>
                      </div>
                      <Button className="w-full">
                        <Check className="h-4 w-4 mr-2" />
                        Save Configuration
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sap-joule" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bot className="h-6 w-6 text-blue-600" />
                  <span>SAP Joule AI Copilot</span>
                </div>
                <Badge className="bg-blue-100 text-blue-800">Ready</Badge>
              </CardTitle>
              <CardDescription>Conversational AI for business insights and process automation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">4</div>
                    <div className="text-xs text-gray-600 mt-1">Interaction Modes</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">14</div>
                    <div className="text-xs text-gray-600 mt-1">SAP Modules</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-600">Active</div>
                    <div className="text-xs text-gray-600 mt-1">Connection Status</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-orange-600">OAuth2</div>
                    <div className="text-xs text-gray-600 mt-1">Authentication</div>
                  </div>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Interaction Modes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        { name: 'Transactional', desc: 'Execute CRUD operations via natural language', color: 'blue' },
                        { name: 'Navigational', desc: 'Find features and applications', color: 'green' },
                        { name: 'Informational', desc: 'Answer business terminology questions', color: 'purple' },
                        { name: 'Analytical', desc: 'Generate insights and recommendations', color: 'orange' }
                      ].map((mode) => (
                        <div key={mode.name} className="p-3 border rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-full bg-${mode.color}-100 flex items-center justify-center`}>
                              <Check className={`h-5 w-5 text-${mode.color}-600`} />
                            </div>
                            <div>
                              <div className="font-medium">{mode.name}</div>
                              <div className="text-sm text-gray-600">{mode.desc}</div>
                            </div>
                          </div>
                          <Badge className={`bg-${mode.color}-100 text-${mode.color}-800`}>Enabled</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>BTP Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">Formation ID</label>
                        <input type="text" className="w-full mt-1 p-2 border rounded" placeholder="joule-formation-001" />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Auth URL</label>
                        <input type="text" className="w-full mt-1 p-2 border rounded" placeholder="https://your-instance.authentication.eu10.hana.ondemand.com" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Client ID</label>
                          <input type="text" className="w-full mt-1 p-2 border rounded" placeholder="client-id" />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Client Secret</label>
                          <input type="password" className="w-full mt-1 p-2 border rounded" placeholder="••••••••" />
                        </div>
                      </div>
                      <Button className="w-full">
                        <Check className="h-4 w-4 mr-2" />
                        Test Connection
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="btp-deployment" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Server className="h-6 w-6 text-indigo-600" />
                  <span>SAP BTP Cloud Foundry Deployment</span>
                </div>
                <Badge className="bg-indigo-100 text-indigo-800">Configured</Badge>
              </CardTitle>
              <CardDescription>Deploy Python FastAPI applications to SAP Business Technology Platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 bg-indigo-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-indigo-600">Python 3.11</div>
                    <div className="text-xs text-gray-600 mt-1">Runtime Version</div>
                  </div>
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">512 MB</div>
                    <div className="text-xs text-gray-600 mt-1">Memory Allocation</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">1</div>
                    <div className="text-xs text-gray-600 mt-1">App Instances</div>
                  </div>
                </div>

                <Card>
                  <CardHeader>
                    <CardTitle>Deployment Configuration</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">App Name</label>
                        <input type="text" className="w-full mt-1 p-2 border rounded" defaultValue="oil-gas-platform" />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">CF API Endpoint</label>
                          <input type="text" className="w-full mt-1 p-2 border rounded" placeholder="api.cf.eu10.hana.ondemand.com" />
                        </div>
                        <div>
                          <label className="text-sm font-medium">Organization</label>
                          <input type="text" className="w-full mt-1 p-2 border rounded" placeholder="your-org" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">Space</label>
                          <select className="w-full mt-1 p-2 border rounded">
                            <option>development</option>
                            <option>staging</option>
                            <option>production</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">Build Pack</label>
                          <input type="text" className="w-full mt-1 p-2 border rounded" defaultValue="python_buildpack" readOnly />
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>manifest.yaml Preview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-900 text-gray-100 p-4 rounded-lg font-mono text-xs overflow-x-auto">
                      <pre>{`---
applications:
  - name: oil-gas-platform
    memory: 512M
    instances: 1
    buildpacks:
      - python_buildpack
    path: .
    env:
      PYTHONUNBUFFERED: true
      LOG_LEVEL: INFO
    timeout: 180
    health-check-type: http
    health-check-http-endpoint: /health
    command: uvicorn app.main:app --host 0.0.0.0 --port $PORT`}</pre>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Environment Variables</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                        <div>
                          <div className="font-medium text-sm">MONGODB_URL</div>
                          <div className="text-xs text-gray-600">MongoDB connection string</div>
                        </div>
                        <Badge className="bg-green-100 text-green-800">Set</Badge>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                        <div>
                          <div className="font-medium text-sm">MONGODB_DB</div>
                          <div className="text-xs text-gray-600">Database name</div>
                        </div>
                        <Badge className="bg-green-100 text-green-800">Set</Badge>
                      </div>
                      <div className="p-3 bg-gray-50 rounded-lg flex justify-between items-center">
                        <div>
                          <div className="font-medium text-sm">LOG_LEVEL</div>
                          <div className="text-xs text-gray-600">Application log level</div>
                        </div>
                        <Badge className="bg-green-100 text-green-800">Set</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 gap-4">
                  <Button className="w-full" variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Download Config Files
                  </Button>
                  <Button className="w-full">
                    <Upload className="h-4 w-4 mr-2" />
                    Deploy to BTP
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

// ==================== ADVANCED ENTERPRISE MODULES ====================

// Field Operations Component
const FieldOperations = () => {
  const [operations, setOperations] = useState([]);
  const [optimization, setOptimization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);

  useEffect(() => {
    fetchOperations();
  }, []);

  const fetchOperations = async () => {
    try {
      const response = await axios.get(`${API}/field-operations`);
      setOperations(response.data);
    } catch (error) {
      console.error('Error fetching field operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const optimizeOperation = async (operationId) => {
    setOptimizing(true);
    try {
      const response = await axios.post(`${API}/field-operations/${operationId}/ai-optimize`);
      setOptimization(response.data);
    } catch (error) {
      console.error('Error optimizing operation:', error);
    } finally {
      setOptimizing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'planned': return 'bg-yellow-100 text-yellow-800';
      case 'delayed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-green-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading field operations...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Field Operations</h1>
        <p className="text-gray-600">AI-enhanced field operations management and optimization</p>
      </div>

      <div className="grid gap-6">
        {operations.map((operation) => (
          <Card key={operation.id} data-testid={`field-operation-${operation.id}`}>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <span className="capitalize">{operation.operation_type.replace('_', ' ')}</span>
                </div>
                <div className="flex gap-2">
                  <Badge className={getStatusColor(operation.status)}>
                    {operation.status}
                  </Badge>
                  <Badge variant="outline" className={getPriorityColor(operation.priority)}>
                    {operation.priority} priority
                  </Badge>
                </div>
              </CardTitle>
              <CardDescription>
                {operation.location} • Crew: {operation.assigned_crew.length} members
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <span className="text-sm font-medium">Safety Score:</span>
                    <div className="text-lg font-bold text-green-600">{operation.safety_score}/100</div>
                  </div>
                  <div>
                    <span className="text-sm font-medium">Scheduled:</span>
                    <div className="text-sm text-gray-600">
                      {new Date(operation.scheduled_start).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <span className="text-sm font-medium">Crew Members:</span>
                    <div className="text-sm text-gray-600">
                      {operation.assigned_crew.join(', ')}
                    </div>
                  </div>
                  <div>
                    <Button 
                      onClick={() => optimizeOperation(operation.id)}
                      disabled={optimizing}
                      size="sm"
                      data-testid={`optimize-operation-${operation.id}`}
                    >
                      {optimizing ? 'Optimizing...' : 'AI Optimize'}
                    </Button>
                  </div>
                </div>

                {operation.weather_conditions && (
                  <div>
                    <span className="text-sm font-medium">Weather Conditions:</span>
                    <div className="text-sm text-gray-600">{operation.weather_conditions}</div>
                  </div>
                )}

                {operation.equipment_required.length > 0 && (
                  <div>
                    <span className="text-sm font-medium mb-2 block">Required Equipment:</span>
                    <div className="flex flex-wrap gap-2">
                      {operation.equipment_required.map((equipment, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {equipment}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {optimization && (
        <Card data-testid="field-optimization-result">
          <CardHeader>
            <CardTitle>AI Optimization Results</CardTitle>
            <CardDescription>
              Operation optimization completed on {new Date(optimization.optimized_at).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-xl font-bold text-green-600">
                    {optimization.efficiency_gains.estimated_time_savings}
                  </div>
                  <div className="text-sm text-gray-600">Time Savings</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-xl font-bold text-blue-600">
                    {optimization.efficiency_gains.cost_reduction}
                  </div>
                  <div className="text-sm text-gray-600">Cost Reduction</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-xl font-bold text-purple-600">
                    {optimization.efficiency_gains.safety_score_improvement}
                  </div>
                  <div className="text-sm text-gray-600">Safety Improvement</div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">AI Recommendations</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{optimization.ai_recommendations}</p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Safety Improvements</h4>
                <div className="space-y-2">
                  {optimization.safety_improvements.map((improvement, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <HardHat className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{improvement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Workforce Safety Component
const WorkforceSafety = () => {
  const [incidents, setIncidents] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);

  useEffect(() => {
    fetchSafetyData();
  }, []);

  const fetchSafetyData = async () => {
    try {
      const [incidentsResponse, metricsResponse] = await Promise.all([
        axios.get(`${API}/safety/incidents`),
        axios.get(`${API}/safety/metrics`)
      ]);
      setIncidents(incidentsResponse.data);
      setMetrics(metricsResponse.data);
    } catch (error) {
      console.error('Error fetching safety data:', error);
    } finally {
      setLoading(false);
    }
  };

  const analyzeIncident = async (incidentId) => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/safety/ai-analysis/${incidentId}`);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error analyzing incident:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-green-100 text-green-800';
    }
  };

  const getIncidentIcon = (type) => {
    switch (type) {
      case 'near_miss': return <AlertTriangle className="h-4 w-4" />;
      case 'injury': return <Users className="h-4 w-4" />;
      case 'environmental': return <Leaf className="h-4 w-4" />;
      case 'equipment_failure': return <Settings className="h-4 w-4" />;
      default: return <AlertTriangle className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading safety data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Workforce Safety</h1>
        <p className="text-gray-600">AI-powered safety incident management and analysis</p>
      </div>

      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Incidents</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.total_incidents}</div>
              <p className="text-xs text-muted-foreground">All reported incidents</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Incident Rate</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.incident_rate}</div>
              <p className="text-xs text-muted-foreground">Per million hours worked</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Days Without Incident</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{metrics.days_without_incident}</div>
              <p className="text-xs text-muted-foreground">Current streak</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Risk Score</CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                {metrics.risk_assessment_score.toFixed(1)}/100
              </div>
              <p className="text-xs text-muted-foreground">Current risk level</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="incidents" className="space-y-6">
        <TabsList>
          <TabsTrigger value="incidents" data-testid="safety-incidents-tab">
            Incidents ({incidents.length})
          </TabsTrigger>
          <TabsTrigger value="analysis" data-testid="safety-analysis-tab">
            AI Analysis
          </TabsTrigger>
        </TabsList>

        <TabsContent value="incidents" className="space-y-4">
          <div className="grid gap-4">
            {incidents.map((incident) => (
              <Card key={incident.id} data-testid={`safety-incident-${incident.id}`}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getIncidentIcon(incident.incident_type)}
                      <span className="capitalize">{incident.incident_type.replace('_', ' ')}</span>
                    </div>
                    <div className="flex gap-2">
                      <Badge className={getSeverityColor(incident.severity)}>
                        {incident.severity}
                      </Badge>
                      <Badge variant={incident.status === 'closed' ? 'default' : 'outline'}>
                        {incident.status}
                      </Badge>
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {incident.location} • Reported by {incident.reported_by} • 
                    {new Date(incident.incident_date).toLocaleDateString()}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-sm mb-2">Description:</h4>
                      <p className="text-sm text-gray-700">{incident.description}</p>
                    </div>

                    {incident.personnel_involved.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Personnel Involved:</h4>
                        <div className="flex flex-wrap gap-2">
                          {incident.personnel_involved.map((person, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {person}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {incident.root_cause && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Root Cause:</h4>
                        <p className="text-sm text-gray-700">{incident.root_cause}</p>
                      </div>
                    )}

                    {incident.corrective_actions.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm mb-2">Corrective Actions:</h4>
                        <ul className="text-sm text-gray-700 space-y-1">
                          {incident.corrective_actions.map((action, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <CheckCircle className="h-3 w-3 text-green-500 mt-1 flex-shrink-0" />
                              {action}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Button 
                      onClick={() => analyzeIncident(incident.id)}
                      disabled={analyzing}
                      size="sm"
                      data-testid={`analyze-incident-${incident.id}`}
                    >
                      {analyzing ? 'Analyzing...' : 'AI Analysis'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analysis" className="space-y-4">
          {analysis ? (
            <Card data-testid="safety-analysis-result">
              <CardHeader>
                <CardTitle>AI Safety Analysis Results</CardTitle>
                <CardDescription>
                  Analysis completed on {new Date(analysis.analyzed_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-xl font-bold text-red-600">
                        {analysis.risk_assessment.probability_recurrence.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Recurrence Probability</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-xl font-bold text-orange-600">
                        {analysis.risk_assessment.potential_impact_score.toFixed(1)}/100
                      </div>
                      <div className="text-sm text-gray-600">Impact Score</div>
                    </div>
                    <div className="text-center p-4 bg-yellow-50 rounded-lg">
                      <div className="text-xl font-bold text-yellow-600">
                        {analysis.risk_assessment.recommended_priority.toUpperCase()}
                      </div>
                      <div className="text-sm text-gray-600">Priority Level</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">AI Analysis</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{analysis.ai_analysis}</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Prevention Recommendations</h4>
                    <div className="space-y-2">
                      {analysis.prevention_recommendations.map((rec, index) => (
                        <div key={index} className="flex items-start gap-2 p-3 bg-white border rounded-lg">
                          <Shield className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <HardHat className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Analysis Generated</h3>
                <p className="text-gray-600">Select an incident and click "AI Analysis" to generate detailed safety analysis</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Supply Chain Component  
const SupplyChain = () => {
  const [items, setItems] = useState([]);
  const [optimization, setOptimization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optimizing, setOptimizing] = useState(false);

  useEffect(() => {
    fetchSupplyChain();
  }, []);

  const fetchSupplyChain = async () => {
    try {
      const response = await axios.get(`${API}/supply-chain`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching supply chain:', error);
    } finally {
      setLoading(false);
    }
  };

  const optimizeSupplyChain = async () => {
    setOptimizing(true);
    try {
      const response = await axios.post(`${API}/supply-chain/optimize`);
      setOptimization(response.data);
    } catch (error) {
      console.error('Error optimizing supply chain:', error);
    } finally {
      setOptimizing(false);
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'equipment': return 'bg-blue-100 text-blue-800';
      case 'materials': return 'bg-green-100 text-green-800';
      case 'chemicals': return 'bg-purple-100 text-purple-800';
      case 'services': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStockStatus = (current, minimum) => {
    const ratio = current / minimum;
    if (ratio <= 1) return { color: 'text-red-600', status: 'Critical' };
    if (ratio <= 1.2) return { color: 'text-orange-600', status: 'Low' };
    if (ratio <= 1.5) return { color: 'text-yellow-600', status: 'Medium' };
    return { color: 'text-green-600', status: 'Good' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading supply chain...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Supply Chain Management</h1>
        <p className="text-gray-600">AI-powered inventory optimization and supplier management</p>
      </div>

      <div className="flex justify-between items-center">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{items.length}</div>
            <div className="text-sm text-gray-600">Total Items</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">
              {items.filter(item => item.current_stock <= item.minimum_stock).length}
            </div>
            <div className="text-sm text-gray-600">Critical Stock</div>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">
              {(items.reduce((sum, item) => sum + item.quality_rating, 0) / items.length || 0).toFixed(1)}%
            </div>
            <div className="text-sm text-gray-600">Avg Quality</div>
          </div>
          <div>
            <Button 
              onClick={optimizeSupplyChain}
              disabled={optimizing}
              className="w-full"
              data-testid="optimize-supply-chain"
            >
              {optimizing ? 'Optimizing...' : 'AI Optimize All'}
            </Button>
          </div>
        </div>
      </div>

      <Tabs defaultValue="inventory" className="space-y-6">
        <TabsList>
          <TabsTrigger value="inventory" data-testid="supply-inventory-tab">
            Inventory ({items.length})
          </TabsTrigger>
          <TabsTrigger value="optimization" data-testid="supply-optimization-tab">
            AI Optimization
          </TabsTrigger>
        </TabsList>

        <TabsContent value="inventory" className="space-y-4">
          <div className="grid gap-4">
            {items.map((item) => {
              const stockStatus = getStockStatus(item.current_stock, item.minimum_stock);
              return (
                <Card key={item.id} data-testid={`supply-item-${item.id}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Package className="h-5 w-5 text-blue-600" />
                        <span>{item.item_name}</span>
                      </div>
                      <div className="flex gap-2">
                        <Badge className={getCategoryColor(item.category)}>
                          {item.category}
                        </Badge>
                        <Badge variant="outline" className={stockStatus.color}>
                          {stockStatus.status}
                        </Badge>
                      </div>
                    </CardTitle>
                    <CardDescription>
                      Supplier: {item.supplier} • Quality: {item.quality_rating}/100 • 
                      Sustainability: {item.sustainability_score}/100
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                      <div>
                        <span className="font-medium">Current Stock:</span>
                        <div className={`font-bold ${stockStatus.color}`}>{item.current_stock}</div>
                      </div>
                      <div>
                        <span className="font-medium">Minimum:</span>
                        <div className="text-gray-600">{item.minimum_stock}</div>
                      </div>
                      <div>
                        <span className="font-medium">Unit Cost:</span>
                        <div className="text-gray-600">${item.unit_cost.toLocaleString()}</div>
                      </div>
                      <div>
                        <span className="font-medium">Lead Time:</span>
                        <div className="text-gray-600">{item.lead_time_days} days</div>
                      </div>
                      <div>
                        <span className="font-medium">Last Ordered:</span>
                        <div className="text-gray-600">
                          {item.last_ordered ? new Date(item.last_ordered).toLocaleDateString() : 'Never'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="optimization" className="space-y-4">
          {optimization ? (
            <Card data-testid="supply-chain-optimization">
              <CardHeader>
                <CardTitle>AI Supply Chain Optimization Results</CardTitle>
                <CardDescription>
                  Optimization completed on {new Date(optimization.optimized_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-xl font-bold text-blue-600">
                        {optimization.total_items_analyzed}
                      </div>
                      <div className="text-sm text-gray-600">Items Analyzed</div>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <div className="text-xl font-bold text-orange-600">
                        {optimization.items_requiring_attention}
                      </div>
                      <div className="text-sm text-gray-600">Need Attention</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-xl font-bold text-green-600">
                        {optimization.potential_cost_savings.toFixed(1)}%
                      </div>
                      <div className="text-sm text-gray-600">Cost Savings</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-xl font-bold text-purple-600">
                        ${(optimization.potential_cost_savings * 10000).toLocaleString()}
                      </div>
                      <div className="text-sm text-gray-600">Est. Savings ($)</div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3">Optimization Recommendations</h4>
                    <div className="space-y-4">
                      {optimization.optimization_results.map((opt, index) => (
                        <Card key={index}>
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base">{opt.item_name}</CardTitle>
                            <CardDescription>
                              Current: {opt.current_stock} • Predicted demand: {opt.predicted_demand}
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                              <div>
                                <span className="font-medium">Recommended Order:</span>
                                <div className="text-blue-600 font-bold">{opt.recommended_order_quantity}</div>
                              </div>
                              <div>
                                <span className="font-medium">Optimal Date:</span>
                                <div className="text-gray-600">{new Date(opt.optimal_order_date).toLocaleDateString()}</div>
                              </div>
                              <div>
                                <span className="font-medium">Savings:</span>
                                <div className="text-green-600 font-bold">{opt.cost_savings_potential.toFixed(1)}%</div>
                              </div>
                              <div>
                                <span className="font-medium">Risk:</span>
                                <div className={`font-bold ${
                                  opt.risk_level === 'high' ? 'text-red-600' : 
                                  opt.risk_level === 'medium' ? 'text-yellow-600' : 'text-green-600'
                                }`}>
                                  {opt.risk_level}
                                </div>
                              </div>
                            </div>
                            {opt.ai_recommendation && (
                              <div className="bg-gray-50 p-3 rounded text-sm">
                                <strong>AI Recommendation:</strong> {opt.ai_recommendation}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Truck className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Optimization Run</h3>
                <p className="text-gray-600 mb-4">
                  Click "AI Optimize All" to generate supply chain optimization recommendations
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Advanced Analytics Component
const AdvancedAnalytics = () => {
  const [reports, setReports] = useState([]);
  const [currentReport, setCurrentReport] = useState(null);
  const [generating, setGenerating] = useState(false);

  const generateReport = async (reportType) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/analytics/generate-report`, {
        report_type: reportType,
        date_range: 30,
        generated_by: 'Executive User'
      });
      setCurrentReport(response.data);
      setReports(prev => [response.data, ...prev]);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Advanced Analytics</h1>
        <p className="text-gray-600">AI-powered comprehensive reporting and insights</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Button 
          onClick={() => generateReport('operational')}
          disabled={generating}
          className="h-20"
          data-testid="generate-operational-report"
        >
          <div className="text-center">
            <Settings className="h-6 w-6 mx-auto mb-1" />
            <div className="text-sm">Operational Report</div>
          </div>
        </Button>
        <Button 
          onClick={() => generateReport('financial')}
          disabled={generating}
          className="h-20"
          data-testid="generate-financial-report"
        >
          <div className="text-center">
            <DollarSign className="h-6 w-6 mx-auto mb-1" />
            <div className="text-sm">Financial Report</div>
          </div>
        </Button>
        <Button 
          onClick={() => generateReport('safety')}
          disabled={generating}
          className="h-20"
          data-testid="generate-safety-report"
        >
          <div className="text-center">
            <Shield className="h-6 w-6 mx-auto mb-1" />
            <div className="text-sm">Safety Report</div>
          </div>
        </Button>
        <Button 
          onClick={() => generateReport('executive')}
          disabled={generating}
          className="h-20"
          data-testid="generate-executive-report"
        >
          <div className="text-center">
            <Users className="h-6 w-6 mx-auto mb-1" />
            <div className="text-sm">Executive Report</div>
          </div>
        </Button>
      </div>

      {generating && (
        <Card>
          <CardContent className="text-center py-12">
            <div className="animate-spin h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold mb-2">Generating AI Report...</h3>
            <p className="text-gray-600">Analyzing data across all modules and generating insights</p>
          </CardContent>
        </Card>
      )}

      {currentReport && (
        <Card data-testid="analytics-report">
          <CardHeader>
            <CardTitle>{currentReport.report_name}</CardTitle>
            <CardDescription>
              Generated on {new Date(currentReport.generated_at).toLocaleString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                  <div className="text-lg font-bold text-blue-600">
                    {currentReport.kpi_summary.operational_efficiency.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600">Efficiency</div>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <div className="text-lg font-bold text-green-600">
                    {currentReport.kpi_summary.safety_score.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600">Safety Score</div>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-lg">
                  <div className="text-lg font-bold text-purple-600">
                    {currentReport.kpi_summary.compliance_rate.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600">Compliance</div>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <div className="text-lg font-bold text-orange-600">
                    {currentReport.kpi_summary.asset_utilization.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600">Asset Utilization</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <div className="text-lg font-bold text-yellow-600">
                    {currentReport.kpi_summary.cost_optimization_potential.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-600">Cost Optimization</div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">AI Analysis & Insights</h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-700 whitespace-pre-wrap">{currentReport.ai_analysis}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-3">Performance Trends</h4>
                  <div className="space-y-2">
                    {Object.entries(currentReport.trends).map(([key, trend]) => (
                      <div key={key} className="flex items-center justify-between p-2 bg-white border rounded">
                        <span className="text-sm capitalize">{key.replace('_', ' ')}</span>
                        <Badge variant="outline" className={
                          trend === 'increasing' ? 'text-green-600' :
                          trend === 'decreasing' ? 'text-red-600' : 'text-blue-600'
                        }>
                          {trend}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-3">Strategic Recommendations</h4>
                  <div className="space-y-2">
                    {currentReport.recommendations.map((rec, index) => (
                      <div key={index} className="flex items-start gap-2 p-2 bg-white border rounded">
                        <Target className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-sm">{rec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Documentation Component
const Documentation = () => {
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', label: 'Value Proposition', icon: Target },
    { id: 'customers', label: 'Target Customers', icon: Users },
    { id: 'sap-value', label: 'SAP Integration Value', icon: Plug },
    { id: 'ai-integrations', label: 'AI/ML Integrations', icon: Bot },
    { id: 'oil-gas-usecases', label: 'Oil & Gas Use Cases', icon: Factory },
    { id: 'patentable-methods', label: 'Patentable Methods', icon: FileCheck },
    { id: 'user-journeys', label: 'User Journeys', icon: MapPin },
    { id: 'pricing', label: 'Pricing Strategy', icon: DollarSign },
    { id: 'architecture', label: 'Technical Architecture', icon: Code },
    { id: 'product-guide', label: 'Product Guide', icon: FileText },
    { id: 'user-guide', label: 'User Guide', icon: TestTube }
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">PetraMind: Unique Value Proposition</h2>
              <div className="bg-blue-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">AI-First Oil & Gas Operations Platform</h3>
                <p className="text-blue-800">
                  PetraMind delivers industry-first AI-powered insights with 90% predictive accuracy, 
                  50% reduction in manual tasks, and 15% OPEX reduction through intelligent automation.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-blue-600" />
                    Competitive Advantages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">vs. Traditional Oil & Gas Software</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• AI-native architecture vs. bolt-on analytics</li>
                        <li>• Real-time predictive insights vs. historical reporting</li>
                        <li>• Unified platform vs. siloed applications</li>
                        <li>• 90% predictive accuracy vs. 60-70% industry standard</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">vs. Generic AI Platforms</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Oil & Gas domain expertise built-in</li>
                        <li>• Pre-configured industry workflows</li>
                        <li>• Regulatory compliance templates</li>
                        <li>• SAP-native integration capabilities</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-green-600" />
                    Measurable Business Impact
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Predictive Accuracy</span>
                      <span className="text-lg font-bold text-green-600">90%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Manual Task Reduction</span>
                      <span className="text-lg font-bold text-blue-600">50%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">ESG Improvement</span>
                      <span className="text-lg font-bold text-purple-600">25%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">OPEX Reduction</span>
                      <span className="text-lg font-bold text-orange-600">15%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Core Differentiators</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center">
                    <Bot className="h-8 w-8 mx-auto text-blue-600 mb-3" />
                    <h4 className="font-semibold mb-2">AI-First Architecture</h4>
                    <p className="text-sm text-gray-600">Built from ground up with AI at the core, not as an afterthought</p>
                  </div>
                  <div className="text-center">
                    <Globe className="h-8 w-8 mx-auto text-green-600 mb-3" />
                    <h4 className="font-semibold mb-2">Industry Expertise</h4>
                    <p className="text-sm text-gray-600">Deep Oil & Gas domain knowledge embedded in every module</p>
                  </div>
                  <div className="text-center">
                    <Plug className="h-8 w-8 mx-auto text-purple-600 mb-3" />
                    <h4 className="font-semibold mb-2">SAP Native</h4>
                    <p className="text-sm text-gray-600">Seamless integration with existing SAP investments</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'customers':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Target Customer Segments</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Factory className="h-5 w-5 text-blue-600" />
                    Primary Target: Large Oil & Gas Companies
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">Company Profile</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Revenue: $5B+ annually</li>
                        <li>• Operations: Multi-site, global presence</li>
                        <li>• Assets: 500+ critical equipment units</li>
                        <li>• SAP Usage: Existing SAP S/4HANA deployment</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Key Decision Makers</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Chief Digital Officer (CDO)</li>
                        <li>• VP of Operations</li>
                        <li>• Chief Technology Officer (CTO)</li>
                        <li>• SAP Center of Excellence Lead</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-green-600" />
                    Secondary: Mid-Market Companies
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">Company Profile</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Revenue: $1B-$5B annually</li>
                        <li>• Operations: Regional presence</li>
                        <li>• Assets: 100-500 equipment units</li>
                        <li>• SAP Usage: SAP ECC or S/4HANA</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Key Decision Makers</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Operations Director</li>
                        <li>• IT Director</li>
                        <li>• Plant Managers</li>
                        <li>• ERP/SAP Administrators</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Industry Segments & Use Cases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">Upstream</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                      <li>• Drilling optimization</li>
                      <li>• Production forecasting</li>
                      <li>• Well integrity monitoring</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-900 mb-2">Midstream</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Pipeline monitoring</li>
                      <li>• Transportation optimization</li>
                      <li>• Storage management</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-semibold text-purple-900 mb-2">Downstream</h4>
                    <ul className="text-sm text-purple-700 space-y-1">
                      <li>• Refinery optimization</li>
                      <li>• Quality management</li>
                      <li>• Yield forecasting</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h4 className="font-semibold text-orange-900 mb-2">Integrated</h4>
                    <ul className="text-sm text-orange-700 space-y-1">
                      <li>• End-to-end visibility</li>
                      <li>• Cross-segment optimization</li>
                      <li>• Enterprise analytics</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'sap-value':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">SAP Integration Value Proposition</h2>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Maximize Your SAP Investment with AI</h3>
                <p className="text-gray-700">
                  PetraMind transforms your existing SAP data into actionable AI insights, 
                  delivering 300% ROI improvement on your SAP investment without replacing existing systems.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="h-5 w-5 text-blue-600" />
                    Data Integration Benefits
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">Real-time SAP Data Access</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Direct integration with SAP S/4HANA APIs</li>
                        <li>• Real-time data synchronization</li>
                        <li>• No data duplication or ETL overhead</li>
                        <li>• Preserve SAP data governance models</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Enhanced Data Value</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• Transform transactional data into insights</li>
                        <li>• Predictive analytics on SAP master data</li>
                        <li>• Cross-module data correlation</li>
                        <li>• Historical trend analysis</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Workflow className="h-5 w-5 text-green-600" />
                    Process Enhancement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900">Intelligent Process Automation</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• AI-driven workflow optimization</li>
                        <li>• Predictive maintenance scheduling in SAP PM</li>
                        <li>• Automated compliance reporting</li>
                        <li>• Smart procurement recommendations</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">Decision Support</h4>
                      <ul className="text-sm text-gray-600 mt-2 space-y-1">
                        <li>• AI insights in SAP Analytics Cloud</li>
                        <li>• Predictive forecasting for SAP IBP</li>
                        <li>• Risk assessment for SAP GRC</li>
                        <li>• Performance optimization suggestions</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>SAP Module Integration Matrix</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2">SAP Module</th>
                        <th className="text-left p-2">PetraMind Integration</th>
                        <th className="text-left p-2">AI Enhancement</th>
                        <th className="text-left p-2">Business Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-2 font-medium">SAP PM (Plant Maintenance)</td>
                        <td className="p-2">Real-time equipment data sync</td>
                        <td className="p-2">Predictive failure analysis</td>
                        <td className="p-2">40% reduction in unplanned downtime</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">SAP MM (Materials Management)</td>
                        <td className="p-2">Inventory optimization</td>
                        <td className="p-2">Demand forecasting</td>
                        <td className="p-2">25% inventory cost reduction</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">SAP FI/CO (Finance)</td>
                        <td className="p-2">Cost center analytics</td>
                        <td className="p-2">Profitability prediction</td>
                        <td className="p-2">15% OPEX optimization</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-2 font-medium">SAP QM (Quality Management)</td>
                        <td className="p-2">Quality data analysis</td>
                        <td className="p-2">Defect prediction</td>
                        <td className="p-2">30% quality improvement</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'ai-integrations':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Advanced AI/ML Integrations</h2>
              <div className="bg-gradient-to-r from-orange-50 to-blue-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Enterprise-Grade AI Capabilities</h3>
                <p className="text-gray-700">
                  PetraMind integrates with leading AI platforms to deliver cutting-edge machine learning, 
                  natural language processing, and automated deployment capabilities for Oil & Gas operations.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cloud className="h-5 w-5 text-orange-600" />
                    AWS Bedrock
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Foundation Models</h4>
                      <div className="space-y-2">
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className="font-medium text-sm">Claude 3 Sonnet</div>
                          <div className="text-xs text-gray-600">Advanced reasoning for complex drilling analysis, seismic interpretation, and maintenance prediction</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-medium text-sm">Amazon Titan</div>
                          <div className="text-xs text-gray-600">Cost-effective text generation for operational summaries and reports</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Oil & Gas Applications</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Drilling parameter analysis (ROP, WOB, RPM)</li>
                        <li>• Seismic data interpretation</li>
                        <li>• Equipment health assessment</li>
                        <li>• Production optimization recommendations</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Key Features</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Domain-specific system prompts</li>
                        <li>• Configurable temperature & token limits</li>
                        <li>• Cost optimization strategies</li>
                        <li>• Real-time inference with retry logic</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Bot className="h-5 w-5 text-blue-600" />
                    SAP Joule AI Copilot
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Interaction Modes</h4>
                      <div className="space-y-2">
                        <div className="p-2 bg-blue-50 rounded-lg">
                          <div className="font-medium text-xs">Transactional</div>
                          <div className="text-xs text-gray-600">Execute CRUD operations via natural language</div>
                        </div>
                        <div className="p-2 bg-green-50 rounded-lg">
                          <div className="font-medium text-xs">Navigational</div>
                          <div className="text-xs text-gray-600">Find SAP features and applications</div>
                        </div>
                        <div className="p-2 bg-purple-50 rounded-lg">
                          <div className="font-medium text-xs">Informational</div>
                          <div className="text-xs text-gray-600">Answer business terminology questions</div>
                        </div>
                        <div className="p-2 bg-orange-50 rounded-lg">
                          <div className="font-medium text-xs">Analytical</div>
                          <div className="text-xs text-gray-600">Generate insights and recommendations</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">SAP Module Coverage</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• S/4HANA (MM, PP, SD, FI, CO, PM)</li>
                        <li>• IBP (Demand & Supply Planning)</li>
                        <li>• BTP (Integration & Analytics)</li>
                        <li>• SuccessFactors, Ariba, Datasphere</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Authentication</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• OAuth2 secure authentication</li>
                        <li>• BTP Formation ID management</li>
                        <li>• Role-based access control</li>
                        <li>• Identity Authentication integration</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Server className="h-5 w-5 text-indigo-600" />
                    SAP BTP Deployment
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Cloud Foundry Runtime</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center p-2 bg-indigo-50 rounded">
                          <span className="text-xs font-medium">Python Version</span>
                          <span className="text-xs text-gray-600">3.11.7</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-blue-50 rounded">
                          <span className="text-xs font-medium">Memory</span>
                          <span className="text-xs text-gray-600">512 MB</span>
                        </div>
                        <div className="flex justify-between items-center p-2 bg-green-50 rounded">
                          <span className="text-xs font-medium">Build Pack</span>
                          <span className="text-xs text-gray-600">python_buildpack</span>
                        </div>
                      </div>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Deployment Features</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• Automated manifest.yaml configuration</li>
                        <li>• Environment variable management</li>
                        <li>• Health check monitoring</li>
                        <li>• Multi-space deployment (dev/staging/prod)</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Integration Capabilities</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>• MongoDB Atlas connectivity</li>
                        <li>• FastAPI application deployment</li>
                        <li>• Service binding for credentials</li>
                        <li>• Auto-scaling & load balancing</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Integration Architecture</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="font-semibold text-sm mb-2">Data Layer</div>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>→ SAP S/4HANA (source data)</li>
                        <li>→ MongoDB (operational data)</li>
                        <li>→ Real-time synchronization</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="font-semibold text-sm mb-2">AI Processing Layer</div>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>→ AWS Bedrock models</li>
                        <li>→ SAP Joule copilot</li>
                        <li>→ Custom ML pipelines</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="font-semibold text-sm mb-2">Application Layer</div>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>→ React frontend</li>
                        <li>→ FastAPI backend</li>
                        <li>→ BTP Cloud Foundry runtime</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'oil-gas-usecases':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Oil & Gas Operations: AI-Powered Use Cases</h2>
              <div className="bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Transform Operations with Intelligent Automation</h3>
                <p className="text-gray-700">
                  Discover how PetraMind leverages AI to optimize, enhance, and automate critical Oil & Gas operations, 
                  delivering measurable improvements in safety, efficiency, cost reduction, and environmental compliance.
                </p>
              </div>
            </div>

            {/* Use Case 1: Predictive Maintenance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-6 w-6 text-blue-600" />
                  Use Case 1: Predictive Maintenance & Equipment Health Monitoring
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Offshore platforms and refining facilities operate thousands of critical assets (pumps, compressors, 
                        turbines, heat exchangers). Unplanned downtime costs $42M-$88M per day for a major platform. 
                        Traditional preventive maintenance is inefficient, leading to unnecessary interventions or 
                        catastrophic failures.
                      </p>
                      <h4 className="font-semibold text-gray-900 mb-2">How PetraMind Optimizes</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li><strong>• Real-time Sensor Analysis:</strong> Monitors temperature, vibration, pressure, flow rates across 10,000+ sensors</li>
                        <li><strong>• AI Anomaly Detection:</strong> AWS Bedrock Claude identifies subtle patterns indicating degradation 7-14 days before failure</li>
                        <li><strong>• Predictive Algorithms:</strong> Machine learning models predict remaining useful life (RUL) with 90% accuracy</li>
                        <li><strong>• SAP PM Integration:</strong> Automatically generates maintenance work orders in SAP with prioritization</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Measurable Business Impact</h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="font-bold text-2xl text-green-600">40%</div>
                          <div className="text-sm text-gray-700">Reduction in unplanned downtime</div>
                          <div className="text-xs text-gray-600">Early failure detection prevents catastrophic shutdowns</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-bold text-2xl text-blue-600">$15M</div>
                          <div className="text-sm text-gray-700">Annual maintenance cost savings</div>
                          <div className="text-xs text-gray-600">Eliminate unnecessary preventive maintenance</div>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <div className="font-bold text-2xl text-purple-600">25%</div>
                          <div className="text-sm text-gray-700">Extension of asset lifespan</div>
                          <div className="text-xs text-gray-600">Optimal maintenance timing reduces wear and tear</div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-900 mt-3 mb-2">AI Automation Features</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>✓ Automated root cause analysis using Claude 3</li>
                        <li>✓ Spare parts demand forecasting</li>
                        <li>✓ Maintenance crew scheduling optimization</li>
                        <li>✓ Historical failure pattern learning</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Use Case 2: Drilling Optimization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-orange-600" />
                  Use Case 2: Drilling Operations Optimization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Drilling costs $500K-$2M per day. Suboptimal parameters (Weight on Bit, Rate of Penetration, RPM) 
                        reduce efficiency by 15-30%. Stuck pipe incidents cost $100M+ annually industry-wide. 
                        Real-time optimization requires expertise that's not always available 24/7.
                      </p>
                      <h4 className="font-semibold text-gray-900 mb-2">How PetraMind Optimizes</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li><strong>• Real-time Parameter Analysis:</strong> Continuous monitoring of WOB, ROP, RPM, torque, ECD (Equivalent Circulating Density)</li>
                        <li><strong>• AI-Powered Recommendations:</strong> Claude 3 analyzes formation characteristics and suggests optimal drilling parameters</li>
                        <li><strong>• Stuck Pipe Prediction:</strong> Machine learning identifies high-risk conditions 2-4 hours in advance</li>
                        <li><strong>• Automated Reporting:</strong> Daily drilling reports generated with AI insights sent to engineers</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Measurable Business Impact</h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className="font-bold text-2xl text-orange-600">20%</div>
                          <div className="text-sm text-gray-700">Improvement in Rate of Penetration (ROP)</div>
                          <div className="text-xs text-gray-600">Faster drilling saves $100K-$400K per well</div>
                        </div>
                        <div className="p-3 bg-red-50 rounded-lg">
                          <div className="font-bold text-2xl text-red-600">75%</div>
                          <div className="text-sm text-gray-700">Reduction in stuck pipe incidents</div>
                          <div className="text-xs text-gray-600">Avoiding costly non-productive time</div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="font-bold text-2xl text-green-600">12%</div>
                          <div className="text-sm text-gray-700">Reduction in overall drilling costs</div>
                          <div className="text-xs text-gray-600">$600K-$2.4M savings per well</div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-900 mt-3 mb-2">AI Enhancement Features</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>✓ Formation pressure prediction</li>
                        <li>✓ Bit selection optimization</li>
                        <li>✓ Mud program recommendations</li>
                        <li>✓ 24/7 AI drilling advisor (via SAP Joule)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Use Case 3: Production Optimization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-6 w-6 text-green-600" />
                  Use Case 3: Production Rate Optimization & Well Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Wells decline at 5-15% annually. Suboptimal production settings leave 10-20% potential output 
                        unrealized ($50M-$200M annual revenue loss for major fields). Manual well testing occurs 
                        quarterly, missing optimization opportunities. Reservoir pressure management requires continuous adjustment.
                      </p>
                      <h4 className="font-semibold text-gray-900 mb-2">How PetraMind Optimizes</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li><strong>• Well Performance Analytics:</strong> Real-time analysis of production data (flow rates, pressure, water cut, GOR)</li>
                        <li><strong>• AI Production Forecasting:</strong> Predicts production trends 30-90 days ahead with 85% accuracy</li>
                        <li><strong>• Choke Optimization:</strong> AI recommends optimal choke settings to maximize production without reservoir damage</li>
                        <li><strong>• Decline Curve Analysis:</strong> Machine learning identifies underperforming wells requiring intervention</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Measurable Business Impact</h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="font-bold text-2xl text-green-600">8-12%</div>
                          <div className="text-sm text-gray-700">Production rate improvement</div>
                          <div className="text-xs text-gray-600">$40M-$120M additional annual revenue</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-bold text-2xl text-blue-600">30%</div>
                          <div className="text-sm text-gray-700">Faster identification of production issues</div>
                          <div className="text-xs text-gray-600">Reduces lost production from 30 days to 7 days</div>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <div className="font-bold text-2xl text-purple-600">15%</div>
                          <div className="text-sm text-gray-700">Reduction in artificial lift costs</div>
                          <div className="text-xs text-gray-600">Optimized pump settings save energy & maintenance</div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-900 mt-3 mb-2">AI Automation Features</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>✓ Automated production allocation across wells</li>
                        <li>✓ Water breakthrough early warning</li>
                        <li>✓ Gas lift optimization algorithms</li>
                        <li>✓ Reservoir pressure maintenance strategies</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Use Case 4: Supply Chain & Inventory Optimization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-6 w-6 text-purple-600" />
                  Use Case 4: Supply Chain & Inventory Optimization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Offshore platforms maintain $50M-$200M inventory to avoid stockouts. Over-stocking ties up working 
                        capital and incurs storage costs. Critical parts stockouts cause $500K-$5M downtime per incident. 
                        Demand forecasting relies on historical averages, missing seasonal and operational patterns.
                      </p>
                      <h4 className="font-semibold text-gray-900 mb-2">How PetraMind Optimizes</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li><strong>• AI Demand Forecasting:</strong> Predicts spare parts requirements based on equipment health and production schedules</li>
                        <li><strong>• SAP MM Integration:</strong> Automated purchase requisitions triggered by predicted demand</li>
                        <li><strong>• Supplier Performance Analytics:</strong> Machine learning optimizes vendor selection based on delivery, quality, cost</li>
                        <li><strong>• Logistics Optimization:</strong> AI routes materials via optimal transportation modes (helicopter, boat, pipeline)</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Measurable Business Impact</h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <div className="font-bold text-2xl text-purple-600">25%</div>
                          <div className="text-sm text-gray-700">Inventory holding cost reduction</div>
                          <div className="text-xs text-gray-600">$12M-$50M working capital freed up</div>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className="font-bold text-2xl text-orange-600">60%</div>
                          <div className="text-sm text-gray-700">Reduction in stockout incidents</div>
                          <div className="text-xs text-gray-600">Critical parts available when needed</div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="font-bold text-2xl text-green-600">18%</div>
                          <div className="text-sm text-gray-700">Procurement cost savings</div>
                          <div className="text-xs text-gray-600">Vendor optimization + volume consolidation</div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-900 mt-3 mb-2">AI Enhancement Features</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>✓ Automated reorder point calculation</li>
                        <li>✓ Multi-location inventory balancing</li>
                        <li>✓ Supplier risk assessment (SAP Ariba integration)</li>
                        <li>✓ Logistics cost optimization (IBP integration)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Use Case 5: ESG & Emissions Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="h-6 w-6 text-green-600" />
                  Use Case 5: ESG Compliance & Carbon Emissions Reduction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Oil & Gas companies face increasing regulatory pressure (Paris Agreement, EU taxonomy, CDP reporting). 
                        Carbon taxes cost $15-$50 per ton CO2. Manual emissions tracking is inaccurate (±20% error). 
                        Scope 1 & 2 emissions reduction targets of 30-50% by 2030 require intelligent monitoring and optimization.
                      </p>
                      <h4 className="font-semibold text-gray-900 mb-2">How PetraMind Optimizes</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li><strong>• Real-time Emissions Monitoring:</strong> Automated tracking of Scope 1, 2, 3 emissions across operations</li>
                        <li><strong>• AI Carbon Forecasting:</strong> Predicts emissions 90 days ahead, enabling proactive mitigation</li>
                        <li><strong>• Flare Optimization:</strong> Machine learning minimizes flaring while maintaining safety (30% reduction)</li>
                        <li><strong>• Regulatory Compliance:</strong> Automated reporting for EPA, EU ETS, CDP, TCFD, GRI standards</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Measurable Business Impact</h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="font-bold text-2xl text-green-600">20-25%</div>
                          <div className="text-sm text-gray-700">Reduction in carbon emissions</div>
                          <div className="text-xs text-gray-600">$5M-$25M in carbon tax savings annually</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-bold text-2xl text-blue-600">85%</div>
                          <div className="text-sm text-gray-700">Reduction in ESG reporting time</div>
                          <div className="text-xs text-gray-600">From 3 months to 2 weeks for annual reports</div>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <div className="font-bold text-2xl text-purple-600">+15pts</div>
                          <div className="text-sm text-gray-700">ESG score improvement (CDP, MSCI)</div>
                          <div className="text-xs text-gray-600">Enhanced investor confidence & valuation</div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-900 mt-3 mb-2">AI Automation Features</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>✓ Methane leak detection (satellite + sensor fusion)</li>
                        <li>✓ Energy consumption optimization</li>
                        <li>✓ Renewable energy integration recommendations</li>
                        <li>✓ Automated carbon offset portfolio management</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Use Case 6: Safety & Risk Management */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-red-600" />
                  Use Case 6: Safety Incident Prevention & Risk Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Safety incidents cost the industry $5B+ annually. Near-miss events go unreported or unanalyzed. 
                        Process safety management (PSM) relies on lagging indicators. Regulatory violations result in 
                        $10M-$100M+ fines. Human error causes 70% of incidents, often preventable with better situational awareness.
                      </p>
                      <h4 className="font-semibold text-gray-900 mb-2">How PetraMind Optimizes</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li><strong>• Predictive Safety Analytics:</strong> AI identifies high-risk conditions 24-48 hours before potential incidents</li>
                        <li><strong>• Real-time Hazard Detection:</strong> Computer vision monitors PPE compliance, hazardous gas levels, pressure anomalies</li>
                        <li><strong>• Incident Pattern Analysis:</strong> Machine learning identifies root causes across historical incident data</li>
                        <li><strong>• Permit-to-Work Optimization:</strong> AI validates permit completeness and identifies conflicting work activities</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Measurable Business Impact</h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-red-50 rounded-lg">
                          <div className="font-bold text-2xl text-red-600">45%</div>
                          <div className="text-sm text-gray-700">Reduction in safety incidents</div>
                          <div className="text-xs text-gray-600">From 12 incidents/year to 6-7 incidents</div>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg">
                          <div className="font-bold text-2xl text-orange-600">$8M</div>
                          <div className="text-sm text-gray-700">Avoided incident costs annually</div>
                          <div className="text-xs text-gray-600">Prevention saves investigation, fines, downtime</div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="font-bold text-2xl text-green-600">Zero</div>
                          <div className="text-sm text-gray-700">Target: Zero fatalities & serious injuries</div>
                          <div className="text-xs text-gray-600">AI-driven proactive safety culture</div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-900 mt-3 mb-2">AI Enhancement Features</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>✓ H2S / benzene exposure prediction</li>
                        <li>✓ Fatigue risk assessment (shift patterns)</li>
                        <li>✓ Emergency response simulation & optimization</li>
                        <li>✓ Behavioral safety analytics (near-miss reporting)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Use Case 7: Digital Twin Technology */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="h-6 w-6 text-indigo-600" />
                  Use Case 7: Digital Twin Technology for Predictive Operations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">The Challenge</h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Physical assets operate in complex, dynamic environments making it difficult to predict behavior 
                        and optimize performance. Traditional monitoring provides historical data but limited predictive capabilities. 
                        Testing optimization strategies on live equipment risks production disruption and safety. Physical prototyping 
                        and testing costs $500K-$5M per scenario. Lack of integrated view across equipment and facility levels.
                      </p>
                      <h4 className="font-semibold text-gray-900 mb-2">How PetraMind Optimizes</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li><strong>• Virtual Equipment Replicas:</strong> 3D physics-based models of 127 critical assets (pumps, compressors, turbines) with real-time sensor overlay</li>
                        <li><strong>• Facility-Level Twins:</strong> Complete digital replicas of 7 facilities (offshore platforms, refineries, pipelines) with process flow simulation</li>
                        <li><strong>• Real-time Data Sync:</strong> 10,847 sensors feeding 3,245 data points per second to digital twins</li>
                        <li><strong>• What-If Simulation:</strong> Test operational changes, maintenance scenarios, emergency responses without physical risk</li>
                        <li><strong>• AI Optimization Engine:</strong> Machine learning recommends optimal parameters for production, energy, emissions</li>
                        <li><strong>• Degradation Modeling:</strong> Physics-based prediction of wear patterns, corrosion, fatigue with 89.8% accuracy</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Measurable Business Impact</h4>
                      <div className="space-y-3">
                        <div className="p-3 bg-indigo-50 rounded-lg">
                          <div className="font-bold text-2xl text-indigo-600">60%</div>
                          <div className="text-sm text-gray-700">Reduction in unplanned downtime</div>
                          <div className="text-xs text-gray-600">Early warning 18 days average before failure</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="font-bold text-2xl text-blue-600">$20M</div>
                          <div className="text-sm text-gray-700">Annual operational savings</div>
                          <div className="text-xs text-gray-600">Prevented downtime (1,240 hrs YTD) + optimization gains</div>
                        </div>
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="font-bold text-2xl text-green-600">30%</div>
                          <div className="text-sm text-gray-700">Asset lifespan extension</div>
                          <div className="text-xs text-gray-600">Optimal operating conditions reduce wear and tear</div>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <div className="font-bold text-2xl text-purple-600">12.4%</div>
                          <div className="text-sm text-gray-700">Production rate optimization</div>
                          <div className="text-xs text-gray-600">AI-identified improvement opportunities</div>
                        </div>
                      </div>
                      <h4 className="font-semibold text-gray-900 mt-3 mb-2">AI Automation Features</h4>
                      <ul className="text-xs text-gray-600 space-y-1">
                        <li>✓ Automated failure mode prediction (72% probability for bearing failures detected 14 days early)</li>
                        <li>✓ Continuous optimization recommendations (separator pressure, compression ratio, flare reduction)</li>
                        <li>✓ Real-time health degradation tracking with visual trend analysis</li>
                        <li>✓ Automated SAP PM work order generation from digital twin predictions</li>
                        <li>✓ What-if scenario simulation (test parameter changes without production disruption)</li>
                        <li>✓ 3D visualization with WebGL rendering (equipment, facilities, process flows)</li>
                        <li>✓ VR/AR ready for immersive training and remote operations</li>
                      </ul>
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <h5 className="font-semibold text-indigo-900 mb-2">Digital Twin Technology Stack</h5>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="font-medium text-indigo-800 mb-1">Data Layer</div>
                        <ul className="text-xs text-indigo-700 space-y-1">
                          <li>→ 10,847 IoT sensors</li>
                          <li>→ SCADA/Historian integration</li>
                          <li>→ Real-time data pipelines (3,245 DPS)</li>
                        </ul>
                      </div>
                      <div>
                        <div className="font-medium text-indigo-800 mb-1">AI/Simulation Layer</div>
                        <ul className="text-xs text-indigo-700 space-y-1">
                          <li>→ Physics-based degradation models</li>
                          <li>→ AWS Bedrock predictive analytics</li>
                          <li>→ 94.5% simulation accuracy</li>
                        </ul>
                      </div>
                      <div>
                        <div className="font-medium text-indigo-800 mb-1">Visualization Layer</div>
                        <ul className="text-xs text-indigo-700 space-y-1">
                          <li>→ Three.js 3D rendering engine</li>
                          <li>→ 127 equipment + 7 facility models</li>
                          <li>→ Real-time sensor data overlay</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Summary Card */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                  Cumulative Business Impact Across All Use Cases
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-3xl font-bold text-green-600">$170M+</div>
                    <div className="text-sm text-gray-700 mt-1">Total Annual Value Creation</div>
                    <div className="text-xs text-gray-600">Including $20M from Digital Twin</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-3xl font-bold text-blue-600">15-20%</div>
                    <div className="text-sm text-gray-700 mt-1">OPEX Reduction</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-3xl font-bold text-purple-600">12-15%</div>
                    <div className="text-sm text-gray-700 mt-1">Production Increase</div>
                    <div className="text-xs text-gray-600">Enhanced with Digital Twin optimization</div>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <div className="text-3xl font-bold text-orange-600">60%</div>
                    <div className="text-sm text-gray-700 mt-1">Downtime Reduction</div>
                    <div className="text-xs text-gray-600">Digital Twin predictive capabilities</div>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Cross-Functional AI Automation Benefits</h4>
                  <ul className="text-sm text-gray-600 space-y-1">
                    <li>✓ <strong>50% reduction in manual data entry & reporting:</strong> AI automates data collection, analysis, and report generation across all operations</li>
                    <li>✓ <strong>24/7 intelligent operations:</strong> SAP Joule copilot provides expert-level guidance to operators around the clock</li>
                    <li>✓ <strong>Unified data visibility:</strong> Single platform integrates data from drilling, production, maintenance, supply chain, ESG</li>
                    <li>✓ <strong>Digital Twin predictive power:</strong> 25 digital twins monitoring 10,847 sensors provide 18-day early warning with 89.8% accuracy</li>
                    <li>✓ <strong>Scalable architecture:</strong> SAP BTP deployment enables elastic scaling as operations expand (new wells, platforms, refineries)</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'patentable-methods':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Patentable Methods & Innovations</h2>
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Novel AI-Powered Methodologies for Oil & Gas Digital Transformation</h3>
                <p className="text-gray-700">
                  PetraMind incorporates multiple innovative methods and systems that represent significant technological 
                  advances in AI-powered industrial operations, predictive maintenance, and digital twin technology for the 
                  Oil & Gas sector. These methods demonstrate novelty, non-obviousness, and practical industrial applicability.
                </p>
              </div>
            </div>

            {/* Patent 1: Hierarchical Digital Twin */}
            <Card className="border-2 border-purple-300">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-6 w-6 text-purple-600" />
                  Patent Method 1: Hierarchical Digital Twin Architecture with Real-Time Predictive Analytics
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Innovation Description</h4>
                    <p className="text-sm text-gray-700">
                      A novel hierarchical digital twin system that creates virtual replicas at both equipment-level and 
                      facility-level, with bidirectional real-time data synchronization from 10,000+ IoT sensors. The system 
                      employs physics-based degradation modeling combined with machine learning to predict equipment failures 
                      with 89.8% accuracy and 18-day average early warning. Unique integration with SAP PM automatically 
                      generates predictive maintenance work orders based on digital twin predictions.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Novel Technical Elements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Hierarchical twin architecture:</strong> Equipment twins nested within facility twins with automatic data aggregation</li>
                        <li>• <strong>Multi-parameter degradation modeling:</strong> Correlates temperature, vibration, pressure, flow rate for failure prediction</li>
                        <li>• <strong>Real-time sensor fusion:</strong> 3,245 data points/second from heterogeneous sources (SCADA, historians, IoT)</li>
                        <li>• <strong>AI-powered simulation engine:</strong> What-if scenario analysis with financial impact quantification</li>
                        <li>• <strong>Automated SAP integration:</strong> Direct work order creation from digital twin predictions with priority optimization</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Industrial Applicability</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Offshore platforms:</strong> Monitor 127+ critical assets (compressors, pumps, turbines, separators)</li>
                        <li>• <strong>Refineries:</strong> Complete facility digital twins tracking production, energy, emissions</li>
                        <li>• <strong>Pipelines:</strong> Integrity monitoring with corrosion and leak prediction</li>
                        <li>• <strong>Measurable impact:</strong> 60% downtime reduction, $20M annual savings, 30% asset lifespan extension</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <h5 className="font-semibold text-purple-900 mb-2">Key Claims</h5>
                    <div className="text-sm text-purple-800 space-y-2">
                      <div><strong>Claim 1:</strong> A hierarchical digital twin system comprising equipment-level twins synchronized to facility-level twins with real-time bidirectional data flow</div>
                      <div><strong>Claim 2:</strong> Method for predicting equipment failure using multi-parameter sensor fusion with physics-based degradation models achieving 89.8% accuracy</div>
                      <div><strong>Claim 3:</strong> Automated integration system connecting digital twin predictions to enterprise resource planning (SAP PM) for predictive work order generation</div>
                      <div><strong>Claim 4:</strong> Interactive 3D visualization system with auto-rotating scene, health-coded models, and real-time sensor data particle effects</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Patent 2: AI-Powered Drilling Optimization */}
            <Card className="border-2 border-orange-300">
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-6 w-6 text-orange-600" />
                  Patent Method 2: AI-Powered Real-Time Drilling Parameter Optimization System
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Innovation Description</h4>
                    <p className="text-sm text-gray-700">
                      A real-time AI system that analyzes drilling parameters (Rate of Penetration, Weight on Bit, RPM, 
                      Equivalent Circulating Density) using AWS Bedrock Claude foundation models with domain-specific system 
                      prompts for petroleum engineering. The system predicts stuck pipe incidents 2-4 hours in advance with 
                      75% prevention rate and optimizes drilling parameters to achieve 20% ROP improvement, saving $100K-$400K per well.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Novel Technical Elements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Domain-specific LLM prompting:</strong> Petroleum engineering system prompts for Claude 3 Sonnet with formation-specific optimization</li>
                        <li>• <strong>Stuck pipe prediction algorithm:</strong> Multi-parameter analysis (torque, drag, ECD, formation pressure) for 2-4 hour advance warning</li>
                        <li>• <strong>Real-time parameter optimization:</strong> Continuous ROP/WOB/RPM adjustment recommendations based on formation characteristics</li>
                        <li>• <strong>Cost-benefit analysis:</strong> Automated calculation of drilling time savings vs. equipment wear trade-offs</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Industrial Applicability</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Deepwater drilling:</strong> Optimize parameters for high-cost environments ($500K-$2M/day)</li>
                        <li>• <strong>Unconventional wells:</strong> Horizontal drilling optimization for shale formations</li>
                        <li>• <strong>Mature field development:</strong> Parameter optimization for challenging reservoirs</li>
                        <li>• <strong>Measurable impact:</strong> 20% ROP improvement, 75% stuck pipe reduction, 12% cost savings per well</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <h5 className="font-semibold text-orange-900 mb-2">Key Claims</h5>
                    <div className="text-sm text-orange-800 space-y-2">
                      <div><strong>Claim 1:</strong> Method for real-time drilling optimization using foundation language models with petroleum engineering domain knowledge</div>
                      <div><strong>Claim 2:</strong> Stuck pipe prediction system analyzing multi-parameter drilling data with 2-4 hour advance warning capability</div>
                      <div><strong>Claim 3:</strong> Automated drilling parameter adjustment system based on formation characteristics and equipment limitations</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Patent 3: Predictive Maintenance Integration */}
            <Card className="border-2 border-green-300">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-6 w-6 text-green-600" />
                  Patent Method 3: AI-Integrated Predictive Maintenance with Automated ERP Workflow
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Innovation Description</h4>
                    <p className="text-sm text-gray-700">
                      An integrated system that combines anomaly detection algorithms monitoring 10,000+ sensors with machine 
                      learning models predicting Remaining Useful Life (RUL), automatically generating SAP PM work orders with 
                      AI-optimized spare parts lists, crew scheduling, and maintenance timing. The system achieves 90% failure 
                      prediction accuracy with 7-14 days early warning, reducing unplanned downtime by 40% and saving $15M annually.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Novel Technical Elements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Multi-sensor anomaly detection:</strong> Correlates temperature, vibration, pressure, flow, chemical levels across equipment</li>
                        <li>• <strong>RUL prediction with degradation modeling:</strong> Physics-based wear patterns combined with ML for 90% accuracy</li>
                        <li>• <strong>Automated root cause analysis:</strong> AWS Bedrock Claude identifies failure modes from sensor patterns</li>
                        <li>• <strong>SAP PM bidirectional integration:</strong> Work order creation with automatic spare parts demand forecasting</li>
                        <li>• <strong>Maintenance crew optimization:</strong> AI schedules work based on equipment criticality, crew skills, parts availability</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Industrial Applicability</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Rotating equipment:</strong> Compressors, turbines, pumps with bearing/seal failure prediction</li>
                        <li>• <strong>Static equipment:</strong> Heat exchangers, pressure vessels with corrosion/fouling detection</li>
                        <li>• <strong>Instrumentation:</strong> Control valves, sensors with drift and calibration prediction</li>
                        <li>• <strong>Measurable impact:</strong> 40% downtime reduction, $15M savings, 25% asset life extension</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Patent 4: SAP Joule Integration */}
            <Card className="border-2 border-blue-300">
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-6 w-6 text-blue-600" />
                  Patent Method 4: Multi-Modal AI Copilot for Enterprise SAP Integration
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Innovation Description</h4>
                    <p className="text-sm text-gray-700">
                      A novel AI copilot system integrating with 14 SAP modules through 4 distinct interaction modes 
                      (Transactional, Navigational, Informational, Analytical), using OAuth2-secured SAP Joule integration 
                      to enable natural language business operations. The system achieves 85-98% confidence in query responses 
                      while maintaining SAP role-based access control and audit compliance.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Novel Technical Elements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Multi-modal interaction framework:</strong> Single interface supporting transactional CRUD, navigation, information retrieval, and analytical queries</li>
                        <li>• <strong>Context-aware SAP module routing:</strong> AI determines optimal SAP module(s) to access based on query intent</li>
                        <li>• <strong>OAuth2 formation-based authentication:</strong> Secure SAP BTP integration with role-based permission inheritance</li>
                        <li>• <strong>Natural language to SAP action mapping:</strong> Translates conversational queries to SAP transactions with validation</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Industrial Applicability</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Finance teams:</strong> "Analyze cost center CC-1001 trends" → SAP FI/CO analytics</li>
                        <li>• <strong>Operations:</strong> "Show production efficiency for Q1" → SAP PP/MM data synthesis</li>
                        <li>• <strong>Procurement:</strong> "Create PO for drilling equipment $500K" → SAP MM transaction</li>
                        <li>• <strong>Measurable impact:</strong> 50% reduction in manual SAP navigation, 24/7 expert-level guidance</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Patent 5: Carbon Forecasting */}
            <Card className="border-2 border-green-300">
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-6 w-6 text-green-600" />
                  Patent Method 5: AI-Powered 90-Day Carbon Emissions Forecasting with Optimization
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Innovation Description</h4>
                    <p className="text-sm text-gray-700">
                      A machine learning system that predicts Scope 1, 2, and 3 carbon emissions 90 days in advance by 
                      analyzing production schedules, equipment efficiency, energy consumption patterns, and operational variables. 
                      The system integrates with SAP Sustainability module for real-time carbon accounting in cost centers and 
                      provides AI-powered reduction recommendations achieving 20-25% emissions reduction ($5M-$25M tax savings).
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Novel Technical Elements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Multi-scope emissions prediction:</strong> Unified model for Scope 1 (direct), Scope 2 (energy), Scope 3 (value chain) forecasting</li>
                        <li>• <strong>Production-emissions correlation:</strong> Links drilling schedules, refining throughput, flare volumes to carbon output</li>
                        <li>• <strong>Flare optimization algorithm:</strong> Machine learning reduces flaring by 30% while maintaining safety compliance</li>
                        <li>• <strong>SAP CO integration:</strong> Real-time carbon accounting synced to cost centers for financial reporting</li>
                        <li>• <strong>Automated regulatory reporting:</strong> CDP, TCFD, GRI, EU ETS report generation with AI insights</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Industrial Applicability</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Carbon tax optimization:</strong> Predict and minimize exposure to $15-$50/ton carbon pricing</li>
                        <li>• <strong>ESG reporting automation:</strong> 85% time reduction (3 months → 2 weeks for annual reports)</li>
                        <li>• <strong>Methane leak detection:</strong> Satellite and sensor fusion for early leak identification</li>
                        <li>• <strong>Measurable impact:</strong> 20-25% emissions reduction, +15 points ESG score improvement</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Patent 6: Facility-Wide Optimization */}
            <Card className="border-2 border-indigo-300">
              <CardHeader className="bg-indigo-50">
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-6 w-6 text-indigo-600" />
                  Patent Method 6: Multi-Objective Facility Optimization Using Digital Twin Simulation
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Innovation Description</h4>
                    <p className="text-sm text-gray-700">
                      A digital twin-based optimization system that simultaneously optimizes multiple competing objectives 
                      (production rate, energy efficiency, emissions reduction) across entire facilities. Uses AI to test 
                      thousands of parameter combinations in virtual environment, identifying optimal settings that achieve 
                      12.4% production increase, 16.5% energy savings, and 24.1% emissions reduction with $15.5M annual benefit.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Novel Technical Elements</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Multi-objective optimization:</strong> Balances production, energy, emissions, safety using Pareto frontier analysis</li>
                        <li>• <strong>Virtual scenario simulation:</strong> Tests parameter changes without physical operational disruption (94.5% accuracy)</li>
                        <li>• <strong>AI recommendation ranking:</strong> Prioritizes actions by effort vs. impact with financial quantification</li>
                        <li>• <strong>Constraint satisfaction:</strong> Ensures recommendations respect equipment limits, safety regulations, production contracts</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Industrial Applicability</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>• <strong>Offshore platforms:</strong> Optimize separator pressure, compression ratio, gas routing for max production</li>
                        <li>• <strong>Refineries:</strong> Balance throughput, energy efficiency, product yields, emissions</li>
                        <li>• <strong>Pipelines:</strong> Optimize flow rates, pump speeds, pressure for energy minimization</li>
                        <li>• <strong>Measurable impact:</strong> $15.5M annual benefit, 12.4% production increase, 24.1% emissions reduction</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Patent Summary */}
            <Card className="border-2 border-blue-500">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-6 w-6 text-blue-600" />
                  Patent Portfolio Strategic Value
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-semibold text-blue-900 mb-2">Competitive Moat</h5>
                    <p className="text-sm text-blue-800">
                      Patent portfolio creates 5-10 year competitive advantage in AI-powered Oil & Gas operations, 
                      preventing competitors from replicating core innovations in digital twin architecture, 
                      drilling optimization, and SAP AI integration.
                    </p>
                  </div>

                  <div className="p-4 bg-green-50 rounded-lg">
                    <h5 className="font-semibold text-green-900 mb-2">Licensing Opportunity</h5>
                    <p className="text-sm text-green-800">
                      Patent methods applicable across Oil & Gas sector (upstream, midstream, downstream) and adjacent 
                      industries (mining, petrochemicals, utilities). Potential licensing revenue $5M-$20M annually 
                      from technology transfer to equipment manufacturers and service companies.
                    </p>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h5 className="font-semibold text-purple-900 mb-2">Valuation Impact</h5>
                    <p className="text-sm text-purple-800">
                      Patent portfolio adds $50M-$200M to company valuation through demonstrable IP protection, 
                      technology differentiation, and defensible market position. Critical for M&A scenarios 
                      and strategic partnerships with major operators.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'user-journeys':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">User Journeys</h2>
              <div className="bg-gradient-to-r from-blue-50 to-green-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">End-to-End User Workflows Across All Modules</h3>
                <p className="text-gray-700">
                  Detailed step-by-step user journeys for each PetraMind module, demonstrating how different roles 
                  (Executives, Operations Managers, Field Technicians, Engineers) leverage AI-powered capabilities 
                  to optimize Oil & Gas operations.
                </p>
              </div>
            </div>

            {/* Journey 1: Maintenance Engineer - Predictive Maintenance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-6 w-6 text-blue-600" />
                  Journey 1: Maintenance Engineer - Predictive Maintenance Workflow
                </CardTitle>
                <CardDescription>Persona: Sarah Chen, Senior Maintenance Engineer, North Sea Platform Alpha</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-5 gap-3">
                    <div className="p-3 bg-blue-50 rounded-lg text-center border-l-4 border-blue-500">
                      <div className="font-bold text-lg text-blue-600">1</div>
                      <div className="text-xs font-medium mt-1">Morning Review</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center border-l-4 border-blue-500">
                      <div className="font-bold text-lg text-blue-600">2</div>
                      <div className="text-xs font-medium mt-1">AI Alert</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center border-l-4 border-blue-500">
                      <div className="font-bold text-lg text-blue-600">3</div>
                      <div className="text-xs font-medium mt-1">Investigate</div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg text-center border-l-4 border-blue-500">
                      <div className="font-bold text-lg text-blue-600">4</div>
                      <div className="text-xs font-medium mt-1">Plan Action</div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg text-center border-l-4 border-green-500">
                      <div className="font-bold text-lg text-green-600">5</div>
                      <div className="text-xs font-medium mt-1">Execute</div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 border-l-4 border-blue-500 bg-blue-50">
                      <h5 className="font-semibold mb-2">Step 1: Morning Dashboard Review (7:00 AM)</h5>
                      <p className="text-sm text-gray-700 mb-2">
                        Sarah logs into PetraMind and navigates to <strong>Predictive Maintenance</strong> module. 
                        Dashboard shows 10 assets under monitoring with overall health score of 88.5%.
                      </p>
                      <div className="text-xs text-gray-600">
                        <strong>Actions:</strong> Click "Predictive Maintenance" → View "Assets" tab → Review health scores
                      </div>
                    </div>

                    <div className="p-4 border-l-4 border-orange-500 bg-orange-50">
                      <h5 className="font-semibold mb-2">Step 2: AI Alert - Critical Equipment Identified (7:05 AM)</h5>
                      <p className="text-sm text-gray-700 mb-2">
                        AI system alerts Sarah: <em>"Main Gas Compressor #3 showing elevated vibration (14.2 mm/s vs. normal 8.5 mm/s). 
                        Predicted bearing failure in 14 days with 72% probability. Immediate attention required."</em>
                      </p>
                      <div className="text-xs text-gray-600">
                        <strong>System Action:</strong> PetraMind auto-generates alert → Email + dashboard notification → Sarah clicks "View Details"
                      </div>
                    </div>

                    <div className="p-4 border-l-4 border-purple-500 bg-purple-50">
                      <h5 className="font-semibold mb-2">Step 3: Digital Twin Investigation (7:10 AM)</h5>
                      <p className="text-sm text-gray-700 mb-2">
                        Sarah navigates to <strong>Digital Twin Manager</strong> → Clicks "Main Gas Compressor #3" → Views:
                        <br/>• Real-time sensors: Temperature 78.5°C, Vibration 14.2 mm/s, Pressure inlet 145.2 psi
                        <br/>• Health degradation trend: 85.2% → 68.5% over 3 weeks
                        <br/>• Failure mode analysis: Bearing failure 72% probability, indicators = elevated vibration + temperature
                        <br/>• 3D visualization shows compressor with red health indicator + pulsing warning
                      </p>
                      <div className="text-xs text-gray-600">
                        <strong>Actions:</strong> Navigate Digital Twin → Select equipment → Review sensor data → View 3D model → Check degradation trend
                      </div>
                    </div>

                    <div className="p-4 border-l-4 border-blue-500 bg-blue-50">
                      <h5 className="font-semibold mb-2">Step 4: Simulation & Planning (7:20 AM)</h5>
                      <p className="text-sm text-gray-700 mb-2">
                        Sarah runs "What-If Simulation" to test impact of delaying maintenance:
                        <br/>• <strong>Scenario 1 (Immediate repair):</strong> 8 hours downtime, $85K cost, prevents catastrophic failure
                        <br/>• <strong>Scenario 2 (Delay 2 weeks):</strong> Risk of failure increases to 95%, potential 3-day shutdown ($6M+ impact)
                        <br/>• AI recommends: "Schedule maintenance within 14 days. Required parts: Bearing-KIT-001, Seal-ASM-205. Estimated duration: 8 hours."
                      </p>
                      <div className="text-xs text-gray-600">
                        <strong>Actions:</strong> Click "Run Simulation" → Review scenarios → Accept AI recommendation → Click "Create Work Order"
                      </div>
                    </div>

                    <div className="p-4 border-l-4 border-green-500 bg-green-50">
                      <h5 className="font-semibold mb-2">Step 5: SAP PM Work Order Creation (7:25 AM)</h5>
                      <p className="text-sm text-gray-700 mb-2">
                        Sarah clicks "Create SAP PM Work Order" → System auto-generates:
                        <br/>• Work Order WO-45890 in SAP PM
                        <br/>• Priority: High, Type: Predictive Maintenance
                        <br/>• Parts list auto-populated from digital twin recommendations
                        <br/>• Estimated duration: 8 hours
                        <br/>• Required skills: Mechanical Technician, Vibration Analyst
                        <br/>• Planned start: 2 days from now (allows parts procurement + crew scheduling)
                      </p>
                      <div className="text-xs text-gray-600">
                        <strong>Outcome:</strong> Work order created in SAP → Parts automatically reserved → Crew notified → Prevented $6M+ catastrophic failure → 5 minutes total effort (vs. 45 minutes manual process)
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-semibold mb-2">Journey Impact</h5>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <div className="font-bold text-blue-600">90% Time Saved</div>
                        <div className="text-gray-600">5 min vs. 45 min manual process</div>
                      </div>
                      <div>
                        <div className="font-bold text-green-600">$6M+ Loss Prevented</div>
                        <div className="text-gray-600">Avoided catastrophic failure</div>
                      </div>
                      <div>
                        <div className="font-bold text-purple-600">14 Days Early Warning</div>
                        <div className="text-gray-600">Time to plan and execute</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Journey 2: Drilling Engineer */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-6 w-6 text-orange-600" />
                  Journey 2: Drilling Engineer - Real-Time Optimization
                </CardTitle>
                <CardDescription>Persona: John Martinez, Drilling Engineer, Gulf of Mexico Operations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-5 gap-3">
                    {[1, 2, 3, 4, 5].map(num => (
                      <div key={num} className="p-3 bg-orange-50 rounded-lg text-center border-l-4 border-orange-500">
                        <div className="font-bold text-lg text-orange-600">{num}</div>
                        <div className="text-xs font-medium mt-1">
                          {num === 1 ? 'Monitor' : num === 2 ? 'Alert' : num === 3 ? 'Ask AI' : num === 4 ? 'Optimize' : 'Apply'}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-3 text-sm">
                    <div className="p-3 bg-orange-50 border-l-4 border-orange-500">
                      <strong>Step 1:</strong> John monitors drilling at 2,450m depth. Current ROP: 10.5 m/hr (below expected 13-15 m/hr)
                    </div>
                    <div className="p-3 bg-yellow-50 border-l-4 border-yellow-500">
                      <strong>Step 2:</strong> AI detects suboptimal parameters → Alert: "ROP 25% below optimal. Review drilling parameters."
                    </div>
                    <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                      <strong>Step 3:</strong> John uses <strong>AI Assistant</strong>: "Why is ROP low at 2,450m depth? Current WOB 18 MT, RPM 95."
                      <br/><em>AI Response (AWS Bedrock Claude):</em> "Formation analysis indicates you're drilling through hard limestone. Recommendations: 1) Increase WOB to 22-24 MT, 2) Reduce RPM to 80-85 for better penetration, 3) Check bit condition - may need replacement after 200m in this formation."
                    </div>
                    <div className="p-3 bg-purple-50 border-l-4 border-purple-500">
                      <strong>Step 4:</strong> John applies AI recommendations → WOB increased to 23 MT, RPM reduced to 82 → ROP improves to 13.8 m/hr (31% increase)
                    </div>
                    <div className="p-3 bg-green-50 border-l-4 border-green-500">
                      <strong>Step 5:</strong> <strong>Result:</strong> 150m section drilled in 11 hours vs. expected 14.3 hours → Saved 3.3 hours × $750K/day = $103K savings
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <strong>Journey Impact:</strong> $103K saved, 23% time reduction, AI provided expert guidance 24/7
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Journey 3: ESG Manager */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Leaf className="h-6 w-6 text-green-600" />
                  Journey 3: ESG Manager - Carbon Reporting & Compliance
                </CardTitle>
                <CardDescription>Persona: Maria Rodriguez, ESG & Sustainability Manager, Corporate HQ</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-6 gap-2">
                    {['Track', 'Forecast', 'SAP Sync', 'Generate Report', 'Review', 'Submit'].map((step, idx) => (
                      <div key={idx} className="p-2 bg-green-50 rounded-lg text-center border-l-4 border-green-500">
                        <div className="font-bold text-sm text-green-600">{idx + 1}</div>
                        <div className="text-xs font-medium mt-1">{step}</div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-2">
                    <div className="p-3 bg-green-50 border-l-4 border-green-500">
                      <strong>Step 1 (Day 1):</strong> Navigate to <strong>ESG Intelligence</strong> → View real-time Scope 1/2/3 emissions across all facilities
                    </div>
                    <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                      <strong>Step 2 (Day 1):</strong> Click "Generate Carbon Forecast" → AI predicts 90-day emissions trajectory: Current 450 tons CO2/day, forecast shows 15% reduction potential
                    </div>
                    <div className="p-3 bg-purple-50 border-l-4 border-purple-500">
                      <strong>Step 3 (Day 2):</strong> Navigate to "SAP Sustainability Integration" tab → Click "Sync ESG Data with SAP" → System syncs emissions data to SAP CO cost centers for financial carbon accounting
                    </div>
                    <div className="p-3 bg-orange-50 border-l-4 border-orange-500">
                      <strong>Step 4 (Day 3-5):</strong> Click "Generate AI Report" → Select "CDP Climate Change Disclosure" → AI generates 120-page report with: emissions data, reduction initiatives, risks/opportunities, governance structure, financial implications. Traditional time: 3 months, PetraMind time: 2 days.
                    </div>
                    <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                      <strong>Step 5 (Day 6):</strong> Maria reviews AI-generated report, adds executive commentary, validates data accuracy
                    </div>
                    <div className="p-3 bg-green-50 border-l-4 border-green-500">
                      <strong>Step 6 (Day 7):</strong> Submit CDP report → <strong>Result:</strong> 85% faster reporting, improved from B- to A- rating (+15 points ESG score), unlocks $500M green financing
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <strong>Journey Impact:</strong> 85% time savings (3 months → 1 week), ESG score +15 points, $500M green financing unlocked
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Journey 4: Operations Manager - Facility Optimization */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Factory className="h-6 w-6 text-indigo-600" />
                  Journey 4: Operations Manager - Facility-Wide Optimization Using Digital Twin
                </CardTitle>
                <CardDescription>Persona: David Thompson, Operations Manager, Gulf Coast Refinery Unit 1</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4 text-sm">
                  <div className="p-3 bg-indigo-50 border-l-4 border-indigo-500">
                    <strong>Scenario:</strong> Refinery operating at 87.5% efficiency, production 125,000 bpd. Monthly energy cost $2.4M. David needs to improve efficiency without major CAPEX.
                  </div>

                  <div className="space-y-2">
                    <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                      <strong>Step 1:</strong> Navigate to <strong>Digital Twin Manager</strong> → Select "Gulf Coast Refinery Unit 1" facility twin → Review current state: 289 equipment, 4,820 sensors, 87.5% efficiency, 125,000 bpd throughput
                    </div>
                    <div className="p-3 bg-purple-50 border-l-4 border-purple-500">
                      <strong>Step 2:</strong> Click "Optimize Energy" button → AI analyzes entire facility in digital twin simulation → Tests 10,000+ parameter combinations virtually
                    </div>
                    <div className="p-3 bg-green-50 border-l-4 border-green-500">
                      <strong>Step 3:</strong> AI returns results: <strong>Improvement Potential:</strong> +6.7% efficiency, 16.5% energy savings ($396K/month), +12.4% production → <strong>Annual benefit: $15.5M</strong>
                    </div>
                    <div className="p-3 bg-orange-50 border-l-4 border-orange-500">
                      <strong>Step 4:</strong> Review <strong>AI-Recommended Actions</strong>:
                      <br/>1. Optimize separator pressure to 465 psi (from 450 psi) - Low effort, Medium impact
                      <br/>2. Adjust gas compression ratio to 6.2 (from 6.0) - Low effort, High impact  
                      <br/>3. Reduce flaring by routing to power generation - Medium effort, High impact
                      <br/>4. Implement VSD on major pumps - High effort, High impact ($8M NPV)
                    </div>
                    <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                      <strong>Step 5:</strong> David implements quick wins (#1, #2) immediately → Efficiency jumps to 91.2% within 24 hours
                    </div>
                    <div className="p-3 bg-green-50 border-l-4 border-green-500">
                      <strong>Step 6:</strong> Plans CAPEX for VSD installation → Presents AI simulation results to CFO for approval
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <strong>Journey Impact:</strong> +3.7% efficiency (immediate), $15.5M annual benefit (projected), No CAPEX for quick wins, Data-driven executive approval for VSD project
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Journey 5: Executive - Strategic Dashboard */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="h-6 w-6 text-purple-600" />
                  Journey 5: C-Level Executive - Strategic Decision Making
                </CardTitle>
                <CardDescription>Persona: Robert Chen, VP Operations, Global O&G Corporation</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-purple-50 border-l-4 border-purple-500">
                    <strong>Morning Routine (6:30 AM):</strong> Robert opens <strong>Executive Dashboard</strong> on mobile during commute
                    <br/>→ Views: 14 total assets, 90.8% efficiency, 18.2% compliance, 97.8% uptime
                    <br/>→ Predicted Savings: $2.6M (AI-driven cost optimization)
                    <br/>→ Environmental Score: 93.0/100 (ESG sustainability metrics)
                  </div>

                  <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                    <strong>AI Strategic Insights Tab:</strong> Reviews AI-generated insights:
                    <br/>"Production efficiency up 8.5% QoQ. Predictive maintenance preventing $15M downtime. CTRM trading optimization captured $3.2M in market opportunities. ESG score improved to A- (from B+). Recommendation: Accelerate Digital Twin rollout to remaining 12 facilities for $85M additional annual value."
                  </div>

                  <div className="p-3 bg-green-50 border-l-4 border-green-500">
                    <strong>Uses AI Assistant:</strong> "What are the top 3 operational risks this quarter?"
                    <br/><em>AI Response:</em> "1) Main Gas Compressor #3 predicted failure (14 days, $6M impact if unplanned) - Work order created. 2) CTRM exposure to crude price volatility ($12M at risk) - Hedge recommendations ready. 3) Compliance deadline Dec 15 for EPA emissions report - On track with AI automation."
                  </div>

                  <div className="p-3 bg-orange-50 border-l-4 border-orange-500">
                    <strong>Board Meeting Prep (2:00 PM):</strong> Navigate to <strong>Executive Reports</strong> → Generate "Operational Summary" → AI creates comprehensive board report with: operational KPIs, financial performance, risk assessment, AI initiative ROI ($170M annual value), strategic recommendations
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <strong>Journey Impact:</strong> 15 minutes daily for complete operational visibility (vs. 2 hours with traditional tools), Data-driven decision making, Proactive risk management
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Journey 6: Supply Chain Manager */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="h-6 w-6 text-teal-600" />
                  Journey 6: Supply Chain Manager - AI-Driven Inventory Optimization
                </CardTitle>
                <CardDescription>Persona: Lisa Wang, Supply Chain Manager, Multi-Asset Operations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-teal-50 border-l-4 border-teal-500">
                    <strong>Challenge:</strong> Managing $85M inventory across 5 offshore platforms. Stockout risk vs. working capital tie-up.
                  </div>

                  <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                    <strong>Step 1:</strong> <strong>Supply Chain</strong> module → AI Demand Forecasting analyzes:
                    <br/>• Digital twin predictions (which equipment needs maintenance when)
                    <br/>• Historical consumption patterns
                    <br/>• Production schedules from SAP PP
                    <br/>→ Predicts: "Bearing-KIT-001 demand spike in 14 days (3 units needed), Seal-ASM-205 in 21 days (2 units)"
                  </div>

                  <div className="p-3 bg-purple-50 border-l-4 border-purple-500">
                    <strong>Step 2:</strong> System auto-generates SAP MM purchase requisitions → Routes to preferred suppliers (SAP Ariba integration)
                  </div>

                  <div className="p-3 bg-green-50 border-l-4 border-green-500">
                    <strong>Step 3:</strong> AI optimizes logistics: Helicopter delivery for critical parts (14-day items), boat delivery for non-urgent (21-day items) → Saves $45K in logistics costs
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <strong>Journey Impact:</strong> 60% stockout reduction, 25% inventory cost reduction ($21M working capital freed), 18% procurement savings, Zero manual purchase requisitions
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Journey 7: SAP Integration Manager */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plug className="h-6 w-6 text-blue-600" />
                  Journey 7: IT Manager - SAP Integration Configuration & Testing
                </CardTitle>
                <CardDescription>Persona: Ahmed Hassan, IT Integration Manager</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                    <strong>Step 1:</strong> Navigate to <strong>SAP Integration Manager</strong> → Dashboard shows 14 SAP modules across 8 categories
                  </div>

                  <div className="p-3 bg-purple-50 border-l-4 border-purple-500">
                    <strong>Step 2:</strong> Select "SAP PM (Plant Maintenance)" module → Click "Test Integration" → Choose test type: "Connectivity, Authentication, Data Flow" → All tests pass ✓
                  </div>

                  <div className="p-3 bg-green-50 border-l-4 border-green-500">
                    <strong>Step 3:</strong> View "Field Mappings" → Configure petra_field → sap_field relationships: equipment_id → SAP Equipment Number, health_score → Technical Status
                  </div>

                  <div className="p-3 bg-orange-50 border-l-4 border-orange-500">
                    <strong>Step 4:</strong> Click "Sync SAP PM Data" → Bidirectional sync: PetraMind equipment data → SAP PM, SAP work orders → PetraMind alerts → Records synced: 247, Duration: 3.2 seconds
                  </div>

                  <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                    <strong>Step 5:</strong> Monitor "Performance Metrics" → Uptime: 99.2%, Average sync time: 2.8s, Success rate: 100%
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <strong>Journey Impact:</strong> 14 SAP modules configured in 2 hours (vs. 2 weeks manual), Real-time monitoring, Zero-downtime deployments
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Journey Summary */}
            <Card className="border-2 border-blue-500">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
                <CardTitle>User Journey Impact Summary</CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <div className="text-3xl font-bold text-blue-600">7</div>
                    <div className="text-sm text-gray-700 mt-1">Detailed User Journeys</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="text-3xl font-bold text-green-600">14</div>
                    <div className="text-sm text-gray-700 mt-1">Modules Covered</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <div className="text-3xl font-bold text-purple-600">5</div>
                    <div className="text-sm text-gray-700 mt-1">User Personas</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <div className="text-3xl font-bold text-orange-600">90%</div>
                    <div className="text-sm text-gray-700 mt-1">Avg Time Savings</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'pricing':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Pricing Strategy & Models</h2>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Flexible Pricing for Digital Transformation</h3>
                <p className="text-gray-700">
                  Choose the tier that matches your digital transformation journey. All tiers include core AI capabilities, 
                  with advanced features like Digital Twin 3D visualization, AWS Bedrock, and SAP Joule available in higher tiers.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Starter Tier */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-center text-blue-600">Starter</CardTitle>
                  <div className="text-center">
                    <div className="text-4xl font-bold">$35K</div>
                    <div className="text-sm text-gray-500">per month</div>
                    <div className="text-xs text-gray-400 mt-1">$420K annual (save $60K vs. monthly)</div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">Core Modules (4)</h4>
                      <ul className="text-sm space-y-1">
                        <li>✅ <strong>Predictive Maintenance</strong> - 90% failure prediction</li>
                        <li>✅ <strong>Compliance Management</strong> - SAP GRC integration</li>
                        <li>✅ <strong>Executive Dashboard</strong> - Real-time KPIs</li>
                        <li>✅ <strong>AI Assistant</strong> - 24/7 conversational AI</li>
                      </ul>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <h4 className="font-semibold text-gray-700 mb-2">Platform Limits</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Up to 150 monitored assets</li>
                        <li>• Up to 2,000 sensors</li>
                        <li>• 3 SAP module integrations (PM, GRC, FI)</li>
                        <li>• 1 facility location</li>
                        <li>• Emergent LLM AI models (9 models)</li>
                        <li>• Email support (48hr response)</li>
                        <li>• Monthly executive reports</li>
                      </ul>
                    </div>

                    <div className="pt-3 border-t">
                      <h4 className="font-semibold text-green-700 mb-2">Projected Annual Value</h4>
                      <div className="text-2xl font-bold text-green-600">$3M - $8M</div>
                      <div className="text-xs text-gray-600">ROI: 7-19x</div>
                    </div>

                    <Button className="w-full mt-4" variant="outline">
                      Start Free 30-Day Trial
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Professional Tier */}
              <Card className="border-2 border-purple-500 relative shadow-xl scale-105">
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-purple-500 text-white px-4 py-1 rounded-full text-xs font-semibold">⭐ Most Popular</span>
                </div>
                <CardHeader>
                  <CardTitle className="text-center text-purple-600">Professional</CardTitle>
                  <div className="text-center">
                    <div className="text-4xl font-bold">$95K</div>
                    <div className="text-sm text-gray-500">per month</div>
                    <div className="text-xs text-gray-400 mt-1">$1.14M annual (save $168K)</div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-purple-900 mb-2">All Starter + Scale Modules (7)</h4>
                      <ul className="text-sm space-y-1">
                        <li>✅ <strong>CTRM Trading</strong> - AI market analysis & risk</li>
                        <li>✅ <strong>Joint Ventures</strong> - Automated reconciliation</li>
                        <li>✅ <strong>ESG Intelligence</strong> - Carbon forecasting + SAP Sustainability</li>
                        <li>✅ <strong>Field Operations</strong> - Mobile workforce management</li>
                        <li>✅ <strong>Supply Chain</strong> - AI inventory optimization</li>
                        <li>✅ <strong>Workforce Safety</strong> - Incident prediction</li>
                        <li>✅ <strong>SAP Integration Manager</strong> - 14 module hub</li>
                      </ul>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <h4 className="font-semibold text-gray-700 mb-2">Platform Limits</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• Up to 1,000 monitored assets</li>
                        <li>• Up to 5,000 sensors</li>
                        <li>• 14 SAP module integrations (all)</li>
                        <li>• Up to 5 facility locations</li>
                        <li>• Emergent LLM + basic AWS Bedrock (Titan)</li>
                        <li>• Priority support (24hr response)</li>
                        <li>• Weekly AI-generated reports</li>
                        <li>• Basic digital twin (equipment-level only)</li>
                      </ul>
                    </div>

                    <div className="pt-3 border-t">
                      <h4 className="font-semibold text-green-700 mb-2">Projected Annual Value</h4>
                      <div className="text-2xl font-bold text-green-600">$25M - $60M</div>
                      <div className="text-xs text-gray-600">ROI: 22-53x</div>
                    </div>

                    <Button className="w-full mt-4 bg-purple-600 hover:bg-purple-700">
                      Request Demo
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Enterprise Tier */}
              <Card className="border-2 border-blue-600">
                <CardHeader>
                  <CardTitle className="text-center text-blue-600">Enterprise</CardTitle>
                  <div className="text-center">
                    <div className="text-4xl font-bold">$175K</div>
                    <div className="text-sm text-gray-500">per month</div>
                    <div className="text-xs text-gray-400 mt-1">$2.1M annual (save $240K)</div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">All Professional + Premium Features (14 Modules)</h4>
                      <ul className="text-sm space-y-1">
                        <li>✅ <strong>AI Studio</strong> - Model management + 3 AI platform integrations</li>
                        <li>✅ <strong>AWS Bedrock</strong> - Claude 3 Sonnet + Titan models</li>
                        <li>✅ <strong>SAP Joule</strong> - AI copilot with 4 interaction modes</li>
                        <li>✅ <strong>Digital Twin Manager</strong> - Equipment + Facility twins</li>
                        <li>✅ <strong>3D Visualization</strong> - Interactive 3D digital twins</li>
                        <li>✅ <strong>Advanced Analytics</strong> - Executive command center</li>
                        <li>✅ <strong>IoT Management</strong> - 10,000+ sensor integration</li>
                        <li>✅ <strong>Automated Workflows</strong> - Process automation</li>
                        <li>✅ <strong>Drilling Operations</strong> - ROP optimization</li>
                        <li>✅ <strong>Pipeline Monitoring</strong> - Integrity management</li>
                      </ul>
                    </div>
                    
                    <div className="pt-3 border-t">
                      <h4 className="font-semibold text-gray-700 mb-2">Enterprise Features</h4>
                      <ul className="text-sm space-y-1 text-gray-600">
                        <li>• <strong>Unlimited</strong> assets & sensors</li>
                        <li>• <strong>Complete SAP ecosystem</strong> (14 modules)</li>
                        <li>• <strong>Full AI platform access</strong> (Bedrock, Joule, Emergent)</li>
                        <li>• <strong>Hierarchical digital twins</strong> (equipment + facility)</li>
                        <li>• <strong>3D visualization engine</strong> (127 equipment + 7 facility models)</li>
                        <li>• <strong>Multi-facility deployment</strong> (unlimited locations)</li>
                        <li>• <strong>Dedicated CSM + TAM</strong></li>
                        <li>• <strong>24/7 premium support</strong> (4hr SLA)</li>
                        <li>• <strong>Custom development</strong> (40 hours/month)</li>
                        <li>• <strong>SAP BTP deployment</strong> assistance</li>
                        <li>• <strong>Patent licensing</strong> option available</li>
                      </ul>
                    </div>

                    <div className="pt-3 border-t">
                      <h4 className="font-semibold text-green-700 mb-2">Projected Annual Value</h4>
                      <div className="text-2xl font-bold text-green-600">$80M - $170M+</div>
                      <div className="text-xs text-gray-600">ROI: 38-81x</div>
                    </div>

                    <Button className="w-full mt-4 bg-blue-600 hover:bg-blue-700">
                      Contact Sales
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Add-On Modules */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5 text-orange-600" />
                  Premium Add-Ons (All Tiers)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-semibold">AWS Bedrock Premium Models</h5>
                          <p className="text-xs text-gray-600">Claude 3 Opus, Llama 3, Mistral Large</p>
                        </div>
                        <Badge className="bg-orange-100 text-orange-800">+$15K/mo</Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Access to most powerful foundation models for complex seismic interpretation, 
                        reservoir simulation, and advanced engineering analysis
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-semibold">Digital Twin 3D Expansion Pack</h5>
                          <p className="text-xs text-gray-600">WebGL rendering, VR/AR ready</p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">+$20K/mo</Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Upgrade from 127 to 500+ equipment 3D models, VR headset support for immersive 
                        training, AR overlay for field technicians, physics-based animation
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-semibold">Multi-Tenant SaaS Deployment</h5>
                          <p className="text-xs text-gray-600">White-label for service companies</p>
                        </div>
                        <Badge className="bg-purple-100 text-purple-800">Custom</Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Deploy PetraMind as white-labeled SaaS for your clients. Includes tenant 
                        isolation, custom branding, API access, and revenue sharing model
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-semibold">Industry-Specific Modules</h5>
                          <p className="text-xs text-gray-600">Drilling, Pipeline, Refining specialization</p>
                        </div>
                        <Badge className="bg-green-100 text-green-800">+$10K/mo each</Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Deep-domain modules: Drilling Operations (stuck pipe prediction, ROP optimization), 
                        Pipeline Monitoring (leak detection, corrosion prediction), Refinery Optimization
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-semibold">Patent Licensing</h5>
                          <p className="text-xs text-gray-600">6 patented AI methods</p>
                        </div>
                        <Badge className="bg-indigo-100 text-indigo-800">Negotiable</Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        License PetraMind's 6 patented methods (Digital Twin Architecture, Drilling 
                        Optimization, Predictive Maintenance, etc.) for use in your proprietary systems
                      </p>
                    </div>

                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h5 className="font-semibold">Professional Services</h5>
                          <p className="text-xs text-gray-600">Implementation, training, customization</p>
                        </div>
                        <Badge className="bg-gray-100 text-gray-800">$250/hr</Badge>
                      </div>
                      <p className="text-sm text-gray-600">
                        Expert services: SAP integration configuration, custom AI model training, 
                        workflow automation development, user training programs
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Feature Comparison Matrix */}
            <Card>
              <CardHeader>
                <CardTitle>Complete Feature Comparison Matrix</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 bg-gray-50">
                        <th className="text-left p-3 font-semibold">Feature Category</th>
                        <th className="text-center p-3 font-semibold">Starter</th>
                        <th className="text-center p-3 font-semibold">Professional</th>
                        <th className="text-center p-3 font-semibold">Enterprise</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-3 font-medium">Core Modules</td>
                        <td className="text-center p-3">4 modules</td>
                        <td className="text-center p-3">11 modules</td>
                        <td className="text-center p-3">14 modules</td>
                      </tr>
                      <tr className="border-b bg-gray-50">
                        <td className="p-3 font-medium">SAP Integrations</td>
                        <td className="text-center p-3">3 modules</td>
                        <td className="text-center p-3">14 modules</td>
                        <td className="text-center p-3">14 modules + BTP</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-medium">AI Platform Access</td>
                        <td className="text-center p-3">Emergent LLM</td>
                        <td className="text-center p-3">Emergent + Titan</td>
                        <td className="text-center p-3">All (Bedrock, Joule, Emergent)</td>
                      </tr>
                      <tr className="border-b bg-gray-50">
                        <td className="p-3 font-medium">Digital Twin</td>
                        <td className="text-center p-3">❌ Not included</td>
                        <td className="text-center p-3">Equipment-level only</td>
                        <td className="text-center p-3">Equipment + Facility (hierarchical)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-medium">3D Visualization</td>
                        <td className="text-center p-3">❌ Not included</td>
                        <td className="text-center p-3">Basic (50 models)</td>
                        <td className="text-center p-3">Advanced (127 equip + 7 facility)</td>
                      </tr>
                      <tr className="border-b bg-gray-50">
                        <td className="p-3 font-medium">Sensor Capacity</td>
                        <td className="text-center p-3">2,000</td>
                        <td className="text-center p-3">5,000</td>
                        <td className="text-center p-3">10,847+ (unlimited)</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-medium">Facility Locations</td>
                        <td className="text-center p-3">1</td>
                        <td className="text-center p-3">5</td>
                        <td className="text-center p-3">Unlimited</td>
                      </tr>
                      <tr className="border-b bg-gray-50">
                        <td className="p-3 font-medium">SAP BTP Deployment</td>
                        <td className="text-center p-3">❌</td>
                        <td className="text-center p-3">Configuration only</td>
                        <td className="text-center p-3">Full automation + support</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-medium">Support SLA</td>
                        <td className="text-center p-3">48 hours</td>
                        <td className="text-center p-3">24 hours</td>
                        <td className="text-center p-3">4 hours (24/7)</td>
                      </tr>
                      <tr className="border-b bg-gray-50">
                        <td className="p-3 font-medium">Custom Development</td>
                        <td className="text-center p-3">❌</td>
                        <td className="text-center p-3">20 hrs/month</td>
                        <td className="text-center p-3">40 hrs/month</td>
                      </tr>
                      <tr className="border-b">
                        <td className="p-3 font-medium">Patent Access</td>
                        <td className="text-center p-3">Usage only</td>
                        <td className="text-center p-3">Usage only</td>
                        <td className="text-center p-3">Licensing option available</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* ROI Calculator */}
            <Card className="border-2 border-green-500">
              <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-6 w-6 text-green-600" />
                  ROI Calculator & Value Justification
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Typical Annual Value Creation by Source</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                        <span className="text-sm">Predictive Maintenance Savings</span>
                        <span className="font-bold text-green-600">$15M - $25M</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                        <span className="text-sm">Production Optimization (12-15%)</span>
                        <span className="font-bold text-blue-600">$40M - $120M</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg">
                        <span className="text-sm">Digital Twin Prevented Downtime</span>
                        <span className="font-bold text-purple-600">$20M - $35M</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                        <span className="text-sm">Supply Chain Optimization</span>
                        <span className="font-bold text-orange-600">$8M - $18M</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-teal-50 rounded-lg">
                        <span className="text-sm">ESG/Carbon Tax Savings</span>
                        <span className="font-bold text-teal-600">$5M - $25M</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-indigo-50 rounded-lg">
                        <span className="text-sm">Safety Incident Prevention</span>
                        <span className="font-bold text-indigo-600">$8M - $15M</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <span className="text-sm">Drilling Optimization</span>
                        <span className="font-bold text-red-600">$5M - $20M</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Investment Payback Analysis</h4>
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <h5 className="font-semibold text-blue-900 mb-2">Professional Tier Example</h5>
                        <div className="text-sm space-y-2">
                          <div className="flex justify-between">
                            <span>Annual Investment:</span>
                            <span className="font-semibold">$1.14M</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Projected Annual Value:</span>
                            <span className="font-semibold text-green-600">$25M - $60M</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="font-bold">ROI:</span>
                            <span className="font-bold text-green-600">22x - 53x</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-bold">Payback Period:</span>
                            <span className="font-bold text-blue-600">2-3 weeks</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-purple-50 rounded-lg">
                        <h5 className="font-semibold text-purple-900 mb-2">Enterprise Tier Example</h5>
                        <div className="text-sm space-y-2">
                          <div className="flex justify-between">
                            <span>Annual Investment:</span>
                            <span className="font-semibold">$2.1M</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Projected Annual Value:</span>
                            <span className="font-semibold text-green-600">$80M - $170M</span>
                          </div>
                          <div className="flex justify-between border-t pt-2">
                            <span className="font-bold">ROI:</span>
                            <span className="font-bold text-green-600">38x - 81x</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="font-bold">Payback Period:</span>
                            <span className="font-bold text-blue-600">1-2 weeks</span>
                          </div>
                        </div>
                      </div>

                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-500">
                        <h5 className="font-semibold text-green-900 mb-2">Additional Value Drivers</h5>
                        <ul className="text-xs text-green-800 space-y-1">
                          <li>✓ Patent portfolio adds $50M-$200M company valuation</li>
                          <li>✓ ESG score improvement enables $500M+ green financing</li>
                          <li>✓ Digital transformation differentiator for M&A (15-25% premium)</li>
                          <li>✓ Competitive advantage through IP protection (5-10 year moat)</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Implementation Timeline */}
            <Card>
              <CardHeader>
                <CardTitle>Implementation Timeline & Onboarding</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg text-center">
                    <div className="font-bold text-2xl text-blue-600">Week 1-2</div>
                    <div className="text-xs text-gray-700 mt-2">SAP Integration Setup</div>
                    <div className="text-xs text-gray-600 mt-1">Configure 14 SAP modules, test connectivity, field mapping</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <div className="font-bold text-2xl text-green-600">Week 3-4</div>
                    <div className="text-xs text-gray-700 mt-2">Data Synchronization</div>
                    <div className="text-xs text-gray-600 mt-1">Initial data sync, AI model training, digital twin creation</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg text-center">
                    <div className="font-bold text-2xl text-purple-600">Week 5-6</div>
                    <div className="text-xs text-gray-700 mt-2">User Training</div>
                    <div className="text-xs text-gray-600 mt-1">Role-based training, workflow setup, best practices</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <div className="font-bold text-2xl text-orange-600">Week 7-8</div>
                    <div className="text-xs text-gray-700 mt-2">Go-Live & Optimization</div>
                    <div className="text-xs text-gray-600 mt-1">Full production deployment, performance tuning, value tracking</div>
                  </div>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-700">
                    <strong>Total Implementation:</strong> 6-8 weeks to full production deployment with complete 
                    feature activation. First value realized within 2-3 weeks (predictive maintenance alerts, 
                    AI assistant operational).
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Value Guarantee */}
            <Card className="border-2 border-blue-500">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                  PetraMind Value Guarantee
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h5 className="font-semibold text-green-900 mb-2">10x ROI Guarantee</h5>
                    <p className="text-sm text-green-800">
                      If PetraMind doesn't deliver at least 10x ROI within 12 months of full deployment, 
                      we'll work with you at no additional cost until targets are achieved or provide a full refund.
                    </p>
                  </div>

                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h5 className="font-semibold text-blue-900 mb-2">90-Day Value Validation</h5>
                    <p className="text-sm text-blue-800">
                      Within 90 days, demonstrate measurable value: predictive maintenance preventing first failure, 
                      AI assistant handling 100+ queries, or digital twin identifying optimization opportunity. 
                      Money-back guarantee if milestones not met.
                    </p>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg">
                    <h5 className="font-semibold text-purple-900 mb-2">No Lock-In Policy</h5>
                    <p className="text-sm text-purple-800">
                      Cancel anytime with 30 days notice. Export all your data, AI model configurations, and 
                      digital twin definitions. We'll even help migrate to alternative platforms if needed. 
                      Your data, your choice.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'architecture':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Technical Architecture & System Design</h2>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Comprehensive System Architecture with AI Decision Flows</h3>
                <p className="text-gray-700">
                  Visual representation of PetraMind's layered architecture, showing data flows, AI decision points, 
                  SAP integration patterns, and Digital Twin ecosystem interactions.
                </p>
              </div>
            </div>

            {/* Visual Architecture Diagram */}
            <Card className="border-2 border-blue-500">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-purple-50">
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-6 w-6 text-blue-600" />
                  PetraMind Functional Architecture - Visual Diagram
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                
                {/* Layer 1: Presentation Layer */}
                <div className="mb-8">
                  <div className="text-center mb-4">
                    <Badge className="bg-blue-600 text-white px-4 py-2 text-sm">PRESENTATION LAYER - React Frontend</Badge>
                  </div>
                  <div className="grid grid-cols-6 gap-3">
                    {[
                      { name: 'Dashboard', icon: BarChart3, color: 'blue' },
                      { name: 'Predictive Maintenance', icon: Settings, color: 'green' },
                      { name: 'Compliance', icon: Shield, color: 'purple' },
                      { name: 'AI Assistant', icon: MessageSquare, color: 'orange' },
                      { name: 'CTRM Trading', icon: LineChart, color: 'red' },
                      { name: 'JVA', icon: Handshake, color: 'teal' },
                      { name: 'ESG Intelligence', icon: Leaf, color: 'green' },
                      { name: 'Field Ops', icon: MapPin, color: 'blue' },
                      { name: 'Safety', icon: HardHat, color: 'red' },
                      { name: 'Supply Chain', icon: Truck, color: 'purple' },
                      { name: 'Digital Twin', icon: Globe, color: 'indigo' },
                      { name: 'AI Studio', icon: Bot, color: 'orange' }
                    ].map((module, idx) => (
                      <div key={idx} className={`p-3 bg-${module.color}-50 border-2 border-${module.color}-300 rounded-lg text-center hover:shadow-lg transition-all`}>
                        <module.icon className={`h-6 w-6 mx-auto mb-1 text-${module.color}-600`} />
                        <div className="text-xs font-semibold text-gray-900">{module.name}</div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Arrow Down */}
                  <div className="flex justify-center my-4">
                    <div className="flex flex-col items-center">
                      <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-green-500"></div>
                      <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-green-500"></div>
                      <div className="text-xs font-semibold text-green-600 mt-1">HTTPS/REST API Calls</div>
                    </div>
                  </div>
                </div>

                {/* Layer 2: Backend API Layer */}
                <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-500">
                  <div className="text-center mb-4">
                    <Badge className="bg-green-600 text-white px-4 py-2 text-sm">BACKEND API LAYER - FastAPI Python 3.11</Badge>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3">
                    {[
                      { endpoint: '/api/dashboard', color: 'blue' },
                      { endpoint: '/api/maintenance', color: 'green' },
                      { endpoint: '/api/compliance', color: 'purple' },
                      { endpoint: '/api/ctrm', color: 'orange' },
                      { endpoint: '/api/jva', color: 'red' },
                      { endpoint: '/api/esg', color: 'green' },
                      { endpoint: '/api/digital-twin/*', color: 'indigo' },
                      { endpoint: '/api/ai-studio/*', color: 'orange' },
                      { endpoint: '/api/sap/*', color: 'blue' },
                      { endpoint: '/api/ai/anomaly', color: 'purple' },
                      { endpoint: '/api/ai/predict', color: 'green' },
                      { endpoint: '/api/nlq/query', color: 'orange' }
                    ].map((api, idx) => (
                      <div key={idx} className={`p-2 bg-${api.color}-100 border border-${api.color}-400 rounded text-center`}>
                        <div className="text-xs font-mono font-semibold text-gray-800">{api.endpoint}</div>
                      </div>
                    ))}
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center my-4">
                    <div className="flex flex-col items-center">
                      <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-purple-500"></div>
                      <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-purple-500"></div>
                    </div>
                  </div>
                </div>

                {/* Layer 3: Core Processing Engines */}
                <div className="mb-8">
                  <div className="text-center mb-4">
                    <Badge className="bg-purple-600 text-white px-4 py-2 text-sm">CORE PROCESSING ENGINES</Badge>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-6">
                    {/* AI Decision Engine */}
                    <div className="p-4 bg-gradient-to-br from-orange-50 to-red-50 border-2 border-orange-400 rounded-lg shadow-lg">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-orange-300">
                        <Bot className="h-6 w-6 text-orange-600" />
                        <h4 className="font-bold text-orange-900">AI Decision Engine</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="p-2 bg-white rounded border-l-4 border-orange-500">
                          <div className="text-xs font-semibold">1. Query Analyzer</div>
                          <div className="text-xs text-gray-600">NLQ Handler</div>
                        </div>
                        <div className="flex justify-center"><div className="text-orange-500">↓</div></div>
                        <div className="p-2 bg-white rounded border-l-4 border-orange-500">
                          <div className="text-xs font-semibold">2. AI Routing</div>
                          <div className="text-xs text-gray-600">Model Selection</div>
                        </div>
                        <div className="flex justify-center"><div className="text-orange-500">↓</div></div>
                        <div className="p-2 bg-white rounded border-l-4 border-orange-500">
                          <div className="text-xs font-semibold">3. LLM Invoke</div>
                          <div className="text-xs text-gray-600">Bedrock/Joule</div>
                        </div>
                      </div>
                    </div>

                    {/* SAP Integration Hub */}
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-400 rounded-lg shadow-lg">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-blue-300">
                        <Plug className="h-6 w-6 text-blue-600" />
                        <h4 className="font-bold text-blue-900">SAP Integration Hub</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="p-2 bg-white rounded border-l-4 border-blue-500">
                          <div className="text-xs font-semibold">1. Field Mapper</div>
                          <div className="text-xs text-gray-600">Petra ↔ SAP Fields</div>
                        </div>
                        <div className="flex justify-center"><div className="text-blue-500">↓</div></div>
                        <div className="p-2 bg-white rounded border-l-4 border-blue-500">
                          <div className="text-xs font-semibold">2. Sync Manager</div>
                          <div className="text-xs text-gray-600">Bidirectional</div>
                        </div>
                        <div className="flex justify-center"><div className="text-blue-500">↓</div></div>
                        <div className="p-2 bg-white rounded border-l-4 border-blue-500">
                          <div className="text-xs font-semibold">3. Test Framework</div>
                          <div className="text-xs text-gray-600">5 Test Types</div>
                        </div>
                      </div>
                    </div>

                    {/* Digital Twin Engine */}
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-400 rounded-lg shadow-lg">
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b-2 border-indigo-300">
                        <Globe className="h-6 w-6 text-indigo-600" />
                        <h4 className="font-bold text-indigo-900">Digital Twin Engine</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="p-2 bg-white rounded border-l-4 border-indigo-500">
                          <div className="text-xs font-semibold">1. Sensor Fusion</div>
                          <div className="text-xs text-gray-600">10,847 IoT Sensors</div>
                        </div>
                        <div className="flex justify-center"><div className="text-indigo-500">↓</div></div>
                        <div className="p-2 bg-white rounded border-l-4 border-indigo-500">
                          <div className="text-xs font-semibold">2. Degradation AI</div>
                          <div className="text-xs text-gray-600">RUL Prediction</div>
                        </div>
                        <div className="flex justify-center"><div className="text-indigo-500">↓</div></div>
                        <div className="p-2 bg-white rounded border-l-4 border-indigo-500">
                          <div className="text-xs font-semibold">3. Simulation</div>
                          <div className="text-xs text-gray-600">94.5% Accuracy</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center my-4">
                    <div className="flex flex-col items-center">
                      <div className="w-1 h-8 bg-gradient-to-b from-purple-500 to-blue-500"></div>
                      <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-blue-500"></div>
                    </div>
                  </div>
                </div>

                {/* Layer 4: AI/ML Integration Layer */}
                <div className="mb-8 p-6 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border-2 border-orange-500">
                  <div className="text-center mb-4">
                    <Badge className="bg-orange-600 text-white px-4 py-2 text-sm">AI/ML INTEGRATION LAYER</Badge>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    {/* AWS Bedrock */}
                    <div className="p-4 bg-white rounded-lg border-2 border-orange-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Cloud className="h-5 w-5 text-orange-600" />
                        <h5 className="font-bold text-sm">AWS Bedrock</h5>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="p-2 bg-orange-50 rounded">Claude 3 Sonnet</div>
                        <div className="p-2 bg-orange-50 rounded">Amazon Titan</div>
                        <div className="mt-2 text-gray-600">Drilling AI, Seismic Analysis</div>
                      </div>
                    </div>

                    {/* SAP Joule */}
                    <div className="p-4 bg-white rounded-lg border-2 border-blue-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Bot className="h-5 w-5 text-blue-600" />
                        <h5 className="font-bold text-sm">SAP Joule</h5>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="p-2 bg-blue-50 rounded">Transactional</div>
                        <div className="p-2 bg-blue-50 rounded">Analytical</div>
                        <div className="mt-2 text-gray-600">4 Interaction Modes</div>
                      </div>
                    </div>

                    {/* Emergent LLM */}
                    <div className="p-4 bg-white rounded-lg border-2 border-purple-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="h-5 w-5 text-purple-600" />
                        <h5 className="font-bold text-sm">Emergent LLM</h5>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="p-2 bg-purple-50 rounded">GPT-4o-mini</div>
                        <div className="p-2 bg-purple-50 rounded">Claude Sonnet</div>
                        <div className="mt-2 text-gray-600">9 Models Available</div>
                      </div>
                    </div>

                    {/* Custom ML */}
                    <div className="p-4 bg-white rounded-lg border-2 border-green-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Target className="h-5 w-5 text-green-600" />
                        <h5 className="font-bold text-sm">Custom ML</h5>
                      </div>
                      <div className="space-y-1 text-xs">
                        <div className="p-2 bg-green-50 rounded">Anomaly Detection</div>
                        <div className="p-2 bg-green-50 rounded">RUL Prediction</div>
                        <div className="mt-2 text-gray-600">Physics-Based Models</div>
                      </div>
                    </div>
                  </div>

                  {/* AI Decision Router */}
                  <div className="mt-6 p-4 bg-white rounded-lg border-2 border-yellow-500 shadow-lg">
                    <div className="text-center">
                      <h5 className="font-bold text-yellow-900 mb-2">🎯 AI DECISION ROUTER</h5>
                      <div className="text-xs text-gray-700">Context Analysis → Model Selection → Inference → Response</div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center my-4">
                    <div className="flex flex-col items-center">
                      <div className="w-1 h-8 bg-gradient-to-b from-orange-500 to-blue-500"></div>
                      <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-blue-500"></div>
                    </div>
                  </div>
                </div>

                {/* Layer 5: SAP Integration Layer */}
                <div className="mb-8 p-6 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border-2 border-blue-500">
                  <div className="text-center mb-4">
                    <Badge className="bg-blue-600 text-white px-4 py-2 text-sm">SAP INTEGRATION LAYER - 14 Modules</Badge>
                  </div>
                  
                  <div className="grid grid-cols-7 gap-3">
                    {[
                      { name: 'SAP PM/EAM', desc: 'Maintenance' },
                      { name: 'SAP GRC', desc: 'Compliance' },
                      { name: 'SAP CTRM', desc: 'Trading' },
                      { name: 'SAP JVA', desc: 'Partners' },
                      { name: 'SAP Sustain', desc: 'Carbon' },
                      { name: 'SAP MM', desc: 'Inventory' },
                      { name: 'SAP IBP', desc: 'Planning' },
                      { name: 'SAP BTP', desc: 'Platform' },
                      { name: 'SAP FI/CO', desc: 'Finance' },
                      { name: 'SAP SD', desc: 'Sales' },
                      { name: 'SAP PP', desc: 'Production' },
                      { name: 'SuccessFactors', desc: 'HR' },
                      { name: 'Ariba', desc: 'Procurement' },
                      { name: 'Datasphere', desc: 'Analytics' }
                    ].map((module, idx) => (
                      <div key={idx} className="p-3 bg-white border-2 border-blue-300 rounded-lg text-center hover:shadow-md transition-all">
                        <div className="text-xs font-bold text-blue-900">{module.name}</div>
                        <div className="text-xs text-gray-600 mt-1">{module.desc}</div>
                      </div>
                    ))}
                  </div>

                  {/* Bidirectional Sync Indicator */}
                  <div className="mt-4 flex justify-center">
                    <div className="p-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-full text-xs font-semibold shadow-lg">
                      ⬍⬍ Bidirectional Real-Time Sync ⬍⬍
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center my-4">
                    <div className="flex flex-col items-center">
                      <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-indigo-500"></div>
                      <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-indigo-500"></div>
                    </div>
                  </div>
                </div>

                {/* Layer 6: Digital Twin & Data Processing */}
                <div className="mb-8">
                  <div className="text-center mb-4">
                    <Badge className="bg-indigo-600 text-white px-4 py-2 text-sm">DATA PROCESSING & INTELLIGENCE LAYER</Badge>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    {/* Digital Twin Ecosystem */}
                    <div className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-500 rounded-lg shadow-xl">
                      <h5 className="font-bold text-indigo-900 mb-4 flex items-center gap-2">
                        <Globe className="h-6 w-6" />
                        Digital Twin Ecosystem
                      </h5>
                      
                      <div className="space-y-3">
                        <div className="p-3 bg-white rounded-lg border border-indigo-300">
                          <div className="font-semibold text-sm text-indigo-900 mb-2">Facility-Level Twins (7)</div>
                          <div className="space-y-1 text-xs text-gray-700">
                            <div>• North Sea Platform Alpha</div>
                            <div>• Gulf Coast Refinery Unit 1</div>
                            <div>• TransAtlantic Pipeline</div>
                            <div className="mt-2 pt-2 border-t border-indigo-200">
                              <strong>Data:</strong> Production (45K-125K bpd), Energy (89.3%), Emissions (CO2 tracking), Equipment (127-289 units)
                            </div>
                          </div>
                        </div>

                        <div className="flex justify-center text-indigo-500 text-xl">↕</div>

                        <div className="p-3 bg-white rounded-lg border border-indigo-300">
                          <div className="font-semibold text-sm text-indigo-900 mb-2">Equipment-Level Twins (18)</div>
                          <div className="space-y-1 text-xs text-gray-700">
                            <div>• Main Gas Compressor #3 <span className="text-red-600 font-bold">(68% ⚠️)</span></div>
                            <div>• Crude Oil Pump A-102 <span className="text-orange-600 font-bold">(72% ⚠️)</span></div>
                            <div>• + 16 other critical assets</div>
                            <div className="mt-2 pt-2 border-t border-indigo-200">
                              <strong>Monitoring:</strong> 8 sensors/equipment, RUL prediction (14-21 days), Failure modes (ML), 3D models
                            </div>
                          </div>
                        </div>

                        <div className="p-3 bg-indigo-100 rounded-lg">
                          <div className="text-xs text-center">
                            <div className="font-bold text-indigo-900">Real-time: 3,245 data points/sec</div>
                            <div className="text-gray-700">Prediction: 89.8% | Simulation: 94.5%</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* AI Decision Flow Engine */}
                    <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-500 rounded-lg shadow-xl">
                      <h5 className="font-bold text-purple-900 mb-4 flex items-center gap-2">
                        <Activity className="h-6 w-6" />
                        AI Decision Flow Engine
                      </h5>
                      
                      <div className="space-y-2">
                        {[
                          { step: '1', title: 'Query Analysis', desc: 'Intent classification, Entity extraction' },
                          { step: '2', title: 'AI Model Selection', desc: 'Complexity assessment, Domain match' },
                          { step: '3', title: 'AI Inference', desc: 'Prompt construction, Model invocation' },
                          { step: '4', title: 'Action Generation', desc: 'SAP work orders, Optimization recommendations' },
                          { step: '5', title: 'Response Delivery', desc: 'Format results, Confidence scores' }
                        ].map((item, idx) => (
                          <div key={idx}>
                            <div className="p-3 bg-white rounded-lg border-l-4 border-purple-500">
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs flex items-center justify-center font-bold">{item.step}</div>
                                <div>
                                  <div className="text-xs font-semibold text-purple-900">{item.title}</div>
                                  <div className="text-xs text-gray-600">{item.desc}</div>
                                </div>
                              </div>
                            </div>
                            {idx < 4 && <div className="flex justify-center text-purple-500">↓</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Arrow Down */}
                  <div className="flex justify-center my-4">
                    <div className="flex flex-col items-center">
                      <div className="w-1 h-8 bg-gradient-to-b from-indigo-500 to-green-500"></div>
                      <div className="w-0 h-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-green-500"></div>
                    </div>
                  </div>
                </div>

                {/* Layer 7: Data Persistence Layer */}
                <div className="mb-8 p-6 bg-gradient-to-r from-green-50 to-teal-50 rounded-lg border-2 border-green-500">
                  <div className="text-center mb-4">
                    <Badge className="bg-green-600 text-white px-4 py-2 text-sm">DATA PERSISTENCE LAYER</Badge>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    {/* MongoDB */}
                    <div className="p-4 bg-white rounded-lg border-2 border-green-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Database className="h-5 w-5 text-green-600" />
                        <h5 className="font-bold text-sm">MongoDB Atlas</h5>
                      </div>
                      <div className="space-y-1 text-xs text-gray-700">
                        <div>• Assets</div>
                        <div>• Maintenance</div>
                        <div>• Compliance</div>
                        <div>• CTRM Trades</div>
                        <div>• ESG Metrics</div>
                        <div>• Digital Twins</div>
                      </div>
                    </div>

                    {/* IoT Time-Series */}
                    <div className="p-4 bg-white rounded-lg border-2 border-blue-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Activity className="h-5 w-5 text-blue-600" />
                        <h5 className="font-bold text-sm">IoT Time-Series</h5>
                      </div>
                      <div className="space-y-1 text-xs text-gray-700">
                        <div>• Sensor Data</div>
                        <div>• 10,847 streams</div>
                        <div>• 3,245 DPS</div>
                        <div className="mt-2 pt-2 border-t">
                          <strong>Sources:</strong> SCADA, OSI PI, Honeywell
                        </div>
                      </div>
                    </div>

                    {/* SAP S/4HANA */}
                    <div className="p-4 bg-white rounded-lg border-2 border-purple-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Plug className="h-5 w-5 text-purple-600" />
                        <h5 className="font-bold text-sm">SAP S/4HANA</h5>
                      </div>
                      <div className="space-y-1 text-xs text-gray-700">
                        <div>• Master Data</div>
                        <div>• Transactions</div>
                        <div>• Work Orders</div>
                        <div>• Cost Centers</div>
                        <div className="mt-2 pt-2 border-t">
                          <strong>Sync:</strong> Real-time via OData
                        </div>
                      </div>
                    </div>

                    {/* External Sources */}
                    <div className="p-4 bg-white rounded-lg border-2 border-orange-400 shadow-md">
                      <div className="flex items-center gap-2 mb-3">
                        <Globe className="h-5 w-5 text-orange-600" />
                        <h5 className="font-bold text-sm">External Sources</h5>
                      </div>
                      <div className="space-y-1 text-xs text-gray-700">
                        <div>• Market Prices</div>
                        <div>• Weather Data</div>
                        <div>• Regulations</div>
                        <div>• News Feeds</div>
                        <div className="mt-2 pt-2 border-t">
                          <strong>APIs:</strong> REST/GraphQL
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Data Flow Examples */}
                <div className="mt-8">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-gray-900">🔄 Critical Data Flow Examples</h3>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Example 1: Predictive Maintenance Flow */}
                    <div className="p-5 bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-400 rounded-lg shadow-lg">
                      <h5 className="font-bold text-red-900 mb-3 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Flow 1: Predictive Maintenance Alert
                      </h5>
                      <div className="space-y-2">
                        {[
                          { icon: Activity, text: 'IoT Sensor: Vibration 14.2 mm/s', color: 'red' },
                          { icon: Globe, text: 'Digital Twin: Anomaly detected', color: 'orange' },
                          { icon: Target, text: 'AI: RUL prediction = 14 days (72% prob)', color: 'yellow' },
                          { icon: Cloud, text: 'AWS Bedrock Claude: Root cause analysis', color: 'orange' },
                          { icon: CheckCircle, text: 'Generate: SAP PM WO-45890 (High priority)', color: 'green' },
                          { icon: Bell, text: 'Notify: Engineer + Dashboard alert', color: 'blue' }
                        ].map((step, idx) => (
                          <div key={idx}>
                            <div className={`p-2 bg-white rounded-lg border-l-4 border-${step.color}-500 flex items-center gap-2`}>
                              <step.icon className={`h-4 w-4 text-${step.color}-600`} />
                              <div className="text-xs text-gray-800">{step.text}</div>
                            </div>
                            {idx < 5 && <div className="flex justify-center text-red-500 text-sm">↓</div>}
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 p-2 bg-green-100 rounded text-xs text-center font-semibold text-green-800">
                        ✓ Result: $6M catastrophic failure prevented
                      </div>
                    </div>

                    {/* Example 2: Drilling Optimization Flow */}
                    <div className="p-5 bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-400 rounded-lg shadow-lg">
                      <h5 className="font-bold text-blue-900 mb-3 flex items-center gap-2">
                        <Target className="h-5 w-5" />
                        Flow 2: AI Drilling Optimization
                      </h5>
                      <div className="space-y-2">
                        {[
                          { icon: MessageSquare, text: 'User: "Optimize drilling at 2,450m depth"', color: 'blue' },
                          { icon: Code, text: 'NLQ Handler: Intent = drilling_optimization', color: 'purple' },
                          { icon: Bot, text: 'Router: Select Claude 3 (drilling expertise)', color: 'orange' },
                          { icon: Cloud, text: 'Claude: Analyze ROP 10.5 m/hr (low)', color: 'orange' },
                          { icon: TrendingUp, text: 'AI Recommendation: WOB 23 MT, RPM 82', color: 'green' },
                          { icon: CheckCircle, text: 'Apply: ROP improved to 13.8 m/hr (+31%)', color: 'green' }
                        ].map((step, idx) => (
                          <div key={idx}>
                            <div className={`p-2 bg-white rounded-lg border-l-4 border-${step.color}-500 flex items-center gap-2`}>
                              <step.icon className={`h-4 w-4 text-${step.color}-600`} />
                              <div className="text-xs text-gray-800">{step.text}</div>
                            </div>
                            {idx < 5 && <div className="flex justify-center text-blue-500 text-sm">↓</div>}
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 p-2 bg-green-100 rounded text-xs text-center font-semibold text-green-800">
                        ✓ Result: $103K saved, 3.3 hours time reduction
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                    {/* Example 3: SAP Integration Pattern */}
                    <div className="p-5 bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-400 rounded-lg shadow-lg">
                      <h5 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
                        <Plug className="h-5 w-5" />
                        Flow 3: PetraMind → SAP PM Integration
                      </h5>
                      <div className="space-y-2">
                        {[
                          { text: 'Digital Twin predicts bearing failure', color: 'red' },
                          { text: 'Field mapping: equipment_id → EQUNR', color: 'purple' },
                          { text: 'Generate work order data (WO-45890)', color: 'blue' },
                          { text: 'POST to SAP PM OData API', color: 'purple' },
                          { text: 'SAP PM creates work order', color: 'green' },
                          { text: 'Sync log: 847ms, Status: Success', color: 'green' }
                        ].map((step, idx) => (
                          <div key={idx}>
                            <div className={`p-2 bg-white rounded-lg border-l-4 border-${step.color}-500`}>
                              <div className="text-xs text-gray-800">{step.text}</div>
                            </div>
                            {idx < 5 && <div className="flex justify-center text-purple-500 text-sm">↓</div>}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Example 4: Facility Optimization */}
                    <div className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-400 rounded-lg shadow-lg">
                      <h5 className="font-bold text-green-900 mb-3 flex items-center gap-2">
                        <Factory className="h-5 w-5" />
                        Flow 4: Facility AI Optimization
                      </h5>
                      <div className="space-y-2">
                        {[
                          { text: 'Load: North Sea Platform (45K bpd, 91.2% eff)', color: 'blue' },
                          { text: 'AI simulates 10,000+ parameter combos', color: 'purple' },
                          { text: 'Multi-objective: Production↑, Energy↓, CO2↓', color: 'orange' },
                          { text: 'Optimization: +12.4% production, -16.5% energy', color: 'green' },
                          { text: 'Recommend: Separator 465 psi, Flare reduction', color: 'green' },
                          { text: 'Financial: $15.5M annual benefit calculated', color: 'green' }
                        ].map((step, idx) => (
                          <div key={idx}>
                            <div className={`p-2 bg-white rounded-lg border-l-4 border-${step.color}-500`}>
                              <div className="text-xs text-gray-800">{step.text}</div>
                            </div>
                            {idx < 5 && <div className="flex justify-center text-green-500 text-sm">↓</div>}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Architecture Legend */}
                <div className="p-4 bg-gray-900 text-white rounded-lg">
                  <div className="grid grid-cols-4 gap-4 text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-blue-500 rounded"></div>
                      <span>Frontend/UI</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded"></div>
                      <span>Backend/API</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-purple-500 rounded"></div>
                      <span>AI/ML Services</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-orange-500 rounded"></div>
                      <span>External Integration</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xl">↓</div>
                      <span>Data Flow</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-xl">↕</div>
                      <span>Bidirectional Sync</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
                      <span>Warning/Critical</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 bg-green-500 rounded-full"></div>
                      <span>Operational</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Technology Stack Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Code className="h-5 w-5 text-blue-600" />
                    Technology Stack
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-semibold text-sm mb-2">Frontend Technologies</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2 bg-blue-50 rounded">React 18.2.0</div>
                        <div className="p-2 bg-blue-50 rounded">Three.js 0.160.0</div>
                        <div className="p-2 bg-blue-50 rounded">Tailwind CSS 3.4.0</div>
                        <div className="p-2 bg-blue-50 rounded">Axios HTTP Client</div>
                        <div className="p-2 bg-blue-50 rounded">React Three Fiber</div>
                        <div className="p-2 bg-blue-50 rounded">Lucide React Icons</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold text-sm mb-2">Backend Technologies</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2 bg-green-50 rounded">Python 3.11.7</div>
                        <div className="p-2 bg-green-50 rounded">FastAPI 0.104+</div>
                        <div className="p-2 bg-green-50 rounded">Pydantic 2.5+</div>
                        <div className="p-2 bg-green-50 rounded">Motor (Async MongoDB)</div>
                        <div className="p-2 bg-green-50 rounded">Uvicorn ASGI Server</div>
                        <div className="p-2 bg-green-50 rounded">Emergent Integrations</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold text-sm mb-2">AI/ML Technologies</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2 bg-orange-50 rounded">AWS Bedrock (Claude, Titan)</div>
                        <div className="p-2 bg-orange-50 rounded">SAP Joule AI Copilot</div>
                        <div className="p-2 bg-orange-50 rounded">OpenAI GPT-4o-mini</div>
                        <div className="p-2 bg-orange-50 rounded">Anthropic Claude Sonnet</div>
                        <div className="p-2 bg-orange-50 rounded">Google Gemini Flash</div>
                        <div className="p-2 bg-orange-50 rounded">Emergent LLM Gateway</div>
                      </div>
                    </div>

                    <div>
                      <h5 className="font-semibold text-sm mb-2">Integration Technologies</h5>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="p-2 bg-purple-50 rounded">SAP OData v4</div>
                        <div className="p-2 bg-purple-50 rounded">SAP BTP Cloud Foundry</div>
                        <div className="p-2 bg-purple-50 rounded">OAuth2 Authentication</div>
                        <div className="p-2 bg-purple-50 rounded">REST/GraphQL APIs</div>
                        <div className="p-2 bg-purple-50 rounded">SCADA Connectors</div>
                        <div className="p-2 bg-purple-50 rounded">Historian Integration</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-red-600" />
                    Security Architecture
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h5 className="font-semibold text-sm mb-2">Authentication & Authorization</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>✓ OAuth2 for SAP BTP and Joule integration</li>
                        <li>✓ JWT tokens for API authentication</li>
                        <li>✓ Role-based access control (RBAC)</li>
                        <li>✓ SAP user permission inheritance</li>
                        <li>✓ API key rotation (90-day policy)</li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-semibold text-sm mb-2">Data Security</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>✓ TLS 1.3 encryption in transit</li>
                        <li>✓ AES-256 encryption at rest</li>
                        <li>✓ Secrets management (AWS Secrets Manager)</li>
                        <li>✓ Data masking for sensitive fields</li>
                        <li>✓ Audit logging (all API calls tracked)</li>
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-semibold text-sm mb-2">Network Security</h5>
                      <ul className="text-sm text-gray-700 space-y-1">
                        <li>✓ CORS with whitelist domains</li>
                        <li>✓ Rate limiting (1000 req/min)</li>
                        <li>✓ DDoS protection (Cloudflare)</li>
                        <li>✓ WAF rules for injection attacks</li>
                        <li>✓ Private VPC for SAP connectivity</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Data Flow Patterns */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Workflow className="h-5 w-5 text-green-600" />
                  Critical Data Flow Patterns
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="p-4 border-2 border-blue-300 rounded-lg">
                      <h5 className="font-semibold text-blue-900 mb-3">Real-Time Sensor Data Flow</h5>
                      <div className="text-xs space-y-2 font-mono bg-blue-50 p-3 rounded">
                        <div>1. IoT Sensors (10,847 sensors)</div>
                        <div className="ml-4">↓ 3,245 data points/second</div>
                        <div>2. SCADA/Historian (OSI PI, Honeywell)</div>
                        <div className="ml-4">↓ Protocol: OPC UA, Modbus</div>
                        <div>3. Digital Twin Sensor Fusion</div>
                        <div className="ml-4">↓ Data validation & normalization</div>
                        <div>4. Anomaly Detection AI</div>
                        <div className="ml-4">↓ Multi-parameter correlation</div>
                        <div>5. Decision: Normal vs. Alert</div>
                        <div className="ml-4">↓ IF anomaly detected</div>
                        <div>6. AWS Bedrock Claude Analysis</div>
                        <div className="ml-4">↓ Root cause identification</div>
                        <div>7. Dashboard Alert + SAP PM Work Order</div>
                      </div>
                    </div>

                    <div className="p-4 border-2 border-green-300 rounded-lg">
                      <h5 className="font-semibold text-green-900 mb-3">SAP Bidirectional Sync Flow</h5>
                      <div className="text-xs space-y-2 font-mono bg-green-50 p-3 rounded">
                        <div>1. PetraMind → SAP (Data Push)</div>
                        <div className="ml-4">├─ Equipment health scores</div>
                        <div className="ml-4">├─ Predictive alerts</div>
                        <div className="ml-4">├─ Digital twin predictions</div>
                        <div className="ml-4">└─ Carbon emissions data</div>
                        <div className="ml-4 mt-2">↓ Field Mapping Engine</div>
                        <div className="ml-4">↓ petra_field → sap_field</div>
                        <div>2. SAP Modules Receive Data</div>
                        <div className="ml-4">├─ SAP PM: Work orders created</div>
                        <div className="ml-4">├─ SAP GRC: Compliance status</div>
                        <div className="ml-4">└─ SAP CO: Carbon accounting</div>
                        <div className="mt-2">3. SAP → PetraMind (Data Pull)</div>
                        <div className="ml-4">├─ Master data (equipment, parts)</div>
                        <div className="ml-4">├─ Transaction data (POs, invoices)</div>
                        <div className="ml-4">└─ Work order status updates</div>
                        <div className="ml-4 mt-2">↓ Sync frequency: Real-time</div>
                        <div>4. MongoDB Update + Dashboard Refresh</div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 border-2 border-purple-300 rounded-lg">
                    <h5 className="font-semibold text-purple-900 mb-3">AI Decision Flow - Query Processing</h5>
                    <div className="text-xs space-y-2 font-mono bg-purple-50 p-3 rounded">
                      <div className="font-bold">User Query: "What are my top maintenance priorities?"</div>
                      <div className="mt-2">STEP 1: NLQ Handler - Intent Analysis</div>
                      <div className="ml-4">├─ Parse query: maintenance + priorities + current</div>
                      <div className="ml-4">├─ Detected intent: MAINTENANCE_PRIORITY_LIST</div>
                      <div className="ml-4">└─ Extract entities: time_frame = "current"</div>
                      
                      <div className="mt-2">STEP 2: AI Decision Router</div>
                      <div className="ml-4">├─ Check cache: Not found</div>
                      <div className="ml-4">├─ Assess complexity: MEDIUM (structured data query)</div>
                      <div className="ml-4">├─ Domain match: Maintenance</div>
                      <div className="ml-4">└─ Model selection: Emergent LLM (cost-effective for structured queries)</div>
                      
                      <div className="mt-2">STEP 3: Data Gathering</div>
                      <div className="ml-4">├─ MongoDB: Fetch all alerts (priority > 0.7)</div>
                      <div className="ml-4">├─ Digital Twin: Get equipment with RUL &lt; 30 days</div>
                      <div className="ml-4">└─ SAP PM: Check open work orders</div>
                      
                      <div className="mt-2">STEP 4: AI Processing</div>
                      <div className="ml-4">├─ Context: 3 alerts found, 2 equipment at risk, 5 open WOs</div>
                      <div className="ml-4">├─ Invoke: Emergent LLM with structured prompt</div>
                      <div className="ml-4">└─ Response time: 847ms</div>
                      
                      <div className="mt-2">STEP 5: Response Formatting</div>
                      <div className="ml-4">└─ Output: "Top 3 priorities: 1) Main Gas Compressor #3 (14 days, $6M risk),</div>
                      <div className="ml-8">2) Crude Oil Pump A-102 (21 days, $2M risk),</div>
                      <div className="ml-8">3) Heat Exchanger E-305 (45 days, $500K risk)"</div>
                      
                      <div className="mt-2 text-green-400">✓ Total processing time: 1,124ms | Confidence: 94.2%</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Infrastructure & Deployment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-indigo-600" />
                  Infrastructure & Deployment Architecture
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-indigo-50 rounded-lg">
                      <h5 className="font-semibold text-sm mb-2">Development Environment</h5>
                      <ul className="text-xs text-gray-700 space-y-1">
                        <li>• Local Docker Compose</li>
                        <li>• Hot reload enabled</li>
                        <li>• MongoDB local instance</li>
                        <li>• Mock SAP endpoints</li>
                        <li>• Postman API testing</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h5 className="font-semibold text-sm mb-2">Staging Environment</h5>
                      <ul className="text-xs text-gray-700 space-y-1">
                        <li>• Kubernetes cluster</li>
                        <li>• MongoDB Atlas (dev tier)</li>
                        <li>• SAP sandbox system</li>
                        <li>• AI model testing</li>
                        <li>• Load testing with Locust</li>
                      </ul>
                    </div>

                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h5 className="font-semibold text-sm mb-2">Production Environment</h5>
                      <ul className="text-xs text-gray-700 space-y-1">
                        <li>• Kubernetes (multi-region)</li>
                        <li>• MongoDB Atlas (production)</li>
                        <li>• SAP S/4HANA production</li>
                        <li>• AWS Bedrock (production)</li>
                        <li>• 99.9% SLA uptime</li>
                      </ul>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-semibold mb-3">Deployment Pipeline (CI/CD)</h5>
                    <div className="grid grid-cols-6 gap-2 text-center text-xs">
                      <div className="p-3 bg-blue-100 rounded">
                        <div className="font-bold">1. Code Commit</div>
                        <div className="text-gray-600 mt-1">Git push</div>
                      </div>
                      <div className="p-3 bg-green-100 rounded">
                        <div className="font-bold">2. Build</div>
                        <div className="text-gray-600 mt-1">Docker image</div>
                      </div>
                      <div className="p-3 bg-purple-100 rounded">
                        <div className="font-bold">3. Test</div>
                        <div className="text-gray-600 mt-1">Unit + E2E</div>
                      </div>
                      <div className="p-3 bg-orange-100 rounded">
                        <div className="font-bold">4. Security Scan</div>
                        <div className="text-gray-600 mt-1">Trivy, Snyk</div>
                      </div>
                      <div className="p-3 bg-yellow-100 rounded">
                        <div className="font-bold">5. Deploy</div>
                        <div className="text-gray-600 mt-1">K8s rollout</div>
                      </div>
                      <div className="p-3 bg-green-100 rounded">
                        <div className="font-bold">6. Monitor</div>
                        <div className="text-gray-600 mt-1">Health check</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scalability & Performance */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Scalability & Performance Characteristics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <h5 className="font-semibold mb-3">Horizontal Scaling</h5>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li>• <strong>Backend:</strong> Auto-scale 2-20 pods based on CPU (>70%)</li>
                      <li>• <strong>Frontend:</strong> CDN distribution (Cloudflare)</li>
                      <li>• <strong>Database:</strong> MongoDB sharding (16 shards)</li>
                      <li>• <strong>AI Services:</strong> Request queuing with auto-scaling</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-3">Performance Metrics</h5>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li>• <strong>API Response:</strong> &lt;100ms (p95)</li>
                      <li>• <strong>AI Inference:</strong> &lt;2s (Claude), &lt;1s (Titan)</li>
                      <li>• <strong>Database Query:</strong> &lt;50ms (indexed)</li>
                      <li>• <strong>3D Rendering:</strong> 60 FPS (CSS transforms)</li>
                      <li>• <strong>Sensor Throughput:</strong> 3,245 DPS sustained</li>
                    </ul>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-3">Caching Strategy</h5>
                    <ul className="text-sm text-gray-700 space-y-2">
                      <li>• <strong>Redis:</strong> AI responses (5 min TTL)</li>
                      <li>• <strong>Browser:</strong> Static assets (7 days)</li>
                      <li>• <strong>CDN:</strong> React bundle (30 days)</li>
                      <li>• <strong>SAP Data:</strong> 60 second cache for reads</li>
                      <li>• <strong>Digital Twin:</strong> Real-time (no cache)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Integration Patterns */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plug className="h-5 w-5 text-purple-600" />
                  SAP Integration Patterns & Data Synchronization
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="font-mono text-xs leading-relaxed">
                      <div className="font-bold mb-2">Pattern 1: PetraMind Predictive Maintenance → SAP PM Work Order Creation</div>
                      <div className="ml-4 space-y-1">
                        <div>① Digital Twin detects: Compressor vibration 14.2 mm/s (threshold: 10 mm/s)</div>
                        <div>② AI predicts: Bearing failure in 14 days (72% probability)</div>
                        <div>③ System generates work order data:</div>
                        <div className="ml-8">- Equipment: EQ-10001 (SAP functional location: ALPHA-DK-B-COMP-03)</div>
                        <div className="ml-8">- Order type: Predictive Maintenance</div>
                        <div className="ml-8">- Priority: High</div>
                        <div className="ml-8">- Parts: Bearing-KIT-001 (qty: 1), Seal-ASM-205 (qty: 2)</div>
                        <div>④ Field mapping: petra_field.equipment_id → sap_field.EQUNR</div>
                        <div>⑤ API call: POST to SAP PM OData service (/sap/opu/odata/sap/API_WORKORDER)</div>
                        <div>⑥ SAP PM creates: Work Order WO-45890</div>
                        <div>⑦ Sync log: Records synced: 1, Duration: 847ms, Status: Success</div>
                        <div className="text-green-400">✓ Work order visible in SAP PM and PetraMind dashboard</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <div className="font-mono text-xs leading-relaxed">
                      <div className="font-bold mb-2">Pattern 2: SAP Master Data → PetraMind Equipment Catalog</div>
                      <div className="ml-4 space-y-1">
                        <div>① SAP PM master data change (new equipment EQ-10999 added)</div>
                        <div>② SAP BTP event trigger: Equipment created in functional location</div>
                        <div>③ PetraMind sync manager polls SAP PM API (every 60 seconds)</div>
                        <div>④ Detect change: New equipment found (EQ-10999)</div>
                        <div>⑤ Field mapping reverse: sap_field.EQUNR → petra_field.equipment_id</div>
                        <div>⑥ Create digital twin:</div>
                        <div className="ml-8">- Initialize equipment twin</div>
                        <div className="ml-8">- Set up sensor monitoring (8 sensors)</div>
                        <div className="ml-8">- Create baseline health profile (100%)</div>
                        <div>⑦ MongoDB insert: New asset document</div>
                        <div>⑧ Dashboard update: Asset count +1</div>
                        <div className="text-green-400">✓ SAP equipment now monitored in PetraMind with digital twin</div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <div className="font-mono text-xs leading-relaxed">
                      <div className="font-bold mb-2">Pattern 3: ESG Carbon Data → SAP Sustainability → SAP CO Cost Centers</div>
                      <div className="ml-4 space-y-1">
                        <div>① PetraMind tracks real-time emissions:</div>
                        <div className="ml-8">- Scope 1: 125.4 tons CO2/day (North Sea Platform)</div>
                        <div className="ml-8">- Scope 2: 45.2 tons CO2/day (energy consumption)</div>
                        <div>② AI forecasts 90-day emissions: 11,286 tons CO2 (Q1 total)</div>
                        <div>③ User clicks "Sync ESG Data with SAP"</div>
                        <div>④ Field mapping: petra_emissions → sap_sustainability.carbon_footprint</div>
                        <div>⑤ API POST to SAP Sustainability module</div>
                        <div>⑥ SAP Sustainability receives carbon data → allocates to cost centers:</div>
                        <div className="ml-8">- Cost Center CC-1001 (Refinery): 4,849 tons CO2</div>
                        <div className="ml-8">- Cost Center CC-1002 (Platform): 1,198 tons CO2</div>
                        <div>⑦ SAP CO creates financial postings: Carbon tax liability ($15-$50/ton)</div>
                        <div>⑧ PetraMind updates ESG dashboard: "2 ESG Metrics Synced to SAP"</div>
                        <div className="text-green-400">✓ Carbon accounting now in SAP financial reports</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'product-guide':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Complete Product Guide</h2>
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">PetraMind Platform Overview</h3>
                <p className="text-gray-700">
                  Comprehensive guide to all modules, features, and capabilities in the PetraMind AI-powered 
                  enterprise platform for Oil & Gas operations. Navigate through core, scale, enterprise, and advanced AI/ML modules.
                </p>
              </div>
            </div>

            {/* Core Modules */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5 text-blue-600" />
                  Core Modules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Settings className="h-5 w-5 text-blue-500" />
                      Predictive Maintenance
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      AI-powered equipment monitoring with 90% failure prediction accuracy
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Real-time sensor monitoring (10,000+ sensors)</div>
                      <div>✓ Anomaly detection & failure prediction</div>
                      <div>✓ SAP PM/EAM integration for work orders</div>
                      <div>✓ Automated root cause analysis</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Shield className="h-5 w-5 text-green-500" />
                      Compliance Management
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      Automated regulatory tracking with GenAI audit reporting
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Real-time compliance monitoring</div>
                      <div>✓ SAP GRC integration</div>
                      <div>✓ AI-powered audit trail analysis</div>
                      <div>✓ Automated regulatory report generation</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <MessageSquare className="h-5 w-5 text-purple-500" />
                      AI Assistant
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      24/7 conversational AI for operational queries and insights
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Natural language query interface</div>
                      <div>✓ Multi-turn conversation support</div>
                      <div>✓ Context-aware recommendations</div>
                      <div>✓ Cross-module data synthesis</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scale Modules */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                  Scale Modules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <LineChart className="h-5 w-5 text-orange-500" />
                      CTRM Trading
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      Commodity Trading & Risk Management with AI market analysis
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Real-time market exposure tracking</div>
                      <div>✓ AI-powered price forecasting</div>
                      <div>✓ Risk analytics dashboard</div>
                      <div>✓ SAP CTRM integration with field mappings</div>
                      <div>✓ Automated hedge recommendations</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Handshake className="h-5 w-5 text-teal-500" />
                      Joint Venture Accounting
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      Automated partner billing and AI-powered reconciliation
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Multi-partner transaction tracking</div>
                      <div>✓ Automated billing calculations</div>
                      <div>✓ AI reconciliation engine</div>
                      <div>✓ SAP JVA integration</div>
                      <div>✓ Cash call automation</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Leaf className="h-5 w-5 text-green-600" />
                      ESG Intelligence
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      Sustainability tracking with carbon forecasting & SAP integration
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Real-time Scope 1/2/3 emissions tracking</div>
                      <div>✓ 90-day carbon forecasting AI</div>
                      <div>✓ SAP Sustainability module integration</div>
                      <div>✓ Automated CDP, TCFD, GRI reporting</div>
                      <div>✓ Carbon accounting in SAP CO</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Enterprise Modules */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Factory className="h-5 w-5 text-indigo-600" />
                  Enterprise Modules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <MapPin className="h-5 w-5 text-blue-500" />
                      Field Operations
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      Real-time field asset management and operational coordination
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Field asset health monitoring</div>
                      <div>✓ Work order management</div>
                      <div>✓ Mobile crew coordination</div>
                      <div>✓ Equipment location tracking</div>
                      <div>✓ AI-powered task prioritization</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <HardHat className="h-5 w-5 text-red-500" />
                      Workforce Safety
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      Predictive safety analytics and incident prevention
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ Real-time hazard detection</div>
                      <div>✓ 24-48hr incident prediction</div>
                      <div>✓ PPE compliance monitoring (computer vision)</div>
                      <div>✓ Permit-to-work AI validation</div>
                      <div>✓ Safety training recommendations</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Globe className="h-5 w-5 text-blue-500" />
                      Digital Twin Manager
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      Comprehensive digital twin ecosystem for equipment and facility optimization
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ 25 digital twins (18 equipment + 7 facilities)</div>
                      <div>✓ Real-time monitoring (10,847 sensors, 3,245 DPS)</div>
                      <div>✓ Physics-based simulation & what-if analysis</div>
                      <div>✓ 3D visualization with Three.js</div>
                      <div>✓ AI-powered optimization (production, energy, emissions)</div>
                      <div>✓ SAP PM integration for predictive work orders</div>
                      <div>✓ 94.5% simulation accuracy, 89.8% prediction accuracy</div>
                    </div>
                  </div>

                  <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                    <h5 className="font-semibold flex items-center gap-2 mb-2">
                      <Truck className="h-5 w-5 text-purple-500" />
                      Supply Chain Orchestration
                    </h5>
                    <p className="text-sm text-gray-600 mb-3">
                      AI-driven supply chain optimization and inventory management
                    </p>
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>✓ AI demand forecasting (predictive maintenance-based)</div>
                      <div>✓ SAP MM/Ariba integration</div>
                      <div>✓ Supplier performance optimization</div>
                      <div>✓ Logistics route optimization</div>
                      <div>✓ 25% inventory cost reduction</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Advanced AI/ML & SAP Integration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-orange-600" />
                  Advanced AI/ML & SAP Integration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                      <h5 className="font-semibold flex items-center gap-2 mb-2">
                        <Bot className="h-5 w-5 text-blue-500" />
                        AI Studio
                      </h5>
                      <p className="text-sm text-gray-600 mb-3">
                        Centralized AI model management with 9 active models across 3 providers
                      </p>
                      <div className="text-xs text-gray-500 space-y-1 mb-3">
                        <div>✓ Model performance analytics (50.1/100 avg score)</div>
                        <div>✓ Prompt engineering lab</div>
                        <div>✓ Multi-model comparison testing</div>
                        <div>✓ Cost optimization ($0 last 30 days on Emergent LLM)</div>
                      </div>
                      <div className="mt-3 pt-3 border-t">
                        <div className="font-medium text-sm mb-2">NEW: External AI Integrations</div>
                        <div className="text-xs text-gray-500 space-y-1">
                          <div>→ <strong>AWS Bedrock:</strong> Claude 3 Sonnet & Titan models</div>
                          <div>→ <strong>SAP Joule:</strong> 4 interaction modes, OAuth2 auth</div>
                          <div>→ <strong>BTP Deployment:</strong> Cloud Foundry automation</div>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                      <h5 className="font-semibold flex items-center gap-2 mb-2">
                        <Plug className="h-5 w-5 text-green-500" />
                        SAP Integration Manager
                      </h5>
                      <p className="text-sm text-gray-600 mb-3">
                        Unified hub for managing 14 SAP module integrations across 8 categories
                      </p>
                      <div className="text-xs text-gray-500 space-y-1">
                        <div>✓ <strong>S/4HANA:</strong> MM, PP, SD, FI, CO, PM modules</div>
                        <div>✓ <strong>IBP:</strong> Demand & Supply Planning</div>
                        <div>✓ <strong>BTP:</strong> Integration Suite & Analytics</div>
                        <div>✓ <strong>Others:</strong> SuccessFactors, Ariba, Datasphere, EAM, OTC</div>
                        <div>✓ Bidirectional sync with field mapping</div>
                        <div>✓ Module testing framework (5 test types)</div>
                        <div>✓ Performance metrics & sync logs</div>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h5 className="font-semibold text-sm mb-2">Anomaly Detection</h5>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>→ Real-time anomaly detection across assets</div>
                        <div>→ Multi-parameter correlation analysis</div>
                        <div>→ Severity scoring & prioritization</div>
                      </div>
                    </div>

                    <div className="p-4 bg-green-50 rounded-lg">
                      <h5 className="font-semibold text-sm mb-2">Predictive Models</h5>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>→ Custom ML model training</div>
                        <div>→ Production forecasting (30-90 days)</div>
                        <div>→ Equipment RUL prediction</div>
                      </div>
                    </div>

                    <div className="p-4 bg-purple-50 rounded-lg">
                      <h5 className="font-semibold text-sm mb-2">Cost Optimization</h5>
                      <div className="text-xs text-gray-600 space-y-1">
                        <div>→ OPEX analysis & recommendations</div>
                        <div>→ Energy consumption optimization</div>
                        <div>→ Procurement cost reduction AI</div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* IoT & Industry-Specific Modules */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-600" />
                  IoT, Automation & Industry-Specific Modules
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <h5 className="font-semibold mb-3">IoT & Automation</h5>
                    <div className="space-y-3">
                      <div className="p-3 border rounded-lg">
                        <div className="font-medium text-sm flex items-center gap-2">
                          <Activity className="h-4 w-4 text-blue-500" />
                          IoT Management
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Device simulation, real-time sensor readings, automated data ingestion from SCADA/historians
                        </div>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <div className="font-medium text-sm flex items-center gap-2">
                          <Workflow className="h-4 w-4 text-green-500" />
                          Automated Workflows
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          AI-driven workflow creation, approval routing, task automation, integration orchestration
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h5 className="font-semibold mb-3">Industry-Specific Operations</h5>
                    <div className="space-y-3">
                      <div className="p-3 border rounded-lg">
                        <div className="font-medium text-sm flex items-center gap-2">
                          <Target className="h-4 w-4 text-orange-500" />
                          Drilling Operations
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          ROP/WOB/RPM optimization, stuck pipe prediction, formation analysis, AWS Bedrock Claude integration
                        </div>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <div className="font-medium text-sm flex items-center gap-2">
                          <Activity className="h-4 w-4 text-purple-500" />
                          Pipeline Monitoring
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          Leak detection, corrosion prediction, flow optimization, integrity management
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SAP Integration Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plug className="h-5 w-5 text-blue-600" />
                  SAP Integration Capabilities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3">SAP Module</th>
                          <th className="text-left p-3">Integration Type</th>
                          <th className="text-left p-3">PetraMind Feature</th>
                          <th className="text-left p-3">AI Enhancement</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP PM/EAM</td>
                          <td className="p-3">Bidirectional sync</td>
                          <td className="p-3">Predictive Maintenance</td>
                          <td className="p-3">90% failure prediction accuracy</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP GRC</td>
                          <td className="p-3">Real-time monitoring</td>
                          <td className="p-3">Compliance Management</td>
                          <td className="p-3">Automated audit trail analysis</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP CTRM</td>
                          <td className="p-3">Field mapping sync</td>
                          <td className="p-3">CTRM Trading</td>
                          <td className="p-3">AI price forecasting & risk analysis</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP JVA</td>
                          <td className="p-3">Transaction sync</td>
                          <td className="p-3">Joint Ventures</td>
                          <td className="p-3">Automated reconciliation engine</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP Sustainability</td>
                          <td className="p-3">Carbon data sync</td>
                          <td className="p-3">ESG Intelligence</td>
                          <td className="p-3">90-day carbon forecasting</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP MM</td>
                          <td className="p-3">Inventory sync</td>
                          <td className="p-3">Supply Chain</td>
                          <td className="p-3">AI demand forecasting</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP IBP</td>
                          <td className="p-3">Planning integration</td>
                          <td className="p-3">Supply Chain</td>
                          <td className="p-3">Demand/supply optimization</td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 font-medium">SAP BTP</td>
                          <td className="p-3">Cloud services</td>
                          <td className="p-3">Platform Deployment</td>
                          <td className="p-3">Scalable infrastructure & Joule integration</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Key Features Summary */}
            <Card className="border-2 border-blue-200">
              <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-blue-600" />
                  Platform Capabilities Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">13</div>
                    <div className="text-sm text-gray-700 mt-1">Active Modules</div>
                    <div className="text-xs text-gray-600">Core, Scale, Enterprise</div>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">14</div>
                    <div className="text-sm text-gray-700 mt-1">SAP Integrations</div>
                    <div className="text-xs text-gray-600">S/4HANA, IBP, BTP, More</div>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">3</div>
                    <div className="text-sm text-gray-700 mt-1">AI Platform Integrations</div>
                    <div className="text-xs text-gray-600">Bedrock, Joule, Emergent</div>
                  </div>
                  <div className="p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">9</div>
                    <div className="text-sm text-gray-700 mt-1">AI Models</div>
                    <div className="text-xs text-gray-600">OpenAI, Anthropic, Gemini</div>
                  </div>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 border rounded-lg">
                    <h5 className="font-semibold mb-2">Data Processing Capabilities</h5>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div>✓ 10,000+ sensors monitored in real-time</div>
                      <div>✓ 9.1K AI requests processed (96.9% success rate)</div>
                      <div>✓ Real-time SAP data synchronization</div>
                      <div>✓ Streaming data from historians & SCADA</div>
                    </div>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <h5 className="font-semibold mb-2">Automation & AI Features</h5>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div>✓ 50% reduction in manual tasks</div>
                      <div>✓ 24/7 AI-powered insights & recommendations</div>
                      <div>✓ Automated report generation (compliance, ESG, financial)</div>
                      <div>✓ Natural language query interface</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      case 'user-guide':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">User Guide</h2>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Getting Started</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Initial Setup</h4>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold">1</div>
                        <div>
                          <h5 className="font-medium">Account Activation</h5>
                          <p className="text-sm text-gray-600">Receive login credentials and activate your account</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold">2</div>
                        <div>
                          <h5 className="font-medium">SAP Integration</h5>
                          <p className="text-sm text-gray-600">Connect your SAP system using provided credentials</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-semibold">3</div>
                        <div>
                          <h5 className="font-medium">Data Synchronization</h5>
                          <p className="text-sm text-gray-600">Initial data sync and AI model training (24-48 hours)</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">User Roles & Permissions</h4>
                    <div className="space-y-3">
                      <div className="p-3 border rounded-lg">
                        <h5 className="font-medium">Executive</h5>
                        <p className="text-sm text-gray-600">Full dashboard access, strategic insights, high-level reports</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <h5 className="font-medium">Operations Manager</h5>
                        <p className="text-sm text-gray-600">Operational modules, maintenance scheduling, compliance tracking</p>
                      </div>
                      <div className="p-3 border rounded-lg">
                        <h5 className="font-medium">Field Technician</h5>
                        <p className="text-sm text-gray-600">Asset monitoring, work orders, mobile access</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Common Workflows</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Predictive Maintenance Workflow</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <Activity className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                        <h5 className="font-medium text-sm">Monitor Assets</h5>
                        <p className="text-xs text-gray-600">Real-time health tracking</p>
                      </div>
                      <div className="p-3 bg-yellow-50 rounded-lg text-center">
                        <AlertTriangle className="h-6 w-6 mx-auto text-yellow-600 mb-2" />
                        <h5 className="font-medium text-sm">AI Alert</h5>
                        <p className="text-xs text-gray-600">Predictive failure detected</p>
                      </div>
                      <div className="p-3 bg-purple-50 rounded-lg text-center">
                        <Settings className="h-6 w-6 mx-auto text-purple-600 mb-2" />
                        <h5 className="font-medium text-sm">Schedule Work</h5>
                        <p className="text-xs text-gray-600">Create maintenance order</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <CheckCircle className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-sm">Execute & Close</h5>
                        <p className="text-xs text-gray-600">Complete maintenance task</p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">AI Assistant Usage</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h5 className="font-medium mb-3">Sample Questions:</h5>
                      <div className="space-y-2">
                        <div className="p-2 bg-white rounded border-l-4 border-blue-500">
                          <p className="text-sm">"What are my top maintenance priorities this week?"</p>
                        </div>
                        <div className="p-2 bg-white rounded border-l-4 border-green-500">
                          <p className="text-sm">"Show me compliance items due in the next 30 days"</p>
                        </div>
                        <div className="p-2 bg-white rounded border-l-4 border-purple-500">
                          <p className="text-sm">"Analyze the cost impact of delaying maintenance on Asset XYZ"</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bot className="h-5 w-5 text-orange-600" />
                  AI/ML Integration Workflows
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {/* AWS Bedrock Workflow */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">AWS Bedrock Integration Workflow</h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <Cloud className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                        <h5 className="font-medium text-xs">Navigate to AI Studio</h5>
                        <p className="text-xs text-gray-600">Click AI Studio in sidebar</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <Settings className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                        <h5 className="font-medium text-xs">Select AWS Bedrock Tab</h5>
                        <p className="text-xs text-gray-600">Access model configuration</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <Code className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                        <h5 className="font-medium text-xs">Configure Model</h5>
                        <p className="text-xs text-gray-600">Set AWS region & IAM role</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <MessageSquare className="h-6 w-6 mx-auto text-orange-600 mb-2" />
                        <h5 className="font-medium text-xs">Choose Use Case</h5>
                        <p className="text-xs text-gray-600">Drilling, Seismic, or Maintenance</p>
                      </div>
                      <div className="p-3 bg-orange-50 rounded-lg text-center">
                        <CheckCircle className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Invoke Model</h5>
                        <p className="text-xs text-gray-600">Get AI-powered insights</p>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-medium mb-2">Example: Drilling Analysis</h5>
                      <div className="text-sm text-gray-700 space-y-1">
                        <div>1. Select <strong>Claude 3 Sonnet</strong> model (best for complex analysis)</div>
                        <div>2. Choose <strong>Drilling Analysis</strong> system prompt</div>
                        <div>3. Input current drilling parameters: ROP, WOB, RPM, ECD</div>
                        <div>4. Receive AI assessment: parameter evaluation, anomaly detection, optimization recommendations</div>
                        <div>5. Apply suggestions to drilling operations → 20% ROP improvement</div>
                      </div>
                    </div>
                  </div>

                  {/* SAP Joule Workflow */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">SAP Joule AI Copilot Workflow</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <Bot className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                        <h5 className="font-medium text-xs">Access Joule</h5>
                        <p className="text-xs text-gray-600">AI Studio → SAP Joule tab</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <MessageSquare className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                        <h5 className="font-medium text-xs">Choose Interaction Mode</h5>
                        <p className="text-xs text-gray-600">Transactional/Analytical/etc</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <Send className="h-6 w-6 mx-auto text-blue-600 mb-2" />
                        <h5 className="font-medium text-xs">Ask Natural Language</h5>
                        <p className="text-xs text-gray-600">Type your business question</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <CheckCircle className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Receive Answer</h5>
                        <p className="text-xs text-gray-600">Get SAP-integrated insights</p>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h5 className="font-medium mb-2">Analytical Query Example</h5>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div><strong>User:</strong> "Analyze production efficiency trends for Q1 2025"</div>
                          <div className="mt-2"><strong>Joule:</strong> "Based on S/4HANA data, production shows positive trend. Key insights: 1) Production up 5%, 2) Costs decreased 3%, 3) Efficiency improved. Recommendation: Continue monitoring."</div>
                          <div className="mt-2 text-xs text-gray-600">→ Accessed modules: S/4HANA, IBP, BTP Analytics</div>
                        </div>
                      </div>
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h5 className="font-medium mb-2">Transactional Query Example</h5>
                        <div className="text-sm text-gray-700 space-y-1">
                          <div><strong>User:</strong> "Create a purchase order for drilling equipment worth $500,000"</div>
                          <div className="mt-2"><strong>Joule:</strong> "Transaction initiated: Processing CRUD operation in SAP S/4HANA. Status: Success. Reference ID: TXN-45782"</div>
                          <div className="mt-2 text-xs text-gray-600">→ Automated PO creation in SAP MM module</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SAP Integration Manager Workflow */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">SAP Integration Manager Workflow</h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <Plug className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Access Integrations</h5>
                        <p className="text-xs text-gray-600">Click Integrations menu</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <Database className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">View SAP Modules</h5>
                        <p className="text-xs text-gray-600">14 modules across 8 categories</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <TestTube className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Test Integration</h5>
                        <p className="text-xs text-gray-600">5 test types available</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <RefreshCw className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Sync Data</h5>
                        <p className="text-xs text-gray-600">Bidirectional sync with mapping</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <Eye className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Monitor Performance</h5>
                        <p className="text-xs text-gray-600">Uptime, sync logs, metrics</p>
                      </div>
                    </div>
                  </div>

                  {/* ESG Intelligence Workflow */}
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">ESG Intelligence & Carbon Management Workflow</h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <Leaf className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Access ESG Module</h5>
                        <p className="text-xs text-gray-600">Navigate to ESG Intelligence</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <Activity className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Track Emissions</h5>
                        <p className="text-xs text-gray-600">Real-time Scope 1/2/3 monitoring</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <TrendingUp className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Generate Forecast</h5>
                        <p className="text-xs text-gray-600">90-day carbon prediction</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <Plug className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">SAP Sustainability Sync</h5>
                        <p className="text-xs text-gray-600">Sync to SAP CO cost centers</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <FileText className="h-6 w-6 mx-auto text-green-600 mb-2" />
                        <h5 className="font-medium text-xs">Generate Reports</h5>
                        <p className="text-xs text-gray-600">CDP, TCFD, GRI compliance</p>
                      </div>
                    </div>
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <h5 className="font-medium mb-2">Business Value</h5>
                      <div className="text-sm text-gray-700">
                        20-25% emissions reduction saving $5M-$25M in carbon taxes annually. 
                        85% faster ESG reporting (3 months → 2 weeks). +15 points ESG score improvement.
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Best Practices</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Data Quality</h4>
                    <ul className="text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                        <span>Ensure SAP master data is complete and accurate</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                        <span>Regular data validation and cleansing</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
                        <span>Maintain consistent naming conventions</span>
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-3">Performance Optimization</h4>
                    <ul className="text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <TrendingUp className="h-4 w-4 text-blue-500 mt-0.5" />
                        <span>Review and act on AI recommendations daily</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="h-4 w-4 text-blue-500 mt-0.5" />
                        <span>Monitor prediction accuracy and provide feedback</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <TrendingUp className="h-4 w-4 text-blue-500 mt-0.5" />
                        <span>Customize dashboards for role-specific needs</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return <div>Select a documentation section</div>;
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Documentation</h1>
        <p className="text-gray-600">Comprehensive business and technical documentation for PetraMind</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Navigation Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <Card>
            <CardContent className="p-4">
              <nav className="space-y-2">
                {sections.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveSection(id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      activeSection === id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    data-testid={`doc-section-${id}`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Content Area */}
        <div className="flex-1">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

// ==================== ADVANCED AI/ML COMPONENTS ====================

// Real-time Anomaly Detection Component
const AnomalyDetection = () => {
  const [anomalies, setAnomalies] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    fetchAnomalies();
  }, []);

  const fetchAnomalies = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ai/anomalies`);
      setAnomalies(response.data);
    } catch (error) {
      console.error('Error fetching anomalies:', error);
    } finally {
      setLoading(false);
    }
  };

  const runAnomalyDetection = async () => {
    setScanning(true);
    try {
      const response = await axios.post(`${API}/ai/anomaly-detection`);
      setAssets([{ total_monitored: response.data.total_assets_monitored }]);
      await fetchAnomalies(); // Refresh anomalies list
    } catch (error) {
      console.error('Error running anomaly detection:', error);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">AI-Powered Anomaly Detection</h1>
        <p className="text-gray-600">Real-time monitoring and anomaly detection across all assets</p>
      </div>

      {/* Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Real-Time Monitoring Control
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Button 
              onClick={runAnomalyDetection} 
              disabled={scanning}
              className="flex items-center gap-2"
            >
              {scanning ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Scanning...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Run Anomaly Detection
                </>
              )}
            </Button>
            <div className="text-sm text-gray-500">
              {assets.length > 0 && `Monitoring ${assets[0]?.total_monitored} assets`}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Anomalies Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-2 text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading anomalies...
          </div>
        ) : anomalies.length === 0 ? (
          <div className="col-span-2 text-center py-8 text-gray-500">
            No anomalies detected. Click "Run Anomaly Detection" to scan assets.
          </div>
        ) : (
          anomalies.map((anomaly, index) => (
            <Card key={index} className={`border-l-4 ${
              anomaly.severity === 'critical' ? 'border-red-500' :
              anomaly.severity === 'high' ? 'border-orange-500' :
              anomaly.severity === 'medium' ? 'border-yellow-500' : 'border-gray-500'
            }`}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{anomaly.description}</CardTitle>
                  <Badge variant={
                    anomaly.severity === 'critical' ? 'destructive' :
                    anomaly.severity === 'high' ? 'destructive' :
                    anomaly.severity === 'medium' ? 'secondary' : 'outline'
                  }>
                    {anomaly.severity?.toUpperCase()}
                  </Badge>
                </div>
                <CardDescription>
                  {anomaly.anomaly_type} anomaly • Confidence: {(anomaly.confidence * 100).toFixed(1)}%
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Sensor Data</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(anomaly.sensor_data || {}).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="capitalize">{key.replace('_', ' ')}</span>
                          <span className="font-mono">{typeof value === 'number' ? value.toFixed(1) : value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Recommended Action:</strong> {anomaly.recommended_action}
                    </AlertDescription>
                  </Alert>
                  
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>Score: {(anomaly.anomaly_score * 100).toFixed(1)}%</span>
                    <span>Detected: {new Date(anomaly.detected_at).toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Predictive Models Management Component
const PredictiveModels = () => {
  const [models, setModels] = useState([]);
  const [training, setTraining] = useState(false);

  const trainModel = async (modelType) => {
    setTraining(true);
    try {
      const response = await axios.post(`${API}/ai/predictive-models/train?model_type=${modelType}`);
      alert(`Model training completed! Accuracy: ${(response.data.model_performance.accuracy * 100).toFixed(1)}%`);
    } catch (error) {
      console.error('Error training model:', error);
    } finally {
      setTraining(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Predictive AI Models</h1>
        <p className="text-gray-600">Train and manage AI models for equipment failure prediction and optimization</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { type: "equipment_failure", title: "Equipment Failure Prediction", icon: Settings, description: "Predict when equipment will fail before it happens" },
          { type: "cost_optimization", title: "Cost Optimization", icon: DollarSign, description: "Find opportunities to reduce operational costs" },
          { type: "production_forecast", title: "Production Forecasting", icon: TrendingUp, description: "Forecast production levels and optimize output" },
          { type: "safety_risk", title: "Safety Risk Assessment", icon: Shield, description: "Assess and predict safety risks across operations" }
        ].map((model) => (
          <Card key={model.type}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <model.icon className="w-5 h-5" />
                {model.title}
              </CardTitle>
              <CardDescription>{model.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                onClick={() => trainModel(model.type)} 
                disabled={training}
                className="w-full"
              >
                {training ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Training...
                  </>
                ) : (
                  <>
                    <Bot className="w-4 h-4 mr-2" />
                    Train Model
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Cost Optimization Analysis Component
const CostOptimization = () => {
  const [optimizations, setOptimizations] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);

  const runCostAnalysis = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai/cost-optimization/analyze`);
      setOptimizations(response.data.optimization_opportunities);
      setAnalysis(response.data);
    } catch (error) {
      console.error('Error running cost analysis:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Cost Optimization</h1>
          <p className="text-gray-600">Identify cost-saving opportunities using AI analysis</p>
        </div>
        <Button onClick={runCostAnalysis} disabled={loading}>
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Analyzing...
            </>
          ) : (
            <>
              <Target className="w-4 h-4 mr-2" />
              Run Analysis
            </>
          )}
        </Button>
      </div>

      {analysis && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Analysis Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  ${analysis.total_potential_savings?.toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Total Potential Savings</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {analysis.roi_analysis?.roi_percentage?.toFixed(1)}%
                </div>
                <div className="text-sm text-gray-500">ROI</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {analysis.roi_analysis?.payback_period_avg_months?.toFixed(0)} mo
                </div>
                <div className="text-sm text-gray-500">Avg Payback Period</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {optimizations.map((opt, index) => (
          <Card key={index}>
            <CardHeader>
              <CardTitle className="capitalize">{opt.optimization_category.replace('_', ' ')} Optimization</CardTitle>
              <CardDescription>{opt.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Current Cost:</span>
                    <div className="font-bold text-lg">${opt.current_cost?.toLocaleString()}</div>
                  </div>
                  <div>
                    <span className="text-gray-500">Potential Savings:</span>
                    <div className="font-bold text-lg text-green-600">${opt.potential_savings?.toLocaleString()}</div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <Badge variant={opt.implementation_difficulty === 'easy' ? 'default' : opt.implementation_difficulty === 'medium' ? 'secondary' : 'destructive'}>
                    {opt.implementation_difficulty} to implement
                  </Badge>
                  <span className="text-sm text-gray-500">{opt.payback_period_months} months payback</span>
                </div>
                
                <div>
                  <h4 className="font-medium mb-2">Implementation Steps:</h4>
                  <ul className="text-sm space-y-1">
                    {opt.implementation_steps?.map((step, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ==================== IOT & AUTOMATION COMPONENTS ====================

// IoT Device Management Component  
const IoTManagement = () => {
  const [devices, setDevices] = useState([]);
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/iot/devices`);
      setDevices(response.data);
    } catch (error) {
      console.error('Error fetching IoT devices:', error);
    } finally {
      setLoading(false);
    }
  };

  const simulateDevices = async () => {
    try {
      await axios.post(`${API}/iot/devices/simulate`);
      await fetchDevices();
      alert('IoT devices simulated successfully!');
    } catch (error) {
      console.error('Error simulating devices:', error);
    }
  };

  const generateReadings = async () => {
    try {
      const response = await axios.post(`${API}/iot/readings/generate`);
      alert(`Generated ${response.data.readings_generated} new sensor readings!`);
    } catch (error) {
      console.error('Error generating readings:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">IoT Device Management</h1>
        <p className="text-gray-600">Monitor and manage IoT sensors across all assets</p>
      </div>

      {/* Control Panel */}
      <Card>
        <CardHeader>
          <CardTitle>IoT Control Center</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <Button onClick={simulateDevices}>
              <Zap className="w-4 h-4 mr-2" />
              Simulate IoT Devices
            </Button>
            <Button onClick={generateReadings}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Generate Readings
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Devices Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-3 text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading devices...
          </div>
        ) : devices.length === 0 ? (
          <div className="col-span-3 text-center py-8 text-gray-500">
            No IoT devices found. Click "Simulate IoT Devices" to get started.
          </div>
        ) : (
          devices.map((device, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle className="text-lg">{device.device_name}</CardTitle>
                <CardDescription>{device.location}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span>Status:</span>
                    <Badge variant={device.status === 'active' ? 'default' : 'secondary'}>
                      {device.status}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Device Type:</span>
                    <span className="text-sm font-mono">{device.device_type}</span>
                  </div>
                  
                  {device.battery_level && (
                    <div className="flex items-center justify-between">
                      <span>Battery:</span>
                      <span className={`text-sm ${device.battery_level > 30 ? 'text-green-600' : 'text-red-600'}`}>
                        {device.battery_level.toFixed(1)}%
                      </span>
                    </div>
                  )}
                  
                  {device.signal_strength && (
                    <div className="flex items-center justify-between">
                      <span>Signal:</span>
                      <span className={`text-sm ${device.signal_strength > 70 ? 'text-green-600' : 'text-yellow-600'}`}>
                        {device.signal_strength.toFixed(1)}%
                      </span>
                    </div>
                  )}
                  
                  <div className="text-xs text-gray-500">
                    Last Reading: {device.last_reading ? new Date(device.last_reading).toLocaleString() : 'Never'}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Automated Workflows Component
const AutomatedWorkflows = () => {
  const [workflowName, setWorkflowName] = useState('');
  const [workflowType, setWorkflowType] = useState('maintenance');
  const [creating, setCreating] = useState(false);

  const createWorkflow = async () => {
    if (!workflowName.trim()) return;
    
    setCreating(true);
    try {
      const response = await axios.post(`${API}/automation/workflows/create?workflow_name=${encodeURIComponent(workflowName)}&workflow_type=${workflowType}`);
      alert(`Workflow "${workflowName}" created successfully!`);
      setWorkflowName('');
    } catch (error) {
      console.error('Error creating workflow:', error);
    } finally {
      setCreating(false);
    }
  };

  const workflowTypes = [
    { value: 'maintenance', label: 'Predictive Maintenance', description: 'Automate maintenance scheduling and alerts' },
    { value: 'compliance', label: 'Compliance Monitoring', description: 'Automated regulatory compliance tracking' },
    { value: 'safety', label: 'Safety Management', description: 'Emergency response and safety protocols' },
    { value: 'production', label: 'Production Optimization', description: 'Optimize production workflows automatically' }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Automated Workflows</h1>
        <p className="text-gray-600">Create and manage automated business processes</p>
      </div>

      {/* Workflow Creation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Workflow className="w-5 h-5" />
            Create New Workflow
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Workflow Name:</label>
            <Input
              value={workflowName}
              onChange={(e) => setWorkflowName(e.target.value)}
              placeholder="Enter workflow name..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Workflow Type:</label>
            <select 
              value={workflowType} 
              onChange={(e) => setWorkflowType(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md"
            >
              {workflowTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>

          <Button onClick={createWorkflow} disabled={creating || !workflowName.trim()}>
            {creating ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Create Workflow
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Workflow Templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {workflowTypes.map((type) => (
          <Card key={type.value}>
            <CardHeader>
              <CardTitle>{type.label}</CardTitle>
              <CardDescription>{type.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button 
                variant="outline" 
                onClick={() => {
                  setWorkflowType(type.value);
                  setWorkflowName(`${type.label} - ${Date.now()}`);
                }}
                className="w-full"
              >
                Use This Template
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ==================== INDUSTRY-SPECIFIC COMPONENTS ====================

// Drilling Operations Management Component
const DrillingOperationsManagement = () => {
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOperation, setNewOperation] = useState({
    well_name: '',
    rig_id: '',
    location: '',
    target_depth: ''
  });

  useEffect(() => {
    fetchOperations();
  }, []);

  const fetchOperations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/drilling/operations`);
      setOperations(response.data);
    } catch (error) {
      console.error('Error fetching drilling operations:', error);
    } finally {
      setLoading(false);
    }
  };

  const createOperation = async () => {
    try {
      const response = await axios.post(`${API}/drilling/operations/create?well_name=${encodeURIComponent(newOperation.well_name)}&rig_id=${encodeURIComponent(newOperation.rig_id)}&location=${encodeURIComponent(newOperation.location)}&target_depth=${newOperation.target_depth}`);
      await fetchOperations();
      setNewOperation({ well_name: '', rig_id: '', location: '', target_depth: '' });
      alert('Drilling operation created successfully!');
    } catch (error) {
      console.error('Error creating drilling operation:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Drilling Operations Management</h1>
        <p className="text-gray-600">Monitor and manage drilling operations in real-time</p>
      </div>

      {/* Create New Operation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5" />
            Create New Drilling Operation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Well Name (e.g., Alpha-8H)"
              value={newOperation.well_name}
              onChange={(e) => setNewOperation({...newOperation, well_name: e.target.value})}
            />
            <Input
              placeholder="Rig ID (e.g., RIG-003)"
              value={newOperation.rig_id}
              onChange={(e) => setNewOperation({...newOperation, rig_id: e.target.value})}
            />
            <Input
              placeholder="Location (e.g., Permian Basin, TX)"
              value={newOperation.location}
              onChange={(e) => setNewOperation({...newOperation, location: e.target.value})}
            />
            <Input
              placeholder="Target Depth (meters)"
              type="number"
              value={newOperation.target_depth}
              onChange={(e) => setNewOperation({...newOperation, target_depth: e.target.value})}
            />
          </div>
          <Button 
            onClick={createOperation} 
            className="mt-4"
            disabled={!newOperation.well_name || !newOperation.target_depth}
          >
            Create Operation
          </Button>
        </CardContent>
      </Card>

      {/* Operations List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="col-span-2 text-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
            Loading operations...
          </div>
        ) : operations.length === 0 ? (
          <div className="col-span-2 text-center py-8 text-gray-500">
            No drilling operations found. Create one to get started.
          </div>
        ) : (
          operations.map((operation, index) => (
            <Card key={index}>
              <CardHeader>
                <CardTitle>{operation.well_name}</CardTitle>
                <CardDescription>{operation.location}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span>Status:</span>
                    <Badge variant={operation.drilling_status === 'drilling' ? 'default' : 'secondary'}>
                      {operation.drilling_status}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span>Progress:</span>
                    <span>{((operation.current_depth / operation.target_depth) * 100).toFixed(1)}%</span>
                  </div>
                  
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{ width: `${(operation.current_depth / operation.target_depth) * 100}%` }}
                    ></div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>Current Depth: {operation.current_depth?.toFixed(0)}m</div>
                    <div>Target Depth: {operation.target_depth?.toFixed(0)}m</div>
                    <div>Drilling Rate: {operation.drilling_rate?.toFixed(1)} m/hr</div>
                    <div>Formation: {operation.formation_type}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Pipeline Monitoring Component
const PipelineMonitoringSystem = () => {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newPipeline, setNewPipeline] = useState({
    pipeline_name: '',
    segment_id: '',
    length_km: '',
    diameter_inches: ''
  });

  const createPipelineMonitoring = async () => {
    try {
      await axios.post(`${API}/pipeline/monitoring/create?pipeline_name=${encodeURIComponent(newPipeline.pipeline_name)}&segment_id=${encodeURIComponent(newPipeline.segment_id)}&length_km=${newPipeline.length_km}&diameter_inches=${newPipeline.diameter_inches}`);
      setNewPipeline({ pipeline_name: '', segment_id: '', length_km: '', diameter_inches: '' });
      alert('Pipeline monitoring created successfully!');
    } catch (error) {
      console.error('Error creating pipeline monitoring:', error);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Pipeline Monitoring System</h1>
        <p className="text-gray-600">Real-time pipeline integrity and flow monitoring</p>
      </div>

      {/* Create Pipeline Monitoring */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Add Pipeline Monitoring
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Pipeline Name"
              value={newPipeline.pipeline_name}
              onChange={(e) => setNewPipeline({...newPipeline, pipeline_name: e.target.value})}
            />
            <Input
              placeholder="Segment ID"
              value={newPipeline.segment_id}
              onChange={(e) => setNewPipeline({...newPipeline, segment_id: e.target.value})}
            />
            <Input
              placeholder="Length (km)"
              type="number"
              value={newPipeline.length_km}
              onChange={(e) => setNewPipeline({...newPipeline, length_km: e.target.value})}
            />
            <Input
              placeholder="Diameter (inches)"
              type="number"
              value={newPipeline.diameter_inches}
              onChange={(e) => setNewPipeline({...newPipeline, diameter_inches: e.target.value})}
            />
          </div>
          <Button 
            onClick={createPipelineMonitoring} 
            className="mt-4"
            disabled={!newPipeline.pipeline_name || !newPipeline.length_km}
          >
            Add Monitoring
          </Button>
        </CardContent>
      </Card>

      <div className="text-center py-8 text-gray-500">
        Pipeline monitoring data will be displayed here once configured.
      </div>
    </div>
  );
};

// Regulatory Compliance Automation Component  
const RegulatoryComplianceAutomation = () => {
  const [complianceItems, setComplianceItems] = useState([]);
  const [scanning, setScanning] = useState(false);

  const scanCompliance = async () => {
    setScanning(true);
    try {
      const response = await axios.post(`${API}/compliance/regulatory/scan`);
      setComplianceItems(response.data.compliance_items);
    } catch (error) {
      console.error('Error scanning compliance:', error);
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Regulatory Compliance Automation</h1>
          <p className="text-gray-600">Automated regulatory compliance monitoring and reporting</p>
        </div>
        <Button onClick={scanCompliance} disabled={scanning}>
          {scanning ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Scanning...
            </>
          ) : (
            <>
              <Shield className="w-4 h-4 mr-2" />
              Scan Compliance
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {complianceItems.map((item, index) => (
          <Card key={index} className={`border-l-4 ${
            item.current_status === 'compliant' ? 'border-green-500' :
            item.current_status === 'action_required' ? 'border-red-500' : 'border-yellow-500'
          }`}>
            <CardHeader>
              <CardTitle>{item.regulation_title}</CardTitle>
              <CardDescription>
                {item.regulation_code} • {item.jurisdiction} • {item.compliance_category}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span>Status:</span>
                  <Badge variant={item.current_status === 'compliant' ? 'default' : 'destructive'}>
                    {item.current_status.replace('_', ' ')}
                  </Badge>
                </div>
                
                <div className="flex items-center justify-between">
                  <span>Risk Level:</span>
                  <Badge variant={item.risk_rating === 'low' ? 'outline' : item.risk_rating === 'medium' ? 'secondary' : 'destructive'}>
                    {item.risk_rating}
                  </Badge>
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Responsible:</strong> {item.responsible_person}
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Next Review:</strong> {new Date(item.next_review_date).toLocaleDateString()}
                </div>
                
                <div className="text-sm text-gray-600">
                  <strong>Penalties:</strong> {item.penalties_if_non_compliant}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Environmental Impact Assessment Component
const EnvironmentalImpactAssessment = () => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);

  const runAssessment = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/environmental/impact/assess`);
      setAssessments(response.data.assessments);
    } catch (error) {
      console.error('Error running environmental assessment:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Environmental Impact Assessment</h1>
          <p className="text-gray-600">Comprehensive environmental monitoring and impact analysis</p>
        </div>
        <Button onClick={runAssessment} disabled={loading}>
          {loading ? (
            <>
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Assessing...
            </>
          ) : (
            <>
              <Leaf className="w-4 h-4 mr-2" />
              Run Assessment
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {assessments.map((assessment, index) => (
          <Card key={index} className={`border-l-4 ${
            assessment.compliance_status === 'compliant' ? 'border-green-500' :
            assessment.compliance_status === 'warning' ? 'border-yellow-500' : 'border-red-500'
          }`}>
            <CardHeader>
              <CardTitle className="text-lg">{assessment.assessment_name}</CardTitle>
              <CardDescription>{assessment.assessment_type.replace('_', ' ')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span>Status:</span>
                  <Badge variant={assessment.compliance_status === 'compliant' ? 'default' : 'secondary'}>
                    {assessment.compliance_status}
                  </Badge>
                </div>
                
                <div className="text-sm">
                  <div className="flex justify-between mb-1">
                    <span>Current vs Target:</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>Current: {assessment.current_value?.toFixed(1)}</div>
                    <div>Target: {assessment.target_value?.toFixed(1)}</div>
                  </div>
                </div>
                
                <div className="text-sm">
                  <span className={`font-medium ${assessment.improvement_percentage > 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {assessment.improvement_percentage > 0 ? '+' : ''}{assessment.improvement_percentage?.toFixed(1)}% change
                  </span>
                </div>
                
                <div className="text-xs text-gray-500">
                  Monitoring: {assessment.monitoring_frequency}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// ==================== ADVANCED ANALYTICS COMPONENTS ====================

// Executive Command Center Component
const ExecutiveCommandCenter = () => {
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [sapDashboard, setSapDashboard] = useState(null);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedView, setSelectedView] = useState('overview');

  useEffect(() => {
    fetchCommandCenterData();
  }, []);

  const fetchCommandCenterData = async () => {
    try {
      const [metricsResponse, alertsResponse, sapResponse, insightsResponse] = await Promise.all([
        axios.get(`${API}/dashboard/metrics`),
        axios.get(`${API}/maintenance/alerts`),
        axios.get(`${API}/sap/dashboard`),
        axios.get(`${API}/executive/insights`)
      ]);
      setMetrics(metricsResponse.data);
      setAlerts(alertsResponse.data.filter(alert => !alert.resolved));
      setSapDashboard(sapResponse.data);
      setInsights(insightsResponse.data);
    } catch (error) {
      console.error('Error fetching command center data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading Executive Command Center...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Executive Command Center</h1>
        <p className="text-gray-600">Real-time operational oversight across all SAP-integrated modules</p>
      </div>

      {/* View Selector */}
      <Tabs value={selectedView} onValueChange={setSelectedView} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Enterprise Overview</TabsTrigger>
          <TabsTrigger value="sap-integration">SAP Integration Health</TabsTrigger>
          <TabsTrigger value="ai-insights">AI-Powered Insights</TabsTrigger>
          <TabsTrigger value="cross-module">Cross-Module Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Critical Alerts Section */}
          <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Critical Alerts ({alerts.filter(a => a.severity === 'critical').length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {alerts.filter(a => a.severity === 'critical').length > 0 ? (
            <div className="space-y-3">
              {alerts.filter(a => a.severity === 'critical').slice(0, 5).map((alert) => (
                <Alert key={alert.id} className="border-red-200">
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <div className="flex-1">
                    <div className="font-semibold">{alert.title}</div>
                    <div className="text-sm text-gray-600">{alert.description}</div>
                  </div>
                  <Badge className="bg-red-100 text-red-800">Critical</Badge>
                </Alert>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
              <p>No critical alerts at this time</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Executive KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Operational Status</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {metrics?.uptime_percentage?.toFixed(1) || 0}%
            </div>
            <p className="text-xs text-muted-foreground">System uptime</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Risk Level</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {alerts.filter(a => a.severity === 'high' || a.severity === 'critical').length}
            </div>
            <p className="text-xs text-muted-foreground">High/Critical risks</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Efficiency</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {metrics?.operational_efficiency?.toFixed(1) || 0}%
            </div>
            <p className="text-xs text-muted-foreground">Operational efficiency</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cost Savings</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              ${metrics?.predicted_savings?.toLocaleString() || '0'}
            </div>
            <p className="text-xs text-muted-foreground">Predicted annual savings</p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Executive Actions</CardTitle>
          <CardDescription>Quick access to critical management functions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button className="h-20 flex flex-col gap-2">
              <FileText className="h-6 w-6" />
              <span className="text-sm">Generate Report</span>
            </Button>
            <Button className="h-20 flex flex-col gap-2" variant="outline">
              <Users className="h-6 w-6" />
              <span className="text-sm">Team Status</span>
            </Button>
            <Button className="h-20 flex flex-col gap-2" variant="outline">
              <BarChart3 className="h-6 w-6" />
              <span className="text-sm">Analytics</span>
            </Button>
            <Button className="h-20 flex flex-col gap-2" variant="outline">
              <Settings className="h-6 w-6" />
              <span className="text-sm">Settings</span>
            </Button>
          </div>
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Executive Reports Component
const ExecutiveReports = () => {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reportData, setReportData] = useState(null);

  const reportTemplates = [
    {
      id: 'operational-summary',
      name: 'Operational Summary Report',
      description: 'Comprehensive overview of operational performance',
      frequency: 'Weekly',
      lastGenerated: '2024-01-15T10:00:00Z'
    },
    {
      id: 'financial-performance',
      name: 'Financial Performance Report',
      description: 'Cost analysis and financial metrics',
      frequency: 'Monthly',
      lastGenerated: '2024-01-01T09:00:00Z'
    },
    {
      id: 'compliance-status',
      name: 'Compliance Status Report',
      description: 'Regulatory compliance and audit findings',
      frequency: 'Quarterly',
      lastGenerated: '2023-12-31T17:00:00Z'
    },
    {
      id: 'predictive-insights',
      name: 'Predictive Analytics Report',
      description: 'AI-driven forecasts and recommendations',
      frequency: 'Daily',
      lastGenerated: '2024-01-15T06:00:00Z'
    }
  ];

  const generateReport = async (reportId) => {
    setGenerating(true);
    try {
      // Simulate report generation
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockReportData = {
        id: reportId,
        generatedAt: new Date().toISOString(),
        metrics: {
          operational_efficiency: 89.6,
          cost_savings: 2543210.50,
          compliance_score: 94.2,
          safety_incidents: 2,
          environmental_score: 87.4
        },
        insights: [
          "Operational efficiency improved 3.2% over last quarter",
          "Predictive maintenance reduced unplanned downtime by 22%",
          "Cost optimization initiatives delivered $2.5M in savings",
          "Environmental compliance maintained above 85% threshold"
        ],
        recommendations: [
          "Increase preventive maintenance frequency for Pump-003",
          "Consider energy efficiency upgrades for Facility-B",
          "Review supplier contracts for potential cost reductions"
        ]
      };
      
      setReportData(mockReportData);
      setSelectedReport(reportId);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Executive Reports</h1>
        <p className="text-gray-600">Generate and manage comprehensive business reports</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Report Templates */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Available Reports</h2>
          {reportTemplates.map((report) => (
            <Card key={report.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{report.name}</span>
                  <Badge variant="outline">{report.frequency}</Badge>
                </CardTitle>
                <CardDescription>{report.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Last generated: {new Date(report.lastGenerated).toLocaleString()}
                  </div>
                  <Button 
                    onClick={() => generateReport(report.id)}
                    disabled={generating}
                    size="sm"
                  >
                    {generating ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-2" />
                        Generate
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Report Preview */}
        <div>
          <h2 className="text-xl font-semibold mb-4">Report Preview</h2>
          {reportData ? (
            <Card>
              <CardHeader>
                <CardTitle>
                  {reportTemplates.find(r => r.id === selectedReport)?.name}
                </CardTitle>
                <CardDescription>
                  Generated: {new Date(reportData.generatedAt).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Key Metrics */}
                <div>
                  <h3 className="font-semibold mb-3">Key Metrics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(reportData.metrics).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-3 rounded">
                        <div className="text-sm text-gray-500 capitalize">
                          {key.replace('_', ' ')}
                        </div>
                        <div className="text-lg font-bold">
                          {typeof value === 'number' && value > 1000 
                            ? `$${value.toLocaleString()}` 
                            : typeof value === 'number' 
                            ? `${value}${key.includes('score') || key.includes('efficiency') ? '%' : ''}`
                            : value
                          }
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Insights */}
                <div>
                  <h3 className="font-semibold mb-3">Key Insights</h3>
                  <ul className="space-y-2">
                    {reportData.insights.map((insight, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                        <span className="text-sm">{insight}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Recommendations */}
                <div>
                  <h3 className="font-semibold mb-3">Recommendations</h3>
                  <ul className="space-y-2">
                    {reportData.recommendations.map((rec, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Target className="w-4 h-4 text-blue-500 mt-1 flex-shrink-0" />
                        <span className="text-sm">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button size="sm">
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button variant="outline" size="sm">
                    <Send className="w-4 h-4 mr-2" />
                    Share Report
                  </Button>
                  <Button variant="outline" size="sm">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p>Select a report template to generate and preview</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Advanced Dashboards Component
const AdvancedDashboards = () => {
  const [dashboards, setDashboards] = useState([]);
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const dashboardTemplates = [
    {
      id: 'operations-kpi',
      name: 'Operations KPI Dashboard',
      description: 'Key operational performance indicators',
      widgets: ['efficiency_chart', 'asset_health', 'alerts_summary', 'cost_trends']
    },
    {
      id: 'safety-metrics',
      name: 'Safety Metrics Dashboard',
      description: 'Comprehensive safety performance tracking',
      widgets: ['incident_tracking', 'safety_scores', 'compliance_status', 'training_status']
    },
    {
      id: 'financial-overview',
      name: 'Financial Overview Dashboard',
      description: 'Financial performance and cost analysis',
      widgets: ['cost_breakdown', 'savings_tracking', 'budget_variance', 'roi_analysis']
    },
    {
      id: 'predictive-analytics',
      name: 'Predictive Analytics Dashboard',
      description: 'AI-powered predictions and forecasts',
      widgets: ['failure_predictions', 'cost_forecasts', 'demand_predictions', 'optimization_opportunities']
    }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Advanced Dashboards</h1>
          <p className="text-gray-600">Create and customize interactive dashboards</p>
        </div>
        <Button onClick={() => setIsEditing(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Create Dashboard
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {dashboardTemplates.map((dashboard) => (
          <Card key={dashboard.id} className="cursor-pointer hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle>{dashboard.name}</CardTitle>
              <CardDescription>{dashboard.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <span className="text-sm font-medium text-gray-500">Widgets:</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {dashboard.widgets.map((widget, index) => (
                      <Badge key={index} variant="outline" className="text-xs">
                        {widget.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" className="flex-1">
                    <Eye className="w-4 h-4 mr-2" />
                    View
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Data Visualization Component
const DataVisualization = () => {
  const [visualizations, setVisualizations] = useState([]);
  const [selectedViz, setSelectedViz] = useState('asset_performance');

  const vizOptions = [
    { id: 'asset_performance', label: 'Asset Performance Trends', type: 'line_chart' },
    { id: 'cost_breakdown', label: 'Cost Breakdown Analysis', type: 'pie_chart' },
    { id: 'compliance_matrix', label: 'Compliance Status Matrix', type: 'heatmap' },
    { id: 'predictive_forecasts', label: 'Predictive Forecasts', type: 'forecast_chart' }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Data Visualization</h1>
        <p className="text-gray-600">Interactive charts and advanced analytics visualization</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Visualization Options */}
        <div className="lg:col-span-1">
          <h2 className="text-lg font-semibold mb-4">Visualizations</h2>
          <div className="space-y-2">
            {vizOptions.map((viz) => (
              <Button
                key={viz.id}
                variant={selectedViz === viz.id ? 'default' : 'outline'}
                onClick={() => setSelectedViz(viz.id)}
                className="w-full justify-start"
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                {viz.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Visualization Display */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>
                {vizOptions.find(v => v.id === selectedViz)?.label}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-96 bg-gradient-to-r from-blue-50 to-indigo-100 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="w-16 h-16 mx-auto mb-4 text-blue-500" />
                  <p className="text-lg font-medium text-gray-700">Interactive Visualization</p>
                  <p className="text-sm text-gray-500">
                    {vizOptions.find(v => v.id === selectedViz)?.label} would be displayed here
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Performance Analytics Component
const PerformanceAnalytics = () => {
  const [performanceData, setPerformanceData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('efficiency');
  const [timeRange, setTimeRange] = useState('7d');

  const metrics = [
    { id: 'efficiency', label: 'Operational Efficiency', unit: '%' },
    { id: 'downtime', label: 'Asset Downtime', unit: 'hours' },
    { id: 'throughput', label: 'Production Throughput', unit: 'units/day' },
    { id: 'quality', label: 'Quality Score', unit: '%' }
  ];

  const timeRanges = [
    { id: '1d', label: 'Last 24 Hours' },
    { id: '7d', label: 'Last 7 Days' },
    { id: '30d', label: 'Last 30 Days' },
    { id: '90d', label: 'Last 90 Days' }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Performance Analytics</h1>
        <p className="text-gray-600">Advanced performance metrics and trend analysis</p>
      </div>

      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Analytics Configuration</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Metric</label>
              <select 
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
                className="border border-gray-300 rounded-md p-2"
              >
                {metrics.map(metric => (
                  <option key={metric.id} value={metric.id}>{metric.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Time Range</label>
              <select 
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="border border-gray-300 rounded-md p-2"
              >
                {timeRanges.map(range => (
                  <option key={range.id} value={range.id}>{range.label}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric) => (
          <Card key={metric.id}>
            <CardHeader>
              <CardTitle className="text-lg">{metric.label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {metric.id === 'efficiency' ? '89.6' :
                   metric.id === 'downtime' ? '12.3' :
                   metric.id === 'throughput' ? '2,847' :
                   '94.2'}
                  <span className="text-lg text-gray-500 ml-1">{metric.unit}</span>
                </div>
                <div className="text-sm text-green-600">
                  ↑ {Math.floor(Math.random() * 10 + 1)}% vs last period
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trend Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Trend Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-gradient-to-r from-green-50 to-blue-100 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <TrendingUp className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <p className="text-lg font-medium text-gray-700">Performance Trend Chart</p>
              <p className="text-sm text-gray-500">
                {metrics.find(m => m.id === selectedMetric)?.label} trends over {timeRanges.find(r => r.id === timeRange)?.label.toLowerCase()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== ENTERPRISE MANAGEMENT COMPONENTS ====================

// User Management Component
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const mockUsers = [
    { id: 1, name: 'John Smith', email: 'john.smith@company.com', role: 'Admin', status: 'Active', lastLogin: '2024-01-15T14:30:00Z' },
    { id: 2, name: 'Sarah Johnson', email: 'sarah.j@company.com', role: 'Operator', status: 'Active', lastLogin: '2024-01-15T12:15:00Z' },
    { id: 3, name: 'Mike Chen', email: 'mike.chen@company.com', role: 'Analyst', status: 'Active', lastLogin: '2024-01-14T16:45:00Z' },
    { id: 4, name: 'Lisa Anderson', email: 'lisa.a@company.com', role: 'Viewer', status: 'Inactive', lastLogin: '2024-01-10T09:30:00Z' }
  ];

  useEffect(() => {
    setUsers(mockUsers);
  }, []);

  const roleColors = {
    'Admin': 'bg-red-100 text-red-800',
    'Operator': 'bg-blue-100 text-blue-800',
    'Analyst': 'bg-green-100 text-green-800',
    'Viewer': 'bg-gray-100 text-gray-800'
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">User Management</h1>
          <p className="text-gray-600">Manage users, roles, and access permissions</p>
        </div>
        <Button onClick={() => setShowAddUser(true)}>
          <UserPlus className="w-4 h-4 mr-2" />
          Add User
        </Button>
      </div>

      {/* User Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Users</p>
                <p className="text-2xl font-bold">{users.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Users</p>
                <p className="text-2xl font-bold">{users.filter(u => u.status === 'Active').length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Administrators</p>
                <p className="text-2xl font-bold">{users.filter(u => u.role === 'Admin').length}</p>
              </div>
              <Shield className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Recent Logins</p>
                <p className="text-2xl font-bold">
                  {users.filter(u => {
                    const lastLogin = new Date(u.lastLogin);
                    const now = new Date();
                    return (now - lastLogin) / (1000 * 60 * 60 * 24) <= 1;
                  }).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Directory</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Name</th>
                  <th className="text-left p-2">Email</th>
                  <th className="text-left p-2">Role</th>
                  <th className="text-left p-2">Status</th>
                  <th className="text-left p-2">Last Login</th>
                  <th className="text-left p-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50">
                    <td className="p-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        {user.name}
                      </div>
                    </td>
                    <td className="p-2 text-gray-600">{user.email}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[user.role]}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.status === 'Active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="p-2 text-gray-600 text-sm">
                      {new Date(user.lastLogin).toLocaleString()}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline">
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Role & Permissions Component
const RolePermissions = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);

  const mockRoles = [
    {
      id: 'admin',
      name: 'Administrator',
      description: 'Full system access and management capabilities',
      userCount: 2,
      permissions: {
        'assets': ['read', 'write', 'delete'],
        'analytics': ['read', 'write'],
        'users': ['read', 'write', 'delete'],
        'compliance': ['read', 'write'],
        'ai_features': ['read', 'write']
      }
    },
    {
      id: 'operator',
      name: 'Operator',
      description: 'Operational management and monitoring',
      userCount: 15,
      permissions: {
        'assets': ['read', 'write'],
        'analytics': ['read'],
        'compliance': ['read', 'write'],
        'ai_features': ['read']
      }
    },
    {
      id: 'analyst',
      name: 'Analyst',
      description: 'Data analysis and reporting access',
      userCount: 8,
      permissions: {
        'assets': ['read'],
        'analytics': ['read', 'write'],
        'compliance': ['read'],
        'ai_features': ['read', 'write']
      }
    },
    {
      id: 'viewer',
      name: 'Viewer',
      description: 'Read-only access to dashboards and reports',
      userCount: 5,
      permissions: {
        'assets': ['read'],
        'analytics': ['read'],
        'compliance': ['read']
      }
    }
  ];

  useEffect(() => {
    setRoles(mockRoles);
    setSelectedRole(mockRoles[0]);
  }, []);

  const permissionModules = {
    'assets': 'Asset Management',
    'analytics': 'Analytics & Reports',
    'users': 'User Management',
    'compliance': 'Compliance',
    'ai_features': 'AI Features'
  };

  const permissionLevels = {
    'read': { label: 'Read', color: 'bg-green-100 text-green-800' },
    'write': { label: 'Write', color: 'bg-yellow-100 text-yellow-800' },
    'delete': { label: 'Delete', color: 'bg-red-100 text-red-800' }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Roles & Permissions</h1>
        <p className="text-gray-600">Configure user roles and access permissions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Roles List */}
        <div>
          <h2 className="text-lg font-semibold mb-4">System Roles</h2>
          <div className="space-y-2">
            {roles.map((role) => (
              <Card 
                key={role.id} 
                className={`cursor-pointer transition-colors ${
                  selectedRole?.id === role.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                }`}
                onClick={() => setSelectedRole(role)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold">{role.name}</h3>
                    <Badge variant="outline">{role.userCount} users</Badge>
                  </div>
                  <p className="text-sm text-gray-600">{role.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Permissions Matrix */}
        <div className="lg:col-span-2">
          {selectedRole && (
            <Card>
              <CardHeader>
                <CardTitle>Permissions for {selectedRole.name}</CardTitle>
                <CardDescription>{selectedRole.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(permissionModules).map(([moduleId, moduleName]) => (
                    <div key={moduleId} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium">{moduleName}</h4>
                        <div className="flex gap-2">
                          {selectedRole.permissions[moduleId]?.map((permission) => (
                            <span 
                              key={permission}
                              className={`px-2 py-1 rounded text-xs font-medium ${permissionLevels[permission].color}`}
                            >
                              {permissionLevels[permission].label}
                            </span>
                          )) || <span className="text-gray-400 text-sm">No access</span>}
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-2">
                        {Object.entries(permissionLevels).map(([permId, permData]) => (
                          <label key={permId} className="flex items-center gap-2 text-sm">
                            <input 
                              type="checkbox" 
                              checked={selectedRole.permissions[moduleId]?.includes(permId) || false}
                              className="rounded"
                            />
                            {permData.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

// Audit Trails Component
const AuditTrails = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [dateRange, setDateRange] = useState('7d');

  const mockAuditLogs = [
    {
      id: 1,
      timestamp: '2024-01-15T14:30:00Z',
      user: 'John Smith',
      action: 'LOGIN',
      resource: 'System',
      details: 'Successful login from 192.168.1.100',
      severity: 'INFO'
    },
    {
      id: 2,
      timestamp: '2024-01-15T14:25:00Z',
      user: 'Sarah Johnson',
      action: 'UPDATE',
      resource: 'Asset (Pump-001)',
      details: 'Updated maintenance schedule',
      severity: 'INFO'
    },
    {
      id: 3,
      timestamp: '2024-01-15T14:20:00Z',
      user: 'Mike Chen',
      action: 'CREATE',
      resource: 'Report (Monthly Summary)',
      details: 'Generated monthly operational report',
      severity: 'INFO'
    },
    {
      id: 4,
      timestamp: '2024-01-15T14:15:00Z',
      user: 'System',
      action: 'ALERT',
      resource: 'Anomaly Detection',
      details: 'High vibration detected on Compressor-003',
      severity: 'WARNING'
    },
    {
      id: 5,
      timestamp: '2024-01-15T14:10:00Z',
      user: 'Lisa Anderson',
      action: 'FAILED_LOGIN',
      resource: 'System',
      details: 'Failed login attempt from 192.168.1.150',
      severity: 'ERROR'
    }
  ];

  useEffect(() => {
    setAuditLogs(mockAuditLogs);
  }, []);

  const severityColors = {
    'INFO': 'bg-blue-100 text-blue-800',
    'WARNING': 'bg-yellow-100 text-yellow-800',
    'ERROR': 'bg-red-100 text-red-800'
  };

  const actionTypes = ['all', 'LOGIN', 'UPDATE', 'CREATE', 'DELETE', 'ALERT', 'FAILED_LOGIN'];

  const filteredLogs = auditLogs.filter(log => {
    if (filterType === 'all') return true;
    return log.action === filterType;
  });

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Audit Trails</h1>
        <p className="text-gray-600">Track and monitor all system activities and changes</p>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Audit Logs</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Action Type</label>
              <select 
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="border border-gray-300 rounded-md p-2"
              >
                {actionTypes.map(type => (
                  <option key={type} value={type}>
                    {type === 'all' ? 'All Actions' : type}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Date Range</label>
              <select 
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="border border-gray-300 rounded-md p-2"
              >
                <option value="1d">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Log Entries ({filteredLogs.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Timestamp</th>
                  <th className="text-left p-2">User</th>
                  <th className="text-left p-2">Action</th>
                  <th className="text-left p-2">Resource</th>
                  <th className="text-left p-2">Details</th>
                  <th className="text-left p-2">Severity</th>
                </tr>
              </thead>
              <tbody>
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="border-b hover:bg-gray-50">
                    <td className="p-2 text-sm text-gray-600">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="p-2 font-medium">{log.user}</td>
                    <td className="p-2">
                      <span className="px-2 py-1 bg-gray-100 rounded text-xs font-mono">
                        {log.action}
                      </span>
                    </td>
                    <td className="p-2 text-sm">{log.resource}</td>
                    <td className="p-2 text-sm text-gray-600">{log.details}</td>
                    <td className="p-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${severityColors[log.severity]}`}>
                        {log.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// System Settings Component
const SystemSettings = () => {
  const [settings, setSettings] = useState({});
  const [activeTab, setActiveTab] = useState('general');

  const settingsTabs = [
    { id: 'general', label: 'General', icon: Settings },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'integrations', label: 'Integrations', icon: Plug }
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">System Settings</h1>
        <p className="text-gray-600">Configure system-wide settings and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings Navigation */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Settings Categories</h2>
          <div className="space-y-2">
            {settingsTabs.map((tab) => (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'outline'}
                onClick={() => setActiveTab(tab.id)}
                className="w-full justify-start"
              >
                <tab.icon className="w-4 h-4 mr-2" />
                {tab.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>
                {settingsTabs.find(tab => tab.id === activeTab)?.label} Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Organization Name</label>
                    <Input defaultValue="PetraMind Enterprise" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Timezone</label>
                    <select className="w-full border border-gray-300 rounded-md p-2">
                      <option>UTC-6 (Central Time)</option>
                      <option>UTC-5 (Eastern Time)</option>
                      <option>UTC-8 (Pacific Time)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Default Language</label>
                    <select className="w-full border border-gray-300 rounded-md p-2">
                      <option>English</option>
                      <option>Spanish</option>
                      <option>French</option>
                    </select>
                  </div>
                </div>
              )}
              
              {activeTab === 'security' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Two-Factor Authentication</h4>
                      <p className="text-sm text-gray-600">Require 2FA for all users</p>
                    </div>
                    <input type="checkbox" className="rounded" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Password Complexity</h4>
                      <p className="text-sm text-gray-600">Enforce strong password requirements</p>
                    </div>
                    <input type="checkbox" defaultChecked className="rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Session Timeout (minutes)</label>
                    <Input type="number" defaultValue="30" />
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Email Notifications</h4>
                      <p className="text-sm text-gray-600">Send notifications via email</p>
                    </div>
                    <input type="checkbox" defaultChecked className="rounded" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">SMS Alerts</h4>
                      <p className="text-sm text-gray-600">Send critical alerts via SMS</p>
                    </div>
                    <input type="checkbox" className="rounded" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">SMTP Server</label>
                    <Input defaultValue="smtp.company.com" />
                  </div>
                </div>
              )}

              {activeTab === 'integrations' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">SCADA Integration</h4>
                      <p className="text-sm text-gray-600">Connect to SCADA systems</p>
                    </div>
                    <Badge variant="outline">Connected</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">ERP Integration</h4>
                      <p className="text-sm text-gray-600">SAP/Oracle integration</p>
                    </div>
                    <Badge variant="secondary">Not Connected</Badge>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">API Rate Limit (requests/hour)</label>
                    <Input type="number" defaultValue="10000" />
                  </div>
                </div>
              )}

              <div className="pt-6 border-t">
                <Button>Save Settings</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// ==================== COLLABORATION & COMMUNICATION COMPONENTS ====================

// Team Collaboration Component
const TeamCollaboration = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  const mockProjects = [
    {
      id: 1,
      name: 'Q1 Maintenance Optimization',
      description: 'Optimize maintenance schedules for Q1 operations',
      status: 'In Progress',
      progress: 65,
      team: ['John Smith', 'Sarah Johnson', 'Mike Chen'],
      deadline: '2024-03-31',
      tasks: 12,
      completedTasks: 8
    },
    {
      id: 2,
      name: 'Compliance Audit Preparation',
      description: 'Prepare for upcoming regulatory compliance audit',
      status: 'Planning',
      progress: 25,
      team: ['Lisa Anderson', 'David Wilson'],
      deadline: '2024-02-15',
      tasks: 8,
      completedTasks: 2
    },
    {
      id: 3,
      name: 'Cost Reduction Initiative',
      description: 'Implement AI-driven cost optimization recommendations',
      status: 'Completed',
      progress: 100,
      team: ['Mike Chen', 'Sarah Johnson', 'Tom Brown'],
      deadline: '2024-01-15',
      tasks: 15,
      completedTasks: 15
    }
  ];

  useEffect(() => {
    setProjects(mockProjects);
  }, []);

  const statusColors = {
    'Planning': 'bg-yellow-100 text-yellow-800',
    'In Progress': 'bg-blue-100 text-blue-800',
    'Completed': 'bg-green-100 text-green-800'
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Team Collaboration</h1>
          <p className="text-gray-600">Manage projects, tasks, and team collaboration</p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Project Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Projects</p>
                <p className="text-2xl font-bold">{projects.filter(p => p.status !== 'Completed').length}</p>
              </div>
              <FolderOpen className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Team Members</p>
                <p className="text-2xl font-bold">12</p>
              </div>
              <Users className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending Tasks</p>
                <p className="text-2xl font-bold">{projects.reduce((sum, p) => sum + (p.tasks - p.completedTasks), 0)}</p>
              </div>
              <CheckSquare className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      <Card>
        <CardHeader>
          <CardTitle>Active Projects</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {projects.map((project) => (
              <div key={project.id} className="border rounded-lg p-4 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{project.name}</h3>
                    <p className="text-gray-600 text-sm">{project.description}</p>
                  </div>
                  <div className="text-right">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[project.status]}`}>
                      {project.status}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">Due: {new Date(project.deadline).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">Progress</p>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${project.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-sm mt-1">{project.progress}% complete</p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Tasks</p>
                    <p className="text-sm">
                      {project.completedTasks} / {project.tasks} completed
                    </p>
                  </div>

                  <div>
                    <p className="text-sm text-gray-500 mb-1">Team</p>
                    <div className="flex -space-x-2">
                      {project.team.map((member, index) => (
                        <div 
                          key={index}
                          className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-semibold border-2 border-white"
                          title={member}
                        >
                          {member.split(' ').map(n => n[0]).join('')}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Real-time Messaging Component
const RealTimeMessaging = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedChannel, setSelectedChannel] = useState('general');

  const channels = [
    { id: 'general', name: 'General', unread: 3 },
    { id: 'operations', name: 'Operations', unread: 0 },
    { id: 'maintenance', name: 'Maintenance', unread: 5 },
    { id: 'safety', name: 'Safety', unread: 1 }
  ];

  const mockMessages = [
    {
      id: 1,
      user: 'John Smith',
      message: 'The morning shift report is ready for review.',
      timestamp: '2024-01-15T14:30:00Z',
      channel: 'general'
    },
    {
      id: 2,
      user: 'Sarah Johnson',
      message: 'Pump-001 maintenance completed successfully. Back online.',
      timestamp: '2024-01-15T14:25:00Z',
      channel: 'maintenance'
    },
    {
      id: 3,
      user: 'Mike Chen',
      message: 'Anomaly detected in Sector 7. Investigating now.',
      timestamp: '2024-01-15T14:20:00Z',
      channel: 'operations'
    },
    {
      id: 4,
      user: 'Lisa Anderson',
      message: 'Safety drill scheduled for tomorrow at 10 AM.',
      timestamp: '2024-01-15T14:15:00Z',
      channel: 'safety'
    }
  ];

  useEffect(() => {
    setMessages(mockMessages);
  }, []);

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    const message = {
      id: messages.length + 1,
      user: 'Current User',
      message: newMessage,
      timestamp: new Date().toISOString(),
      channel: selectedChannel
    };
    
    setMessages([...messages, message]);
    setNewMessage('');
  };

  const channelMessages = messages.filter(msg => msg.channel === selectedChannel);

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Real-time Messaging</h1>
        <p className="text-gray-600">Communicate with your team in real-time</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Channels Sidebar */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Channels</h2>
          <div className="space-y-2">
            {channels.map((channel) => (
              <Button
                key={channel.id}
                variant={selectedChannel === channel.id ? 'default' : 'outline'}
                onClick={() => setSelectedChannel(channel.id)}
                className="w-full justify-between"
              >
                <span># {channel.name}</span>
                {channel.unread > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {channel.unread}
                  </Badge>
                )}
              </Button>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Hash className="w-5 h-5" />
                {channels.find(c => c.id === selectedChannel)?.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Messages */}
              <div className="h-96 overflow-y-auto mb-4 space-y-3">
                {channelMessages.map((message) => (
                  <div key={message.id} className="flex gap-3">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                      {message.user.split(' ').map(n => n[0]).join('')}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{message.user}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(message.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-sm">{message.message}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={`Message #${channels.find(c => c.id === selectedChannel)?.name}`}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1"
                />
                <Button onClick={sendMessage}>
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

// Notification Center Component
const NotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');

  const mockNotifications = [
    {
      id: 1,
      type: 'alert',
      title: 'High Vibration Detected',
      message: 'Compressor-003 showing abnormal vibration levels',
      timestamp: '2024-01-15T14:30:00Z',
      priority: 'high',
      read: false
    },
    {
      id: 2,
      type: 'maintenance',
      title: 'Scheduled Maintenance Complete',
      message: 'Pump-001 maintenance completed successfully',
      timestamp: '2024-01-15T14:15:00Z',
      priority: 'medium',
      read: true
    },
    {
      id: 3,
      type: 'compliance',
      title: 'Compliance Report Due',
      message: 'Environmental compliance report due in 7 days',
      timestamp: '2024-01-15T14:00:00Z',
      priority: 'medium',
      read: false
    },
    {
      id: 4,
      type: 'system',
      title: 'System Update Available',
      message: 'New features and improvements are ready to install',
      timestamp: '2024-01-15T13:45:00Z',
      priority: 'low',
      read: true
    }
  ];

  useEffect(() => {
    setNotifications(mockNotifications);
  }, []);

  const typeIcons = {
    'alert': AlertTriangle,
    'maintenance': Settings,
    'compliance': Shield,
    'system': Info
  };

  const priorityColors = {
    'high': 'border-l-red-500 bg-red-50',
    'medium': 'border-l-yellow-500 bg-yellow-50',
    'low': 'border-l-blue-500 bg-blue-50'
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notif.read;
    return notif.type === filter;
  });

  const markAsRead = (id) => {
    setNotifications(notifications.map(notif => 
      notif.id === id ? { ...notif, read: true } : notif
    ));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Notification Center</h1>
          <p className="text-gray-600">Stay informed about system events and alerts</p>
        </div>
        <Button variant="outline">
          <Settings className="w-4 h-4 mr-2" />
          Settings
        </Button>
      </div>

      {/* Notification Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold">{notifications.length}</p>
              </div>
              <Bell className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Unread</p>
                <p className="text-2xl font-bold">{notifications.filter(n => !n.read).length}</p>
              </div>
              <BellRing className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">High Priority</p>
                <p className="text-2xl font-bold">{notifications.filter(n => n.priority === 'high').length}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Alerts</p>
                <p className="text-2xl font-bold">{notifications.filter(n => n.type === 'alert').length}</p>
              </div>
              <Zap className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filter Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 flex-wrap">
            {['all', 'unread', 'alert', 'maintenance', 'compliance', 'system'].map((filterType) => (
              <Button
                key={filterType}
                variant={filter === filterType ? 'default' : 'outline'}
                onClick={() => setFilter(filterType)}
                size="sm"
              >
                {filterType === 'all' ? 'All' : filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <Card>
        <CardHeader>
          <CardTitle>Notifications ({filteredNotifications.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredNotifications.map((notification) => {
              const Icon = typeIcons[notification.type] || Bell;
              return (
                <div
                  key={notification.id}
                  className={`border-l-4 p-4 rounded-r-lg ${priorityColors[notification.priority]} ${
                    !notification.read ? 'font-medium' : 'opacity-75'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex gap-3 flex-1">
                      <Icon className="w-5 h-5 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <h4 className="font-semibold">{notification.title}</h4>
                        <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(notification.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {!notification.read && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => markAsRead(notification.id)}
                        >
                          Mark Read
                        </Button>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {notification.priority}
                      </Badge>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Knowledge Sharing Component
const KnowledgeSharing = () => {
  const [articles, setArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const mockArticles = [
    {
      id: 1,
      title: 'Best Practices for Predictive Maintenance',
      author: 'John Smith',
      category: 'Maintenance',
      views: 245,
      likes: 18,
      createdAt: '2024-01-10T10:00:00Z',
      excerpt: 'Learn how to implement effective predictive maintenance strategies...'
    },
    {
      id: 2,
      title: 'Anomaly Detection Troubleshooting Guide',
      author: 'Sarah Johnson',
      category: 'AI/ML',
      views: 189,
      likes: 12,
      createdAt: '2024-01-08T14:30:00Z',
      excerpt: 'Common issues and solutions when working with anomaly detection...'
    },
    {
      id: 3,
      title: 'Regulatory Compliance Checklist',
      author: 'Lisa Anderson',
      category: 'Compliance',
      views: 156,
      likes: 24,
      createdAt: '2024-01-05T09:15:00Z',
      excerpt: 'Essential steps to ensure regulatory compliance across operations...'
    }
  ];

  useEffect(() => {
    setArticles(mockArticles);
  }, []);

  const categories = ['All', 'Maintenance', 'AI/ML', 'Compliance', 'Safety', 'Operations'];

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Knowledge Sharing</h1>
          <p className="text-gray-600">Share expertise and learn from the community</p>
        </div>
        <Button>
          <Plus className="w-4 h-4 mr-2" />
          New Article
        </Button>
      </div>

      {/* Search and Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select className="border border-gray-300 rounded-md p-2">
              {categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Articles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((article) => (
          <Card key={article.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <Badge variant="outline">{article.category}</Badge>
                <span className="text-xs text-gray-500">
                  {new Date(article.createdAt).toLocaleDateString()}
                </span>
              </div>
              <CardTitle className="text-lg">{article.title}</CardTitle>
              <CardDescription>By {article.author}</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-600 mb-4">{article.excerpt}</p>
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {article.views}
                  </span>
                  <span className="flex items-center gap-1">
                    <Heart className="w-4 h-4" />
                    {article.likes}
                  </span>
                </div>
                <Button size="sm" variant="outline">
                  Read More
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = ({ currentPage, setCurrentPage }) => {
  // Top navigation items (only Dashboard, Analytics, Integrations)
  const topNavItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'integrations', label: 'Integrations', icon: Plug }
  ];

  // Sidebar navigation items (all other modules)
  const sidebarItems = [
    { id: 'maintenance', label: 'Predictive Maintenance', icon: Settings },
    { id: 'compliance', label: 'Compliance Management', icon: Shield },
    { id: 'ai-chat', label: 'AI Assistant', icon: MessageSquare },
    { id: 'documentation', label: 'Documentation', icon: FileText },
    { 
      category: 'Scale Modules', 
      items: [
        { id: 'ctrm', label: 'CTRM Trading', icon: LineChart },
        { id: 'jva', label: 'Joint Ventures', icon: Handshake },
        { id: 'esg', label: 'ESG Intelligence', icon: Leaf }
      ]
    },
    { 
      category: 'Enterprise Modules', 
      items: [
        { id: 'field-ops', label: 'Field Operations', icon: MapPin },
        { id: 'safety', label: 'Workforce Safety', icon: HardHat },
        { id: 'supply-chain', label: 'Supply Chain', icon: Truck },
        { id: 'digital-twin', label: 'Digital Twin Manager', icon: Globe },
        { id: 'ai-studio', label: 'AI Studio', icon: Bot }
      ]
    },
    { 
      category: 'Advanced AI/ML', 
      items: [
        { id: 'anomaly-detection', label: 'Anomaly Detection', icon: Zap },
        { id: 'predictive-models', label: 'Predictive Models', icon: Bot },
        { id: 'cost-optimization', label: 'Cost Optimization', icon: Target }
      ]
    },
    { 
      category: 'IoT & Automation', 
      items: [
        { id: 'iot-management', label: 'IoT Management', icon: Database },
        { id: 'automated-workflows', label: 'Automated Workflows', icon: Workflow }
      ]
    },
    { 
      category: 'Industry-Specific', 
      items: [
        { id: 'drilling-operations', label: 'Drilling Operations', icon: TestTube },
        { id: 'pipeline-monitoring', label: 'Pipeline Monitoring', icon: Activity },
        { id: 'regulatory-compliance', label: 'Regulatory Compliance', icon: Shield },
        { id: 'environmental-impact', label: 'Environmental Impact', icon: Leaf }
      ]
    },
    { 
      category: 'Advanced Analytics', 
      items: [
        { id: 'executive-command', label: 'Executive Command Center', icon: Target },
        { id: 'nlq-assistant', label: 'AI Query Assistant', icon: Bot },
        { id: 'predictive-analytics', label: 'Predictive Analytics', icon: TrendingUp },
        { id: 'custom-reporting', label: 'Executive Reports', icon: FileText },
        { id: 'advanced-dashboards', label: 'Advanced Dashboards', icon: PieChart },
        { id: 'data-visualization', label: 'Data Visualization', icon: BarChart3 },
        { id: 'performance-analytics', label: 'Performance Analytics', icon: LineChart }
      ]
    },
    {
      category: 'Digital Operations',
      items: [
        { id: 'iot-monitoring', label: 'IoT & Real-Time Monitoring', icon: Activity },
        { id: 'supply-chain', label: 'Supply Chain Orchestration', icon: Truck }
      ]
    },
    { 
      category: 'Enterprise Management', 
      items: [
        { id: 'user-management', label: 'User Management', icon: Users },
        { id: 'role-permissions', label: 'Role & Permissions', icon: Shield },
        { id: 'audit-trails', label: 'Audit Trails', icon: FileCheck },
        { id: 'system-settings', label: 'System Settings', icon: Settings },
        { id: 'integrations', label: 'Integrations Layer', icon: Plug },
        { id: 'sap-integration', label: 'SAP Integration', icon: Database }
      ]
    },
    { 
      category: 'Collaboration & Communication', 
      items: [
        { id: 'team-collaboration', label: 'Team Collaboration', icon: Users },
        { id: 'real-time-messaging', label: 'Real-time Messaging', icon: MessageCircle },
        { id: 'notification-center', label: 'Notification Center', icon: Bell },
        { id: 'knowledge-sharing', label: 'Knowledge Sharing', icon: BookOpen }
      ]
    }
  ];

  return (
    <>
      {/* Top Navigation */}
      <nav className="bg-white shadow-sm border-b ml-80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <h1 className="text-2xl font-bold text-blue-600">PetraMind</h1>
                <p className="text-xs text-gray-500">AI-First Oil & Gas Operations</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-8">
              {/* Top nav items */}
              {topNavItems.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setCurrentPage(id)}
                  className={`flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    currentPage === id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                  data-testid={`nav-${id}`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      {/* Left Sidebar - Pinned */}
      <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-xl z-50">
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center p-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">PetraMind Modules</h2>
          </div>

          {/* Sidebar Navigation */}
          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Core Modules */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Core Modules</h3>
              <div className="space-y-1">
                {sidebarItems.slice(0, 4).map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setCurrentPage(id)}
                    className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      currentPage === id
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                    data-testid={`sidebar-${id}`}
                  >
                    <Icon className="h-4 w-4 mr-3" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Categorized Modules */}
            {sidebarItems.slice(4).map(({ category, items }) => (
              <div key={category}>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">{category}</h3>
                <div className="space-y-1">
                  {items.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      onClick={() => setCurrentPage(id)}
                      className={`w-full flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        currentPage === id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                      data-testid={`sidebar-${id}`}
                    >
                      <Icon className="h-4 w-4 mr-3" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

    </>
  );
};

// Advanced Predictive Analytics Component
const PredictiveAnalytics = () => {
  const [predictions, setPredictions] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);

  const runPrediction = async (type) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/analytics/predict`, { type });
      setPredictions(res.data);
    } catch (error) {
      console.error('Error running prediction:', error);
    } finally {
      setLoading(false);
    }
  };

  const runForecast = async (metric) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/analytics/timeseries/forecast`, { metric, periods: 30 });
      setForecast(res.data);
    } catch (error) {
      console.error('Error running forecast:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Advanced Predictive Analytics</h1>
        <p className="text-gray-600">AI-powered forecasting and optimization</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Button onClick={() => runPrediction('maintenance_forecast')} disabled={loading}>
          <TrendingUp className="h-4 w-4 mr-2" />Maintenance Forecast
        </Button>
        <Button onClick={() => runPrediction('cost_optimization')} disabled={loading}>
          <DollarSign className="h-4 w-4 mr-2" />Cost Optimization
        </Button>
        <Button onClick={() => runPrediction('risk_forecast')} disabled={loading}>
          <AlertTriangle className="h-4 w-4 mr-2" />Risk Forecast
        </Button>
        <Button onClick={() => runPrediction('trading_optimization')} disabled={loading}>
          <LineChart className="h-4 w-4 mr-2" />Trading Optimization
        </Button>
      </div>

      {predictions && (
        <Card>
          <CardHeader>
            <CardTitle>Prediction Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(predictions).map(([key, value]) => (
                <div key={key} className="p-4 bg-gray-50 rounded-lg">
                  <div className="font-semibold capitalize mb-2">{key.replace(/_/g, ' ')}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    {typeof value === 'object' && Object.entries(value).map(([k, v]) => (
                      <div key={k}>
                        <div className="text-gray-600 capitalize">{k.replace(/_/g, ' ')}</div>
                        <div className="font-medium">
                          {typeof v === 'number' ? v.toFixed(2) : JSON.stringify(v)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Button variant="outline" onClick={() => runForecast('production')} disabled={loading}>Production</Button>
        <Button variant="outline" onClick={() => runForecast('emissions')} disabled={loading}>Emissions</Button>
        <Button variant="outline" onClick={() => runForecast('costs')} disabled={loading}>Costs</Button>
        <Button variant="outline" onClick={() => runForecast('efficiency')} disabled={loading}>Efficiency</Button>
      </div>

      {forecast && (
        <Card>
          <CardHeader>
            <CardTitle>Time Series Forecast - {forecast.metric}</CardTitle>
            <CardDescription>30-day forecast with confidence intervals</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-sm space-y-2">
              {forecast.forecast.slice(0, 7).map((item) => (
                <div key={item.period} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span>Day {item.period}</span>
                  <span className="font-medium">{item.predicted_value.toFixed(2)}</span>
                  <span className="text-xs text-gray-600">{(item.confidence * 100).toFixed(0)}% confidence</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Natural Language Query Assistant Component
const NLQAssistant = () => {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [suggestions, setSuggestions] = useState(null);

  useEffect(() => {
    fetchSuggestions();
  }, []);

  const fetchSuggestions = async () => {
    try {
      const res = await axios.get(`${API}/nlq/suggestions`);
      setSuggestions(res.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const handleQuery = async () => {
    if (!query.trim()) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/nlq/query`, { query });
      setResponse(res.data);
      setHistory([{ query, response: res.data, timestamp: new Date() }, ...history]);
    } catch (error) {
      console.error('Error processing query:', error);
      setResponse({ success: false, response: 'Error processing your query.' });
    } finally {
      setLoading(false);
    }
  };

  const useSuggestion = (suggestion) => {
    setQuery(suggestion);
    setResponse(null);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
          <Bot className="h-8 w-8 text-purple-600" />
          AI Query Assistant
        </h1>
        <p className="text-gray-600">Ask questions about your enterprise data in natural language</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ask a Question</CardTitle>
          <CardDescription>Type your question or select a suggestion below</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="e.g., How many assets do we have?"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleQuery()}
              className="flex-1"
            />
            <Button onClick={handleQuery} disabled={loading || !query.trim()}>
              {loading ? 'Processing...' : 'Ask'}
            </Button>
          </div>

          {response && (
            <Alert className={response.success ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500'}>
              <div className="flex-1">
                <div className="font-semibold mb-2">{response.success ? 'Answer:' : 'Error:'}</div>
                <div className="text-sm">{response.response}</div>
                {response.data_summary && (
                  <div className="text-xs text-gray-600 mt-2">
                    Found {response.data_summary.records_found} records from {response.data_summary.endpoint_used}
                  </div>
                )}
              </div>
            </Alert>
          )}
        </CardContent>
      </Card>

      {suggestions && (
        <Card>
          <CardHeader>
            <CardTitle>Suggested Queries</CardTitle>
            <CardDescription>Click any suggestion to try it</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue={Object.keys(suggestions.categories)[0]} className="space-y-4">
              <TabsList className="flex flex-wrap">
                {Object.keys(suggestions.categories).map((category) => (
                  <TabsTrigger key={category} value={category}>{category}</TabsTrigger>
                ))}
              </TabsList>
              {Object.entries(suggestions.categories).map(([category, queries]) => (
                <TabsContent key={category} value={category} className="space-y-2">
                  {queries.map((suggestion, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      className="w-full justify-start text-left"
                      onClick={() => useSuggestion(suggestion)}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      {suggestion}
                    </Button>
                  ))}
                </TabsContent>
              ))}
            </Tabs>
          </CardContent>
        </Card>
      )}

      {history.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Query History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {history.slice(0, 5).map((item, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="font-medium text-sm mb-1">{item.query}</div>
                  <div className="text-sm text-gray-600">{item.response.response}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {item.timestamp.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// IoT & Real-Time Monitoring Component
const IoTMonitoring = () => {
  const [sensors, setSensors] = useState([]);
  const [realTimeData, setRealTimeData] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIoTData();
    // Set up real-time data polling
    const interval = setInterval(fetchRealTimeData, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchIoTData = async () => {
    try {
      const [sensorsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/iot/sensors`),
        axios.get(`${API}/iot/alerts`)
      ]);
      setSensors(sensorsRes.data);
      setAlerts(alertsRes.data);
    } catch (error) {
      console.error('Error fetching IoT data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRealTimeData = async () => {
    try {
      const response = await axios.get(`${API}/iot/real-time-data`);
      setRealTimeData(response.data);
    } catch (error) {
      console.error('Error fetching real-time data:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading IoT monitoring...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">IoT & Real-Time Monitoring</h1>
        <p className="text-gray-600">Real-time sensor data and equipment monitoring</p>
      </div>

      <Tabs defaultValue="sensors" className="space-y-6">
        <TabsList>
          <TabsTrigger value="sensors">Sensors ({sensors.length})</TabsTrigger>
          <TabsTrigger value="real-time">Real-Time Data</TabsTrigger>
          <TabsTrigger value="alerts">Active Alerts ({alerts.filter(a => !a.resolved).length})</TabsTrigger>
        </TabsList>

        <TabsContent value="sensors" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sensors.map((sensor) => (
              <Card key={sensor.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{sensor.name}</span>
                    <Badge variant={sensor.status === 'online' ? 'default' : 'secondary'}>
                      {sensor.status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {sensor.type} • {sensor.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Current Value</span>
                      <span className="font-semibold">
                        {sensor.current_value} {sensor.unit}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Last Update</span>
                      <span className="text-sm text-gray-600">
                        {new Date(sensor.last_update).toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="real-time" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(realTimeData).map(([key, value]) => (
              <Card key={key}>
                <CardContent className="text-center p-6">
                  <div className="text-2xl font-bold text-blue-600 mb-2">
                    {typeof value === 'number' ? value.toFixed(2) : value}
                  </div>
                  <div className="text-sm text-gray-600 capitalize">
                    {key.replace(/_/g, ' ')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="alerts" className="space-y-4">
          <div className="grid gap-4">
            {alerts.filter(alert => !alert.resolved).map((alert) => (
              <Alert key={alert.id}>
                <AlertTriangle className="h-4 w-4" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold">{alert.title}</h4>
                    <Badge className={
                      alert.severity === 'critical' ? 'bg-red-100 text-red-800' :
                      alert.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>
                      {alert.severity}
                    </Badge>
                  </div>
                  <AlertDescription>
                    {alert.description}
                  </AlertDescription>
                </div>
              </Alert>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Supply Chain Orchestration Component
const SupplyChainOrchestration = () => {
  const [shipments, setShipments] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSupplyChainData();
  }, []);

  const fetchSupplyChainData = async () => {
    try {
      const [shipmentsRes, inventoryRes, suppliersRes] = await Promise.all([
        axios.get(`${API}/supply-chain/shipments`),
        axios.get(`${API}/supply-chain/inventory`),
        axios.get(`${API}/supply-chain/suppliers`)
      ]);
      setShipments(shipmentsRes.data);
      setInventory(inventoryRes.data);
      setSuppliers(suppliersRes.data);
    } catch (error) {
      console.error('Error fetching supply chain data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading supply chain data...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Supply Chain Orchestration</h1>
        <p className="text-gray-600">End-to-end supply chain visibility and management</p>
      </div>

      <Tabs defaultValue="shipments" className="space-y-6">
        <TabsList>
          <TabsTrigger value="shipments">Shipments ({shipments.length})</TabsTrigger>
          <TabsTrigger value="inventory">Inventory ({inventory.length})</TabsTrigger>
          <TabsTrigger value="suppliers">Suppliers ({suppliers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="shipments" className="space-y-4">
          <div className="grid gap-4">
            {shipments.map((shipment) => (
              <Card key={shipment.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Shipment #{shipment.tracking_number}</span>
                    <Badge className={
                      shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
                      shipment.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                      shipment.status === 'delayed' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }>
                      {shipment.status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {shipment.origin} → {shipment.destination}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Product:</span>
                      <div className="text-gray-600">{shipment.product}</div>
                    </div>
                    <div>
                      <span className="font-medium">Quantity:</span>
                      <div className="text-gray-600">{shipment.quantity} {shipment.unit}</div>
                    </div>
                    <div>
                      <span className="font-medium">ETA:</span>
                      <div className="text-gray-600">
                        {new Date(shipment.estimated_arrival).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium">Carrier:</span>
                      <div className="text-gray-600">{shipment.carrier}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="inventory" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {inventory.map((item) => (
              <Card key={item.id}>
                <CardHeader>
                  <CardTitle>{item.product_name}</CardTitle>
                  <CardDescription>{item.location}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Current Stock</span>
                      <span className="font-semibold">
                        {item.current_stock} {item.unit}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Reorder Level</span>
                      <span className="text-sm text-gray-600">
                        {item.reorder_level} {item.unit}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Status</span>
                      <Badge className={
                        item.current_stock <= item.reorder_level ? 'bg-red-100 text-red-800' :
                        item.current_stock <= item.reorder_level * 1.5 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }>
                        {item.current_stock <= item.reorder_level ? 'Low Stock' :
                         item.current_stock <= item.reorder_level * 1.5 ? 'Warning' : 'Good'}
                      </Badge>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4">
          <div className="grid gap-4">
            {suppliers.map((supplier) => (
              <Card key={supplier.id}>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{supplier.name}</span>
                    <Badge className={
                      supplier.performance_rating >= 90 ? 'bg-green-100 text-green-800' :
                      supplier.performance_rating >= 75 ? 'bg-blue-100 text-blue-800' :
                      supplier.performance_rating >= 60 ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {supplier.performance_rating}% Rating
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {supplier.category} • {supplier.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Contact:</span>
                      <div className="text-gray-600">{supplier.contact_person}</div>
                    </div>
                    <div>
                      <span className="font-medium">Lead Time:</span>
                      <div className="text-gray-600">{supplier.lead_time_days} days</div>
                    </div>
                    <div>
                      <span className="font-medium">Active Orders:</span>
                      <div className="text-gray-600">{supplier.active_orders}</div>
                    </div>
                    <div>
                      <span className="font-medium">Last Delivery:</span>
                      <div className="text-gray-600">
                        {new Date(supplier.last_delivery_date).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
// Main App Component
// ==================== DIGITAL TWIN MANAGER ====================

const DigitalTwinManager = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [realtimeSensors, setRealtimeSensors] = useState([]);
  const [simulationResults, setSimulationResults] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('overview');  // overview, equipment, facility, simulation

  useEffect(() => {
    fetchDashboard();
    fetchAnalytics();
    const interval = setInterval(fetchRealtimeSensors, 5000);  // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/digital-twin/dashboard`);
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching digital twin dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/digital-twin/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchRealtimeSensors = async () => {
    try {
      const response = await axios.get(`${API}/digital-twin/sensors/realtime`);
      setRealtimeSensors(response.data.sensor_data || []);
    } catch (error) {
      console.error('Error fetching realtime sensors:', error);
    }
  };

  const selectEquipment = async (equipmentId) => {
    try {
      const response = await axios.get(`${API}/digital-twin/equipment/${equipmentId}`);
      setSelectedEquipment(response.data);
      setActiveView('equipment');
    } catch (error) {
      console.error('Error fetching equipment twin:', error);
    }
  };

  const selectFacility = async (facilityId) => {
    try {
      const response = await axios.get(`${API}/digital-twin/facility/${facilityId}`);
      setSelectedFacility(response.data);
      setActiveView('facility');
    } catch (error) {
      console.error('Error fetching facility twin:', error);
    }
  };

  const runSimulation = async (equipmentId) => {
    try {
      const response = await axios.post(`${API}/digital-twin/equipment/simulate`, {
        equipment_id: equipmentId,
        scenario: {
          name: "Optimal Operating Conditions",
          parameters: {
            temperature: 70,
            pressure: 500,
            vibration_limit: 10
          }
        }
      });
      setSimulationResults(response.data);
    } catch (error) {
      console.error('Error running simulation:', error);
    }
  };

  const optimizeFacility = async (facilityId, target) => {
    try {
      const response = await axios.post(`${API}/digital-twin/facility/optimize`, {
        facility_id: facilityId,
        target: target  // production, energy, emissions
      });
      setSimulationResults(response.data);
      setActiveView('simulation');
    } catch (error) {
      console.error('Error optimizing facility:', error);
    }
  };

  const createWorkOrder = async (equipmentId) => {
    try {
      await axios.post(`${API}/digital-twin/equipment/create-workorder`, {
        equipment_id: equipmentId,
        priority: 'high'
      });
      alert('Work order created successfully in SAP PM!');
      fetchDashboard();
    } catch (error) {
      console.error('Error creating work order:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-lg">Loading Digital Twin Ecosystem...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Digital Twin Manager</h1>
        <p className="text-gray-600">Comprehensive digital twin ecosystem for equipment and facilities</p>
      </div>

      <Tabs value={activeView} onValueChange={setActiveView} className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Ecosystem Overview</TabsTrigger>
          <TabsTrigger value="equipment">Equipment Twins</TabsTrigger>
          <TabsTrigger value="facility">Facility Twins</TabsTrigger>
          <TabsTrigger value="simulation">Simulation & Optimization</TabsTrigger>
          <TabsTrigger value="3d-view">3D Visualization</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {dashboardData && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Digital Twins</CardTitle>
                    <Database className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600">{dashboardData.summary.total_twins}</div>
                    <p className="text-xs text-muted-foreground">
                      {dashboardData.summary.equipment_twins} Equipment + {dashboardData.summary.facility_twins} Facilities
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Active Monitoring</CardTitle>
                    <Activity className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600">{dashboardData.summary.active_twins}</div>
                    <p className="text-xs text-muted-foreground">{dashboardData.summary.twins_in_warning} in warning state</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Sensors</CardTitle>
                    <Zap className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-purple-600">{dashboardData.summary.total_sensors.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Real-time monitoring</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Data Throughput</CardTitle>
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-orange-600">{dashboardData.summary.data_points_per_second.toLocaleString()}</div>
                    <p className="text-xs text-muted-foreground">Data points/second</p>
                  </CardContent>
                </Card>
              </div>

              {analytics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Heart className="h-5 w-5 text-green-600" />
                        Ecosystem Health
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Overall Health Score</span>
                          <span className="text-2xl font-bold text-green-600">{analytics.ecosystem_health.overall_score}/100</span>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>Equipment Twins Avg</span>
                            <span className="font-semibold">{analytics.ecosystem_health.equipment_health_avg}%</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span>Facility Twins Avg</span>
                            <span className="font-semibold">{analytics.ecosystem_health.facility_health_avg}%</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-orange-600" />
                        Predictive Insights
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="p-3 bg-red-50 rounded-lg flex justify-between">
                          <span className="text-sm font-medium">Equipment at Risk</span>
                          <Badge className="bg-red-100 text-red-800">{analytics.predictive_insights.equipment_at_risk}</Badge>
                        </div>
                        <div className="p-3 bg-orange-50 rounded-lg flex justify-between">
                          <span className="text-sm font-medium">Predicted Failures (30 days)</span>
                          <Badge className="bg-orange-100 text-orange-800">{analytics.predictive_insights.predicted_failures_next_30_days}</Badge>
                        </div>
                        <div className="p-3 bg-yellow-50 rounded-lg flex justify-between">
                          <span className="text-sm font-medium">Predicted Failures (90 days)</span>
                          <Badge className="bg-yellow-100 text-yellow-800">{analytics.predictive_insights.predicted_failures_next_90_days}</Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Factory className="h-5 w-5 text-blue-600" />
                    Facility Digital Twins
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {dashboardData.facilities.map((facility) => (
                      <div 
                        key={facility.id} 
                        className="p-4 border rounded-lg hover:shadow-md transition-all cursor-pointer"
                        onClick={() => selectFacility(facility.id)}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900">{facility.name}</h4>
                          <Badge className={
                            facility.status === 'operational' ? 'bg-green-100 text-green-800' :
                            facility.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }>
                            {facility.status}
                          </Badge>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Type:</span>
                            <span className="font-medium capitalize">{facility.type.replace('_', ' ')}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Equipment:</span>
                            <span className="font-medium">{facility.equipment_count}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Sensors:</span>
                            <span className="font-medium">{facility.sensors.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Health:</span>
                            <div className="flex items-center gap-2">
                              <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${
                                    facility.health_score >= 90 ? 'bg-green-500' :
                                    facility.health_score >= 70 ? 'bg-yellow-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${facility.health_score}%` }}
                                />
                              </div>
                              <span className="font-medium">{facility.health_score}%</span>
                            </div>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">{facility.type === 'pipeline' ? 'Flow Rate:' : 'Production:'}</span>
                            <span className="font-medium text-blue-600">
                              {(facility.production_rate || facility.flow_rate).toLocaleString()} bpd
                            </span>
                          </div>
                        </div>
                        <Button className="w-full mt-3" size="sm" variant="outline">
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    Critical Equipment Requiring Attention
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {dashboardData.critical_equipment.map((equipment) => (
                      <div key={equipment.id} className="p-4 border-l-4 border-red-500 bg-red-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-semibold text-gray-900">{equipment.name}</h4>
                          <Badge className="bg-red-100 text-red-800">Health: {equipment.health_score}%</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                          <div>
                            <span className="text-gray-600">Facility:</span>
                            <span className="ml-2 font-medium">{equipment.facility}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Type:</span>
                            <span className="ml-2 font-medium capitalize">{equipment.type}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Predicted Failure:</span>
                            <span className="ml-2 font-medium text-red-600">{equipment.predicted_failure_days} days</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Status:</span>
                            <Badge className="bg-yellow-100 text-yellow-800">{equipment.status}</Badge>
                          </div>
                        </div>
                        <div className="bg-white p-3 rounded mb-3">
                          <div className="text-sm font-medium mb-1">AI Recommendation:</div>
                          <div className="text-sm text-gray-700">{equipment.recommendation}</div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => selectEquipment(equipment.id)}>
                            <Eye className="h-4 w-4 mr-2" />
                            View Twin
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => runSimulation(equipment.id)}>
                            <Play className="h-4 w-4 mr-2" />
                            Run Simulation
                          </Button>
                          <Button size="sm" className="bg-blue-600" onClick={() => createWorkOrder(equipment.id)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Create Work Order
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {analytics && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Performance Metrics</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Simulation Accuracy</span>
                          <span className="text-lg font-bold text-green-600">{analytics.performance_metrics.simulation_accuracy}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Prediction Accuracy</span>
                          <span className="text-lg font-bold text-blue-600">{analytics.performance_metrics.prediction_accuracy}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">False Positive Rate</span>
                          <span className="text-lg font-bold text-orange-600">{analytics.performance_metrics.false_positive_rate}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">Early Warning (avg)</span>
                          <span className="text-lg font-bold text-purple-600">{analytics.performance_metrics.early_warning_days_average} days</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Operational Impact (YTD)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="p-3 bg-green-50 rounded-lg">
                          <div className="text-sm text-gray-600">Prevented Downtime</div>
                          <div className="text-2xl font-bold text-green-600">{analytics.operational_impact.prevented_downtime_hours_ytd.toLocaleString()} hrs</div>
                        </div>
                        <div className="p-3 bg-blue-50 rounded-lg">
                          <div className="text-sm text-gray-600">Cost Savings</div>
                          <div className="text-2xl font-bold text-blue-600">${(analytics.operational_impact.cost_savings_ytd / 1000000).toFixed(1)}M</div>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <div className="text-sm text-gray-600">Production Optimization</div>
                          <div className="text-2xl font-bold text-purple-600">+{analytics.operational_impact.production_optimization_increase}%</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-600" />
                    Real-Time Sensor Streams
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                    {realtimeSensors.slice(0, 12).map((sensor) => (
                      <div key={sensor.sensor_id} className={`p-3 rounded-lg ${
                        sensor.status === 'normal' ? 'bg-green-50 border border-green-200' :
                        'bg-yellow-50 border border-yellow-300'
                      }`}>
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-xs font-mono text-gray-600">{sensor.sensor_id}</span>
                          <Badge className={sensor.status === 'normal' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                            {sensor.status}
                          </Badge>
                        </div>
                        <div className="text-lg font-bold text-gray-900">{sensor.value} {sensor.unit}</div>
                        <div className="text-xs text-gray-600 capitalize">{sensor.sensor_type}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-center">
                    <Button variant="outline" size="sm">
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Sensor Data
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="equipment" className="space-y-6">
          {selectedEquipment ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Settings className="h-6 w-6 text-blue-600" />
                      <span>{selectedEquipment.name}</span>
                    </div>
                    <Badge className={
                      selectedEquipment.status === 'operational' ? 'bg-green-100 text-green-800' :
                      selectedEquipment.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }>
                      {selectedEquipment.status}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    {selectedEquipment.manufacturer} {selectedEquipment.model} • {selectedEquipment.location}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-semibold mb-3">Equipment Information</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type:</span>
                          <span className="font-medium capitalize">{selectedEquipment.asset_type}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Installed:</span>
                          <span className="font-medium">{selectedEquipment.installed_date}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Runtime:</span>
                          <span className="font-medium">{selectedEquipment.sensor_data.runtime_hours.toLocaleString()} hours</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Health Score:</span>
                          <span className={`text-lg font-bold ${
                            selectedEquipment.health_score >= 90 ? 'text-green-600' :
                            selectedEquipment.health_score >= 70 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>{selectedEquipment.health_score}%</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3">Failure Prediction</h4>
                      <div className="p-4 bg-red-50 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Remaining Useful Life</div>
                        <div className="text-3xl font-bold text-red-600 mb-2">
                          {selectedEquipment.remaining_useful_life_days} days
                        </div>
                        <div className="text-xs text-gray-600">
                          Predicted Failure: {new Date(selectedEquipment.predicted_failure_date).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Real-Time Sensor Data</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {Object.entries(selectedEquipment.sensor_data).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center">
                          <span className="text-sm capitalize">{key.replace('_', ' ')}</span>
                          <span className="font-semibold">{typeof value === 'number' ? value.toFixed(1) : value}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Failure Modes Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {selectedEquipment.failure_modes.map((mode, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium">{mode.mode}</span>
                            <Badge className={
                              mode.probability > 0.7 ? 'bg-red-100 text-red-800' :
                              mode.probability > 0.5 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }>
                              {(mode.probability * 100).toFixed(0)}% probability
                            </Badge>
                          </div>
                          <div className="text-xs text-gray-600">
                            Severity: <span className="font-medium capitalize">{mode.severity}</span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            Indicators: {mode.indicators.join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Health Degradation Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedEquipment.degradation_trend.map((point, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <span className="text-sm text-gray-600 w-24">{point.date}</span>
                        <div className="flex-1">
                          <div className="w-full h-6 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className={`h-full ${
                                point.health >= 80 ? 'bg-green-500' :
                                point.health >= 60 ? 'bg-yellow-500' :
                                'bg-red-500'
                              }`}
                              style={{ width: `${point.health}%` }}
                            />
                          </div>
                        </div>
                        <span className="text-sm font-semibold w-16">{point.health}%</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>AI Maintenance Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {selectedEquipment.maintenance_recommendations.map((rec, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                        <CheckCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                        <span className="text-sm text-gray-700">{rec}</span>
                      </div>
                    ))}
                  </div>
                  <Button className="w-full mt-4" onClick={() => createWorkOrder(selectedEquipment.id)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create SAP PM Work Order
                  </Button>
                </CardContent>
              </Card>

              <Button variant="outline" onClick={() => { setSelectedEquipment(null); setActiveView('overview'); }}>
                ← Back to Overview
              </Button>
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Settings className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Equipment Selected</h3>
                <p className="text-gray-600 mb-4">
                  Select a critical equipment from the overview or choose from the facility list
                </p>
                <Button onClick={() => setActiveView('overview')}>Go to Overview</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="facility" className="space-y-6">
          {selectedFacility ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Factory className="h-6 w-6 text-blue-600" />
                      <span>{selectedFacility.name}</span>
                    </div>
                    <Badge className="bg-blue-100 text-blue-800 capitalize">
                      {selectedFacility.facility_type.replace('_', ' ')}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-blue-600">{selectedFacility.equipment.length}</div>
                      <div className="text-xs text-gray-600">Equipment Units</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-green-600">{selectedFacility.sensors.total.toLocaleString()}</div>
                      <div className="text-xs text-gray-600">Active Sensors</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-purple-600">{selectedFacility.sensors.percentage_online}%</div>
                      <div className="text-xs text-gray-600">Sensor Availability</div>
                    </div>
                    <div className="p-4 bg-orange-50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-orange-600">{selectedFacility.production.uptime_percentage}%</div>
                      <div className="text-xs text-gray-600">Uptime</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Production</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {Object.entries(selectedFacility.production).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                            <span className="font-semibold">{typeof value === 'number' ? value.toLocaleString() : value}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Energy</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {Object.entries(selectedFacility.energy).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                            <span className="font-semibold">{typeof value === 'number' ? value.toLocaleString() : value}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>

                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Emissions</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {Object.entries(selectedFacility.emissions).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-600 capitalize">{key.replace('_', ' ')}:</span>
                            <span className="font-semibold">{typeof value === 'number' ? value.toLocaleString() : value}</span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Equipment Health Overview</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedFacility.equipment.map((equip) => (
                          <div key={equip.id} className="p-3 border rounded-lg flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                               onClick={() => selectEquipment(equip.id)}>
                            <div className="flex items-center gap-3">
                              <Settings className="h-5 w-5 text-gray-400" />
                              <div>
                                <div className="font-medium">{equip.name}</div>
                                <div className="text-xs text-gray-600 capitalize">{equip.type}</div>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${
                                    equip.health >= 90 ? 'bg-green-500' :
                                    equip.health >= 70 ? 'bg-yellow-500' :
                                    'bg-red-500'
                                  }`}
                                  style={{ width: `${equip.health}%` }}
                                />
                              </div>
                              <span className="font-semibold w-12">{equip.health}%</span>
                              <Badge className={
                                equip.status === 'operational' ? 'bg-green-100 text-green-800' :
                                'bg-yellow-100 text-yellow-800'
                              }>
                                {equip.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex gap-4">
                    <Button onClick={() => optimizeFacility(selectedFacility.id, 'production')}>
                      <TrendingUp className="h-4 w-4 mr-2" />
                      Optimize Production
                    </Button>
                    <Button variant="outline" onClick={() => optimizeFacility(selectedFacility.id, 'energy')}>
                      <Zap className="h-4 w-4 mr-2" />
                      Optimize Energy
                    </Button>
                    <Button variant="outline" onClick={() => optimizeFacility(selectedFacility.id, 'emissions')}>
                      <Leaf className="h-4 w-4 mr-2" />
                      Reduce Emissions
                    </Button>
                  </div>

                  <Button variant="outline" onClick={() => { setSelectedFacility(null); setActiveView('overview'); }}>
                    ← Back to Overview
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Factory className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Facility Selected</h3>
                <p className="text-gray-600 mb-4">
                  Select a facility from the overview to view detailed digital twin data
                </p>
                <Button onClick={() => setActiveView('overview')}>Go to Overview</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="simulation" className="space-y-6">
          {simulationResults ? (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="h-6 w-6 text-green-600" />
                    {simulationResults.scenario_name || 'Facility Optimization Results'}
                  </CardTitle>
                  <CardDescription>
                    {simulationResults.optimization_target && `Target: ${simulationResults.optimization_target}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {simulationResults.predicted_outcomes && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {simulationResults.predicted_outcomes.efficiency_change > 0 ? '+' : ''}
                          {simulationResults.predicted_outcomes.efficiency_change}%
                        </div>
                        <div className="text-xs text-gray-600">Efficiency Change</div>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {simulationResults.predicted_outcomes.production_impact > 0 ? '+' : ''}
                          {simulationResults.predicted_outcomes.production_impact}%
                        </div>
                        <div className="text-xs text-gray-600">Production Impact</div>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {simulationResults.predicted_outcomes.energy_consumption_change}%
                        </div>
                        <div className="text-xs text-gray-600">Energy Savings</div>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-orange-600">
                          {simulationResults.predicted_outcomes.health_impact > 0 ? '+' : ''}
                          {simulationResults.predicted_outcomes.health_impact}%
                        </div>
                        <div className="text-xs text-gray-600">Health Impact</div>
                      </div>
                    </div>
                  )}

                  {simulationResults.improvement_potential && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                      <div className="p-4 bg-green-50 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Production Increase</div>
                        <div className="text-2xl font-bold text-green-600">+{simulationResults.improvement_potential.production_increase_percent}%</div>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Efficiency Gain</div>
                        <div className="text-2xl font-bold text-blue-600">+{simulationResults.improvement_potential.efficiency_gain_percent}%</div>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Energy Savings</div>
                        <div className="text-2xl font-bold text-purple-600">{simulationResults.improvement_potential.energy_savings_percent}%</div>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-lg">
                        <div className="text-sm text-gray-600 mb-1">Emissions Reduction</div>
                        <div className="text-2xl font-bold text-orange-600">{simulationResults.improvement_potential.emissions_reduction_percent}%</div>
                      </div>
                    </div>
                  )}

                  {simulationResults.recommended_actions && (
                    <Card>
                      <CardHeader>
                        <CardTitle>AI-Recommended Actions</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {simulationResults.recommended_actions.map((action, idx) => (
                            <div key={idx} className="flex items-start gap-3 p-3 border rounded-lg">
                              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                              <span className="text-sm">{action}</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {simulationResults.financial_impact && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Financial Impact Analysis</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {Object.entries(simulationResults.financial_impact).map(([key, value]) => (
                            <div key={key} className="p-4 bg-green-50 rounded-lg">
                              <div className="text-xs text-gray-600 capitalize mb-1">
                                {key.replace(/_/g, ' ')}
                              </div>
                              <div className="text-2xl font-bold text-green-600">
                                ${(value / 1000000).toFixed(1)}M
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <Button variant="outline" onClick={() => { setSimulationResults(null); setActiveView('overview'); }}>
                    ← Back to Overview
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Play className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Simulation Results</h3>
                <p className="text-gray-600 mb-4">
                  Run a simulation from equipment or facility view to see AI-powered optimization results
                </p>
                <Button onClick={() => setActiveView('overview')}>Go to Overview</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="3d-view" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-6 w-6 text-blue-600" />
                3D Digital Twin Visualization
              </CardTitle>
              <CardDescription>Interactive 3D models with real-time sensor data overlay</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {/* 3D Scene */}
                <div className="h-[600px] bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 rounded-lg relative overflow-hidden border-4 border-blue-500">
                  <ThreeDScene 
                    equipment={dashboardData?.critical_equipment || []} 
                    facilities={dashboardData?.facilities || []}
                    realtimeSensors={realtimeSensors}
                  />
                </div>

                {/* Controls and Info Panel */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">View Controls</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center text-xs font-semibold">🖱️</div>
                        <span>Left Click + Drag: Rotate view</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-green-100 flex items-center justify-center text-xs font-semibold">⚙️</div>
                        <span>Scroll: Zoom in/out</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded bg-purple-100 flex items-center justify-center text-xs font-semibold">👆</div>
                        <span>Click Equipment: View details</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Health Status Legend</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-green-500"></div>
                        <span className="text-sm">Excellent (90-100%)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-yellow-500"></div>
                        <span className="text-sm">Warning (70-89%)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-red-500"></div>
                        <span className="text-sm">Critical (&lt;70%)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gray-500"></div>
                        <span className="text-sm">Offline</span>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Scene Info</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Equipment Models:</span>
                        <span className="font-semibold">127</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Facility Models:</span>
                        <span className="font-semibold">7</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Active Sensors:</span>
                        <span className="font-semibold text-green-600">{realtimeSensors.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Render FPS:</span>
                        <span className="font-semibold text-blue-600">60</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Settings className="h-5 w-5 text-blue-500" />
                        Equipment View
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>✓ 3D CAD model rendering</li>
                        <li>✓ Real-time sensor overlays</li>
                        <li>✓ Heat map visualization</li>
                        <li>✓ Stress analysis display</li>
                        <li>✓ Maintenance history timeline</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Factory className="h-5 w-5 text-green-500" />
                        Facility View
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>✓ Complete platform/refinery model</li>
                        <li>✓ Process flow visualization</li>
                        <li>✓ Temperature gradients</li>
                        <li>✓ Pressure distribution maps</li>
                        <li>✓ Safety zone highlighting</li>
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="pt-6">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Play className="h-5 w-5 text-purple-500" />
                        Simulation View
                      </h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>✓ What-if scenario playback</li>
                        <li>✓ Failure mode animation</li>
                        <li>✓ Optimization path display</li>
                        <li>✓ Time-lapse degradation</li>
                        <li>✓ Interactive parameter control</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <div className="flex items-start gap-4">
                    <Info className="h-6 w-6 text-blue-600 mt-1" />
                    <div>
                      <h4 className="font-semibold text-blue-900 mb-2">3D Visualization Features</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• <strong>WebGL-accelerated rendering</strong> for smooth 60 FPS performance</li>
                        <li>• <strong>Real-time sensor data overlay</strong> on 3D models with color-coded indicators</li>
                        <li>• <strong>Interactive navigation</strong> - rotate, zoom, pan through facilities</li>
                        <li>• <strong>Physics-based simulation</strong> for degradation and failure mode visualization</li>
                        <li>• <strong>VR/AR ready</strong> for immersive field technician training</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ==================== 3D SCENE COMPONENT ====================

const ThreeDScene = ({ equipment, facilities, realtimeSensors }) => {
  const [selectedObject, setSelectedObject] = React.useState(null);
  const [rotation, setRotation] = React.useState(0);

  // Auto-rotate effect
  React.useEffect(() => {
    const interval = setInterval(() => {
      setRotation(prev => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  const getHealthColor = (health) => {
    if (health >= 90) return '#10b981';
    if (health >= 70) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="relative w-full h-full bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900" style={{ perspective: '1000px' }}>
      {/* 3D Scene Container */}
      <div 
        className="w-full h-full flex items-center justify-center"
        style={{
          transform: `rotateY(${rotation}deg)`,
          transformStyle: 'preserve-3d',
          transition: 'transform 0.05s linear'
        }}
      >
        {/* Equipment Models */}
        <div className="absolute" style={{ transform: 'translateZ(200px)' }}>
          <div className="grid grid-cols-2 gap-8">
            {equipment.slice(0, 2).map((equip, idx) => (
              <div 
                key={equip.id}
                className="relative cursor-pointer group"
                onClick={() => setSelectedObject(equip)}
                style={{
                  transform: `translateZ(${idx * 50}px)`,
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Equipment 3D representation */}
                <div 
                  className="w-32 h-32 rounded-lg relative"
                  style={{
                    backgroundColor: getHealthColor(equip.health_score),
                    boxShadow: `0 0 40px ${getHealthColor(equip.health_score)}`,
                    transform: 'rotateX(15deg) rotateY(15deg)'
                  }}
                >
                  {/* Equipment icon/shape */}
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    <Settings className="h-16 w-16 opacity-80" />
                  </div>
                  
                  {/* Health indicator */}
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center text-xs font-bold"
                       style={{ borderColor: getHealthColor(equip.health_score) }}>
                    {equip.health_score}%
                  </div>

                  {/* Pulsing warning for critical */}
                  {equip.health_score < 70 && (
                    <div className="absolute inset-0 rounded-lg animate-pulse"
                         style={{ backgroundColor: 'rgba(239, 68, 68, 0.3)' }}>
                    </div>
                  )}
                </div>

                {/* Label */}
                <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-white px-3 py-2 rounded-lg shadow-xl border-2 border-blue-500 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity"
                     style={{ transform: 'translateX(-50%) translateZ(50px)' }}>
                  <div className="text-xs font-bold">{equip.name}</div>
                  <div className="text-xs text-gray-600">{equip.type}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Facility Models */}
        <div className="absolute bottom-0" style={{ transform: 'translateZ(-100px) translateY(100px)' }}>
          <div className="grid grid-cols-3 gap-12">
            {facilities.slice(0, 3).map((facility, idx) => (
              <div 
                key={facility.id}
                className="relative cursor-pointer group"
                onClick={() => setSelectedObject(facility)}
                style={{
                  transform: `translateZ(${-idx * 30}px)`,
                  transformStyle: 'preserve-3d'
                }}
              >
                {/* Facility platform */}
                <div className="relative">
                  <div 
                    className="w-48 h-48 rounded-xl relative"
                    style={{
                      backgroundColor: getHealthColor(facility.health_score),
                      boxShadow: `0 0 60px ${getHealthColor(facility.health_score)}`,
                      transform: 'rotateX(45deg)'
                    }}
                  >
                    {/* Platform details */}
                    <div className="absolute inset-4 grid grid-cols-3 gap-2">
                      {[...Array(9)].map((_, i) => (
                        <div key={i} className="bg-white/20 rounded" />
                      ))}
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center text-white">
                      <Factory className="h-20 w-20 opacity-50" />
                    </div>
                  </div>

                  {/* Facility label */}
                  <div className="absolute -bottom-16 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-xl border-2 border-blue-500 whitespace-nowrap text-center">
                    <div className="text-xs font-bold">{facility.name}</div>
                    <div className="text-xs text-blue-600">{facility.production_rate?.toLocaleString() || facility.flow_rate?.toLocaleString()} bpd</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating sensor data particles */}
        <div className="absolute inset-0 pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 rounded-full bg-blue-400 animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                opacity: 0.6
              }}
            />
          ))}
        </div>
      </div>

      {/* Overlay info panel */}
      <div className="absolute top-4 left-4 bg-black/70 backdrop-blur text-white p-4 rounded-lg max-w-sm z-10">
        <h4 className="font-bold mb-2 flex items-center gap-2">
          <Activity className="h-4 w-4" />
          Live Digital Twin Scene
        </h4>
        <div className="text-xs space-y-1">
          <div>🔴 Critical Equipment: {equipment.filter(e => e.health_score < 70).length}</div>
          <div>🟡 Warning Status: {equipment.filter(e => e.health_score >= 70 && e.health_score < 90).length}</div>
          <div>🟢 Operational: {equipment.filter(e => e.health_score >= 90).length}</div>
          <div className="pt-2 border-t border-gray-600 mt-2">
            <div>📡 Real-time Sensors: {realtimeSensors.length} active</div>
            <div>🏭 Facilities: {facilities.length} monitoring</div>
            <div>🔄 Auto-rotating view</div>
          </div>
        </div>
      </div>

      {/* Selected object detail panel */}
      {selectedObject && (
        <div className="absolute top-4 right-4 bg-white p-4 rounded-lg shadow-2xl max-w-md border-2 border-blue-500 z-10">
          <div className="flex justify-between items-start mb-3">
            <h4 className="font-bold text-lg">{selectedObject.name}</h4>
            <button 
              onClick={() => setSelectedObject(null)}
              className="text-gray-400 hover:text-gray-600 text-xl"
            >
              ✕
            </button>
          </div>
          <div className="space-y-2 text-sm">
            {selectedObject.type && (
              <div className="flex justify-between">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium capitalize">{selectedObject.type}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-600">Health Score:</span>
              <span className={`font-bold ${
                selectedObject.health_score >= 90 ? 'text-green-600' :
                selectedObject.health_score >= 70 ? 'text-yellow-600' :
                'text-red-600'
              }`}>{selectedObject.health_score}%</span>
            </div>
            {selectedObject.predicted_failure_days && (
              <div className="flex justify-between">
                <span className="text-gray-600">Predicted Failure:</span>
                <span className="font-medium text-red-600">{selectedObject.predicted_failure_days} days</span>
              </div>
            )}
            {selectedObject.production_rate && (
              <div className="flex justify-between">
                <span className="text-gray-600">Production:</span>
                <span className="font-medium text-blue-600">{selectedObject.production_rate.toLocaleString()} bpd</span>
              </div>
            )}
            {selectedObject.recommendation && (
              <div className="mt-3 p-2 bg-yellow-50 rounded border border-yellow-300">
                <div className="text-xs font-medium text-yellow-900">AI Recommendation:</div>
                <div className="text-xs text-yellow-800 mt-1">{selectedObject.recommendation}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FPS Counter */}
      <div className="absolute bottom-4 right-4 bg-black/70 backdrop-blur text-white px-3 py-1 rounded text-xs font-mono z-10">
        60 FPS | CSS 3D Transform
      </div>

      {/* Grid lines for depth perception */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: 'linear-gradient(0deg, transparent 24%, rgba(59, 130, 246, 0.5) 25%, rgba(59, 130, 246, 0.5) 26%, transparent 27%, transparent 74%, rgba(59, 130, 246, 0.5) 75%, rgba(59, 130, 246, 0.5) 76%, transparent 77%, transparent), linear-gradient(90deg, transparent 24%, rgba(59, 130, 246, 0.5) 25%, rgba(59, 130, 246, 0.5) 26%, transparent 27%, transparent 74%, rgba(59, 130, 246, 0.5) 75%, rgba(59, 130, 246, 0.5) 76%, transparent 77%, transparent)',
          backgroundSize: '50px 50px'
        }} />
      </div>
    </div>
  );
};

// ==================== APP COMPONENT ====================

function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard />;
      case 'maintenance':
        return <PredictiveMaintenance />;
      case 'compliance':
        return <ComplianceManagement />;
      case 'ctrm':
        return <CTRM />;
      case 'jva':
        return <JVA />;
      case 'esg':
        return <ESGIntelligence />;
      case 'field-ops':
        return <FieldOperations />;
      case 'safety':
        return <WorkforceSafety />;
      case 'supply-chain':
        return <SupplyChain />;
      case 'analytics':
        return <AdvancedAnalytics />;
      case 'integrations':
        return <IntegrationsLayer />;
      case 'sap-integration':
        return <SAPIntegrationManager />;
      case 'ai-studio':
        return <AIStudio />;
      case 'ai-chat':
        return <AIAssistant />;
      case 'documentation':
        return <Documentation />;
      case 'digital-twin':
        return <DigitalTwinManager />;
      // Advanced AI/ML Components
      case 'anomaly-detection':
        return <AnomalyDetection />;
      case 'predictive-models':
        return <PredictiveModels />;
      case 'cost-optimization':
        return <CostOptimization />;
      // IoT & Automation Components
      case 'iot-management':
        return <IoTManagement />;
      case 'automated-workflows':
        return <AutomatedWorkflows />;
      // Industry-Specific Components
      case 'drilling-operations':
        return <DrillingOperationsManagement />;
      case 'pipeline-monitoring':
        return <PipelineMonitoringSystem />;
      case 'regulatory-compliance':
        return <RegulatoryComplianceAutomation />;
      case 'environmental-impact':
        return <EnvironmentalImpactAssessment />;
      // Advanced Analytics Components
      case 'executive-command':
        return <ExecutiveCommandCenter />;
      case 'nlq-assistant':
        return <AIAssistant />;
      case 'predictive-analytics':
        return <PredictiveMaintenance />;
      case 'custom-reporting':
        return <ExecutiveReports />;
      case 'advanced-dashboards':
        return <AdvancedDashboards />;
      case 'data-visualization':
        return <DataVisualization />;
      case 'performance-analytics':
        return <PerformanceAnalytics />;
      // Digital Operations Components
      case 'iot-monitoring':
        return <IoTMonitoring />;
      case 'supply-chain':
        return <SupplyChainOrchestration />;
      // Enterprise Management Components
      case 'user-management':
        return <UserManagement />;
      case 'role-permissions':
        return <RolePermissions />;
      case 'audit-trails':
        return <AuditTrails />;
      case 'system-settings':
        return <SystemSettings />;
      // Collaboration & Communication Components
      case 'team-collaboration':
        return <TeamCollaboration />;
      case 'real-time-messaging':
        return <RealTimeMessaging />;
      case 'notification-center':
        return <NotificationCenter />;
      case 'knowledge-sharing':
        return <KnowledgeSharing />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation 
        currentPage={currentPage} 
        setCurrentPage={setCurrentPage}
      />
      <main className="max-w-7xl mx-auto ml-80">
        {renderPage()}
      </main>
    </div>
  );
}

export default App;